// ============================================================================
// CONFIGURATION
// ============================================================================

// Debug configuration
const DEBUG_CONFIG = {
  ENABLE_LOGS: false, // Set to true to enable console logging
  LOG_LEVELS: {
    ERROR: false,
    WARN: false, 
    INFO: false,
    DEBUG: false
  }
};

// Helper function for conditional logging
const debugLog = (level, ...args) => {
  if (DEBUG_CONFIG.ENABLE_LOGS && DEBUG_CONFIG.LOG_LEVELS[level]) {
    console[level.toLowerCase()](...args);
  }
};

const CONFIG = {
  MAX_GRID_SIZE: 96,
  DEFAULT_GRID_SIZE: 32,
  FRAME_WIDTH: 2, // Width of black frame when baseplate is 'Framed'
  ADJUSTMENT_STEP_SIZE: 1, // Step size for +/- buttons on adjustment sliders
  SHOW_ASSEMBLY_INSTRUCTIONS_IN_CART: false,
  MAX_FILE_SIZE: 15, // File size limit in MB
  CANVAS_SELECTORS: {
    UPLOAD_CANVAS: '#uploaded-image-canvas',
    EDIT_CANVAS: '#image-edit-canvas'
  },
  SELECTORS: {
    UPLOAD_INPUT: '#custom-image',
    UPLOAD_LABEL: '#upload-label',
    UPLOAD_TEXT: '#upload-text',
    SHAPE_RADIOS: 'input[name="properties[Shape]"]'
  },
  // Cloudinary configuration
  // Upload provider is now determined dynamically based on section setting: enable_cloudinary_upload
  CLOUDINARY: {
    cloudName: 'ddisxym9x', // Public - safe to expose
    uploadPreset: 'shopify_order_assets' // Public - unsigned preset is safe to expose
    // apiKey is not needed for unsigned uploads
  },
  // run window.mosaicApp.mosaicRenderer.testApplyAllColors() via console to test color legend in the assembly instructions.
  DEV: {
    LOAD_DEFAULT_IMAGE: false,
    DEFAULT_IMAGE_PATH: '/assets/aalfredo.jpg', // Path to default image
    FORCE_VALIDATION_WARNING: false // Set to true to always show mosaic validation warning for testing
  }
};

// ============================================================================
// STATE MANAGER
// ============================================================================

class StateManager {
  constructor() {
    this.state = {
      gridWidth: CONFIG.DEFAULT_GRID_SIZE,
      gridHeight: CONFIG.DEFAULT_GRID_SIZE,
      gridSizeString: `${CONFIG.DEFAULT_GRID_SIZE} × ${CONFIG.DEFAULT_GRID_SIZE}`,
      gridAspectRatio: 1,
      baseplate: false,
      currentPrice: '',
      uploadedImage: null,
      currentShape: 'square',
      cropRect: null,
      imageBounds: null,
      adjustments: {
        brightness: 0,
        contrast: 0,
        saturation: 0,
        hue: 0
      }
    };
    
    this.listeners = new Map();
    this.eventBus = new EventTarget();
  }

  getState() {
    return { ...this.state };
  }

  // Update state and notify listeners
  updateState(updates, suppressNotifications = false) {
    const oldState = { ...this.state };
    this.state = { ...this.state, ...updates };
    
    // Notify listeners of state change (unless suppressed)
    if (!suppressNotifications) {
      this.notifyListeners('stateChanged', { oldState, newState: this.state });
    }
  }

  // Subscribe to state changes
  subscribe(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);
  }

  // Notify all listeners of an event
  notifyListeners(event, data) {
    const eventListeners = this.listeners.get(event) || [];
    eventListeners.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        debugLog('ERROR', 'Error in state listener:', error);
      }
    });
  }

  isFramed() {
    return this.state.baseplate === 'Framed';
  }

  // Reset to initial state
  reset() {
    this.updateState({
      gridWidth: CONFIG.DEFAULT_GRID_SIZE,
      gridHeight: CONFIG.DEFAULT_GRID_SIZE,
      gridSizeString: `${CONFIG.DEFAULT_GRID_SIZE} × ${CONFIG.DEFAULT_GRID_SIZE}`,
      gridAspectRatio: 1,
      baseplate: false,
      currentPrice: '',
      uploadedImage: null,
      currentShape: 'square',
      cropRect: null,
      imageBounds: null,
      adjustments: {
        brightness: 0,
        contrast: 0,
        saturation: 0,
        hue: 0
      }
    });
  }
}

// ============================================================================
// IMAGE MANAGER
// ============================================================================

class ImageManager {
  constructor(stateManager) {
    this.stateManager = stateManager;
    this.uploadCanvas = null;
    this.uploadCtx = null;
    this.currentImage = null;
    this.canvasWrapper = null;
    this.storedCanvasDimensions = null;
    this.boundHandlers = {}; // Store bound event handlers for cleanup
    
    this.init();
  }

  init() {
    this.setupCanvas();
    this.setupEventListeners();
  }

  setupCanvas() {
    debugLog('INFO', '🔍 ImageManager: Setting up canvas...');
    this.uploadCanvas = document.querySelector(CONFIG.CANVAS_SELECTORS.UPLOAD_CANVAS);
    debugLog('INFO', '🔍 ImageManager: uploadCanvas found:', this.uploadCanvas);
    if (this.uploadCanvas) {
      this.uploadCtx = this.uploadCanvas.getContext('2d');
      this.canvasWrapper = this.uploadCanvas.closest('.image-cropper-wrapper');
      debugLog('INFO', '🔍 ImageManager: uploadCtx created:', this.uploadCtx);
      debugLog('INFO', '🔍 ImageManager: canvasWrapper found:', this.canvasWrapper);
      
      // Initially hide the cropper elements until an image is uploaded
      if (this.canvasWrapper) {
        this.canvasWrapper.classList.add('hidden');
      }
      
      // Also hide cropper controls initially (they'll be shown permanently after first upload)
      const cropperControls = document.querySelector('.cropper-controls');
      if (cropperControls) {
        cropperControls.classList.add('hidden');
      }
    } else {
      debugLog('WARN', 'Upload canvas not found');
    }
  }

  setupEventListeners() {
    // Clear existing listeners first
    this.removeEventListeners();
    
    debugLog('INFO', '🔍 ImageManager: Setting up event listeners...');
    // Both modes use #custom-image for the file input
    const uploadInput = document.getElementById('custom-image');
    const uploadLabel = document.querySelector(CONFIG.SELECTORS.UPLOAD_LABEL);
    
    debugLog('INFO', '🔍 ImageManager: uploadInput found:', uploadInput);
    debugLog('INFO', '🔍 ImageManager: uploadLabel found:', uploadLabel);
    
    // Store bound handlers for cleanup
    if (uploadInput) {
      this.boundHandlers.inputChange = (e) => this.handleImageUpload(e);
      uploadInput.addEventListener('change', this.boundHandlers.inputChange);
      debugLog('INFO', '🔍 ImageManager: Added change event listener to uploadInput');
    }
    
    if (uploadLabel) {
      // Document-level handlers to catch all drag/drop events and handle file processing
      this.boundHandlers.documentDragover = (e) => {
        e.preventDefault();
        
        // Add dragover class when dragging over upload area
        const uploadArea = e.target.closest('.image-upload-area');
        const allUploadLabels = document.querySelectorAll('.image-upload-area');
        
        if (uploadArea) {
          // Add class to the specific area being dragged over
          if (!uploadArea.classList.contains('dragover')) {
            uploadArea.classList.add('dragover');
          }
          // Also ensure other upload areas don't have the class
          allUploadLabels.forEach(label => {
            if (label !== uploadArea && label.classList.contains('dragover')) {
              label.classList.remove('dragover');
            }
          });
        } else {
          // Remove class from all upload areas when not over any
          allUploadLabels.forEach(label => {
            if (label.classList.contains('dragover')) {
              label.classList.remove('dragover');
            }
          });
        }
      };
      this.boundHandlers.documentDrop = (e) => {
        e.preventDefault();
        
        // Remove dragover class from all upload areas
        const allUploadLabels = document.querySelectorAll('.image-upload-area');
        allUploadLabels.forEach(label => {
          if (label.classList.contains('dragover')) {
            label.classList.remove('dragover');
          }
        });
        
        // Check if drop is on upload area
        const uploadArea = e.target.closest('.image-upload-area') || e.target.closest('.image-upload-wrapper');
        if (uploadArea) {
          const files = e.dataTransfer.files;
          if (files.length > 0) {
            const file = files[0];
            
            // Check file type
            if (!file.type.startsWith('image/')) {
              this.showErrorMessage('Please drop an image file.');
              return;
            }
            
            // Check file size limit
            const maxSizeBytes = CONFIG.MAX_FILE_SIZE * 1024 * 1024;
            if (file.size > maxSizeBytes) {
              const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
              this.showErrorMessage(`File size (${fileSizeMB} MB) exceeds the maximum allowed size of ${CONFIG.MAX_FILE_SIZE} MB.`);
              this.updateUploadTextForFileSizeError();
              return;
            }
            
            // Set the file to the file input element (same as click/select behavior)
            const uploadInput = document.getElementById('custom-image');
            if (uploadInput) {
              const dataTransfer = new DataTransfer();
              dataTransfer.items.add(file);
              uploadInput.files = dataTransfer.files;
            }
            
            this.loadImage(file);
          }
        }
      };
      document.addEventListener('dragover', this.boundHandlers.documentDragover);
      document.addEventListener('drop', this.boundHandlers.documentDrop);
    }
    
    const showCropperBtn = document.getElementById('show-cropper-btn');
    const hideCropperBtn = document.getElementById('hide-cropper-btn');
    
    if (showCropperBtn) {
      this.boundHandlers.showCropper = () => this.showCanvas();
      showCropperBtn.addEventListener('click', this.boundHandlers.showCropper);
    }
    
    if (hideCropperBtn) {
      this.boundHandlers.hideCropper = () => this.hideCanvas();
      hideCropperBtn.addEventListener('click', this.boundHandlers.hideCropper);
    }
    
    // Store element references for cleanup
    this.boundHandlers.elements = { uploadInput, uploadLabel, showCropperBtn, hideCropperBtn };
  }

  removeEventListeners() {
    // Remove event listeners from stored elements
    if (this.boundHandlers.elements) {
      const { uploadInput, uploadLabel, showCropperBtn, hideCropperBtn } = this.boundHandlers.elements;
      
      if (uploadInput && this.boundHandlers.inputChange) {
        uploadInput.removeEventListener('change', this.boundHandlers.inputChange);
      }
      
      if (this.boundHandlers.documentDragover) {
        document.removeEventListener('dragover', this.boundHandlers.documentDragover);
      }
      if (this.boundHandlers.documentDrop) {
        document.removeEventListener('drop', this.boundHandlers.documentDrop);
      }
      
      if (showCropperBtn && this.boundHandlers.showCropper) {
        showCropperBtn.removeEventListener('click', this.boundHandlers.showCropper);
      }
      
      if (hideCropperBtn && this.boundHandlers.hideCropper) {
        hideCropperBtn.removeEventListener('click', this.boundHandlers.hideCropper);
      }
    }
    
    this.boundHandlers = {};
  }

  handleImageUpload(event) {
    debugLog('INFO', '🔍 ImageManager: handleImageUpload called');
    debugLog('INFO', '🔍 ImageManager: event:', event);
    const file = event.target.files[0];
    debugLog('INFO', '🔍 ImageManager: file:', file);
    
    if (!file) {
      debugLog('INFO', '🔍 ImageManager: No file selected');
      return;
    }
    
    // Check file type
    if (!file.type.startsWith('image/')) {
      debugLog('INFO', '🔍 ImageManager: File is not an image');
      this.showErrorMessage('Please select an image file.');
      // Reset file input
      event.target.value = '';
      return;
    }
    
    // Check file size (20 MB limit)
    const maxSizeBytes = CONFIG.MAX_FILE_SIZE * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      debugLog('INFO', '🔍 ImageManager: File size exceeds limit:', fileSizeMB, 'MB');
      this.showErrorMessage(`File size (${fileSizeMB} MB) exceeds the maximum allowed size of ${CONFIG.MAX_FILE_SIZE} MB.`);
      this.updateUploadTextForFileSizeError();
      // Reset file input
      event.target.value = '';
      return;
    }
    
    debugLog('INFO', '🔍 ImageManager: File is image, calling loadImage');
    this.loadImage(file);
  }

  loadImage(file) {
    debugLog('INFO', '🔍 ImageManager: loadImage called with file:', file);
    
    // Note: Custom image upload to Cloudinary is now deferred until PM button click
    // File is stored in the file input element for later upload
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        this.displayImage(img);
        
        if (window.mosaicApp && window.mosaicApp.mobileNavigationManager) {
          window.mosaicApp.mobileNavigationManager.setActiveTab('size');
        }
        
        if (window.innerWidth <= 750) {
          const mosaicWrapper = document.getElementById('mosaic__wrapper');
          const productInfoWrapper = document.querySelector('.product__info-wrapper');
          const imageUploadWrapper = document.querySelector('.image-upload-wrapper');
          
          if (mosaicWrapper) {
            mosaicWrapper.classList.remove('mobile-hidden-until-upload');
          }
          if (productInfoWrapper) {
            productInfoWrapper.classList.remove('mobile-hidden-until-upload');
          }
          if (imageUploadWrapper) {
            imageUploadWrapper.classList.add('hidden');
          }
        }
        
        this.stateManager.updateState({ uploadedImage: img });
        
        // Reset custom image upload flag when new image is uploaded (Cloudinary mode)
        if (window.mosaicApp?.mosaicImageCapture && window.mosaicApp.mosaicImageCapture.uploadProvider === 'cloudinary') {
          window.mosaicApp.mosaicImageCapture.hasUploadedCustomImage = false;
        }
        
        const zoomControls = document.querySelector('.zoom-controls');
        if (zoomControls) {
          zoomControls.classList.remove('hidden');
        }
        
        const mosaicStatusContainer = window.mosaicApp.paintingManager.ensureMosaicStatusContainer();
        mosaicStatusContainer.classList.remove('hidden');
        
        if (window.mosaicApp && window.mosaicApp.adjustmentManager && window.mosaicApp.undoRedoManager) {
          window.mosaicApp.undoRedoManager.clear();
          window.mosaicApp.adjustmentManager.resetAdjustments(true);
          
          if (window.mosaicApp.mosaicRenderer) {
            debugLog('INFO', '🔶 ImageManager: Clearing toolEdits for new image');
            window.mosaicApp.mosaicRenderer.toolEdits = [];
            debugLog('INFO', '🔶 ImageManager: toolEdits cleared, length:', window.mosaicApp.mosaicRenderer.toolEdits.length);
            
            window.mosaicApp.mosaicRenderer.compositeLayers();
          } else {
            debugLog('WARN', '🔶 ImageManager: mosaicRenderer not available for toolEdits clearing');
          }
          
        }
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  // Note: uploadCustomImageToCloudinary() method removed
  // Custom image upload is now deferred until PM button click in Cloudinary mode
  // File is stored in the file input element (#custom-image) for later upload

  displayImage(img) {
    debugLog('INFO', '🔍 ImageManager: displayImage called with img:', img);
    debugLog('INFO', '🔍 ImageManager: uploadCanvas:', this.uploadCanvas);
    debugLog('INFO', '🔍 ImageManager: uploadCtx:', this.uploadCtx);
    if (!this.uploadCanvas || !this.uploadCtx) {
      debugLog('ERROR', '🔍 ImageManager: Canvas or context not available');
      return;
    }
    
    this.uploadCanvas.width = img.width;
    this.uploadCanvas.height = img.height;
    this.uploadCtx.clearRect(0, 0, img.width, img.height);
    this.uploadCtx.drawImage(img, 0, 0);
    
    this.currentImage = img;
    debugLog('INFO', '🔍 ImageManager: Image drawn to canvas, calling showCanvas');
    this.showCanvas();
  }

  showCanvas() {
    debugLog('INFO', '🔍 ImageManager: showCanvas called');
    debugLog('INFO', '🔍 ImageManager: canvasWrapper:', this.canvasWrapper);
    if (this.canvasWrapper) {
      this.canvasWrapper.classList.remove('hidden');
      debugLog('INFO', '🔍 ImageManager: Removed hidden class from canvasWrapper');
    }
    this.uploadCanvas.style.display = 'block';
    debugLog('INFO', '🔍 ImageManager: Set uploadCanvas display to block');
    
    // Show cropper controls permanently after first image upload
    const cropperControls = document.querySelector('.cropper-controls');
    if (cropperControls) {
      cropperControls.classList.remove('hidden');
    }
    
    this.storeCanvasDimensions();
    
    this.notifyCropFrameRescale();
    
    const showCropperBtn = document.getElementById('show-cropper-btn');
    const hideCropperBtn = document.getElementById('hide-cropper-btn');
    if (showCropperBtn) showCropperBtn.classList.add('hidden');
    if (hideCropperBtn) hideCropperBtn.classList.remove('hidden');
  }

  hideCanvas() {
    if (this.canvasWrapper) {
      this.canvasWrapper.classList.add('hidden');
    }
    
    const showCropperBtn = document.getElementById('show-cropper-btn');
    const hideCropperBtn = document.getElementById('hide-cropper-btn');
    if (showCropperBtn) showCropperBtn.classList.remove('hidden');
    if (hideCropperBtn) hideCropperBtn.classList.add('hidden');
  }

  toggleCanvas() {
    if (this.canvasWrapper) {
      const isHidden = this.canvasWrapper.classList.contains('hidden');
      if (isHidden) {
        this.showCanvas();
      } else {
        this.hideCanvas();
      }
    }
  }

  clear() {
    if (this.uploadCanvas && this.uploadCtx) {
      this.uploadCtx.clearRect(0, 0, this.uploadCanvas.width, this.uploadCanvas.height);
    }
    
    this.currentImage = null;
    this.hideCanvas();

  }

  storeCanvasDimensions() {
    if (this.uploadCanvas && this.uploadCanvas.offsetWidth > 0) {
      this.storedCanvasDimensions = {
        width: this.uploadCanvas.offsetWidth,
        height: this.uploadCanvas.offsetHeight
      };
      debugLog('INFO', '🔍 ImageManager: Stored canvas dimensions:', this.storedCanvasDimensions);
    }
  }

  getCanvasDimensions() {
    if (this.uploadCanvas && this.uploadCanvas.offsetWidth > 0) {
      this.storedCanvasDimensions = {
        width: this.uploadCanvas.offsetWidth,
        height: this.uploadCanvas.offsetHeight
      };
    }
    return this.storedCanvasDimensions || { width: 0, height: 0 };
  }

  notifyCropFrameRescale() {
    if (window.mosaicApp && window.mosaicApp.cropFrame) {
      window.mosaicApp.cropFrame.rescaleOnShow();
    }
  }

  showErrorMessage(message) {
    // Remove existing message
    const existingMessage = document.getElementById('image-upload-error-message');
    if (existingMessage) {
      existingMessage.remove();
    }
    
    // Create new error message
    const errorMessage = document.createElement('div');
    errorMessage.id = 'image-upload-error-message';
    errorMessage.textContent = message;
    errorMessage.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(220, 38, 38, 0.95);
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      font-size: 14px;
      z-index: 10000;
      pointer-events: none;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      max-width: 400px;
      text-align: center;
    `;
    
    document.body.appendChild(errorMessage);
    
    // Clear previous message timer
    if (this.errorMessageTimer) {
      clearTimeout(this.errorMessageTimer);
    }
    
    // Remove after 5 seconds
    this.errorMessageTimer = setTimeout(() => {
      if (errorMessage.parentNode) {
        errorMessage.remove();
      }
      this.errorMessageTimer = null;
    }, 5000);
  }

  updateUploadTextForFileSizeError() {
    debugLog('INFO', '🔍 ImageManager: updateUploadTextForFileSizeError called');
    
    // Find all upload-text elements (there may be multiple - desktop/mobile)
    let uploadTextElements = document.querySelectorAll(CONFIG.SELECTORS.UPLOAD_TEXT);
    debugLog('INFO', '🔍 ImageManager: Found upload-text elements:', uploadTextElements.length);
    
    if (uploadTextElements.length === 0) {
      debugLog('WARN', '🔍 ImageManager: No upload-text elements found with selector:', CONFIG.SELECTORS.UPLOAD_TEXT);
      // Try alternative selector
      uploadTextElements = document.querySelectorAll('#upload-text');
      debugLog('INFO', '🔍 ImageManager: Found elements with #upload-text:', uploadTextElements.length);
      if (uploadTextElements.length === 0) {
        return;
      }
    }
    
    uploadTextElements.forEach((uploadText, index) => {
      debugLog('INFO', `🔍 ImageManager: Processing upload-text[${index}]:`, uploadText);
      debugLog('INFO', `🔍 ImageManager: upload-text[${index}] innerHTML:`, uploadText.innerHTML);
      
      // Find small element - try querySelector first
      let smallElement = uploadText.querySelector('small');
      
      debugLog('INFO', `🔍 ImageManager: upload-text[${index}] small element (querySelector):`, smallElement);
      
      if (smallElement) {
        const oldText = smallElement.textContent;
        smallElement.textContent = `Max file size: ${CONFIG.MAX_FILE_SIZE} MB`;
        smallElement.style.color = 'red';
        debugLog('INFO', `🔍 ImageManager: Updated small element from "${oldText}" to "Max file size: ${CONFIG.MAX_FILE_SIZE} MB"`);
      } else {
        debugLog('WARN', `🔍 ImageManager: No small element found in upload-text[${index}]`);
      }
    });
  }

  // Cleanup method for event listeners and DOM references
  destroy() {
    this.removeEventListeners();
    this.currentImage = null;
    if (this.uploadCanvas) {
      this.uploadCanvas.width = 1;
      this.uploadCanvas.height = 1;
    }
    this.uploadCanvas = null;
    this.uploadCtx = null;
    this.canvasWrapper = null;
    this.storedCanvasDimensions = null;
    
    // Clean up error message timer
    if (this.errorMessageTimer) {
      clearTimeout(this.errorMessageTimer);
      this.errorMessageTimer = null;
    }
    
    // Remove any existing error message
    const existingMessage = document.getElementById('image-upload-error-message');
    if (existingMessage) {
      existingMessage.remove();
    }
  }
}

// ============================================================================
// SHOPIFY VARIANT OBSERVER
// ============================================================================

/**
 * Observes Shopify product variant changes and keeps the application state in sync.
 * 
 * This class handles:
 * - Grid size variant changes (e.g., "32 × 64" → "64 × 96")
 * - Baseplate variant changes (e.g., "Regular" → "Framed")
 * - Price updates from the selected variant
 * - Auto-fixing unavailable variant combinations
 * - Shape-based grid size filtering (showing/hiding options based on selected shape)
 * 
 * It uses MutationObservers to watch for DOM changes in Shopify's variant selectors
 * and automatically updates the StateManager when variants change.
 */
class ShopifyVariantObserver {
  constructor(stateManager) {
    this.stateManager = stateManager;
    this.observers = [];
    this.currentGridSize = null;
    this.currentBaseplate = null;
    this.syncTimer = null;
    
    this.init();
  }

  init() {
    this.setupObservers();
    this.syncState();

  }

  setupObservers() {
    if (this.observers.length > 0) return;
    
    const variantSelects = document.querySelector('variant-selects');
    const priceElement = document.getElementById('price-selected-variant');
    
    [variantSelects, priceElement].filter(Boolean).forEach(element => {
      const observer = new MutationObserver(() => {
        if (this.syncTimer) {
          clearTimeout(this.syncTimer);
        }
        this.syncTimer = setTimeout(() => this.syncState(), 0);
      });
      
      observer.observe(element, { 
        childList: true, 
        subtree: true, 
        attributes: true, 
        attributeFilter: ['data-selected'] 
      });
      
      this.observers.push(observer);
    });
    

  }

  syncState() {
    this.updateGridSize();
    this.updateBaseplate();
    this.updatePrice();
    this.autoFixUnavailableVariant();
  }

  updateGridSize() {
    const gridSizeSpan = document.getElementById('Grid-Size');
    if (!gridSizeSpan) return;
    
    const gridSize = gridSizeSpan.getAttribute('data-selected');
    if (!gridSize || gridSize === this.currentGridSize) return;
    

    this.currentGridSize = gridSize;
    
    const parsedSize = this.parseGridSize(gridSize);
    if (parsedSize) {
      this.stateManager.updateState({ 
        gridWidth: parsedSize.width,
        gridHeight: parsedSize.height,
        gridSizeString: gridSize,
        gridAspectRatio: parsedSize.aspectRatio
      });
      
      // Reset both tool edits and slider adjustments when grid size changes
      if (window.mosaicApp && window.mosaicApp.undoRedoManager) {
        if (window.mosaicApp.adjustmentManager) {
          window.mosaicApp.adjustmentManager.resetAdjustments(false);
        }
        
        if (window.mosaicApp.mosaicRenderer) {
          debugLog('INFO', '🔶 ShopifyVariantObserver: Clearing toolEdits for grid size change');
          window.mosaicApp.mosaicRenderer.toolEdits = [];
          debugLog('INFO', '🔶 ShopifyVariantObserver: toolEdits cleared, length:', window.mosaicApp.mosaicRenderer.toolEdits.length);
          
          window.mosaicApp.mosaicRenderer.compositeLayers();
        } else {
          debugLog('WARN', '🔶 ShopifyVariantObserver: mosaicRenderer not available for toolEdits clearing');
        }
        
      }
    }
  }

  updateBaseplate() {
    const baseplateSpan = document.getElementById('Baseplate');
    if (!baseplateSpan) return;
    
    const baseplate = baseplateSpan.getAttribute('data-selected');
    if (!baseplate || baseplate === this.currentBaseplate) return;
    

    this.currentBaseplate = baseplate;
    this.stateManager.updateState({ baseplate });
  }

  updatePrice() {
    const priceSpan = document.querySelector('#price-selected-variant .price-item--regular.main-price');
    if (!priceSpan) return;
    
    const priceText = priceSpan.textContent.trim();
    if (priceText) {
      this.stateManager.updateState({ currentPrice: priceText });
    }
  }

  autoFixUnavailableVariant() {
    const { gridSizeString, baseplate } = this.stateManager.getState();
    if (!gridSizeString || !baseplate) return;
    
    const baseplateRadio = document.querySelector(`input[name="Baseplate"][value="${baseplate}"]`);
    if (!baseplateRadio?.classList.contains('disabled') && !baseplateRadio?.classList.contains('unavailable')) return;
    
    const availableRadio = document.querySelector('input[name="Baseplate"]:not(.disabled):not(.unavailable)');
    if (!availableRadio) return;
    

    availableRadio.checked = true;
    availableRadio.dispatchEvent(new Event('change', { bubbles: true }));
    this.stateManager.updateState({ baseplate: availableRadio.value });
  }

  updateVisibleByShape(selectedShape) {
    const allSizeLabels = document.querySelectorAll('.grid-size-label');
    let firstVisibleRadio = null;
    let currentSelectionValid = false;

    allSizeLabels.forEach(label => {
      const isMatch = label.classList.contains(`shape-${selectedShape}`);
      const radio = document.getElementById(label.getAttribute('for'));
      
      if (isMatch) {
        label.classList.remove('hidden');
        radio?.classList.remove('hidden');
        
        if (!firstVisibleRadio && radio && !radio.disabled) {
          firstVisibleRadio = radio;
        }
        if (radio?.checked) currentSelectionValid = true;
      } else {
        label.classList.add('hidden');
        radio?.classList.add('hidden');
      }
    });

    if (!currentSelectionValid && firstVisibleRadio) {
      firstVisibleRadio.checked = true;
      firstVisibleRadio.dispatchEvent(new Event('change', { bubbles: true }));
  
    }
  }

  parseGridSize(selectedSize) {
    const match = selectedSize.match(/(\d+)\s*[xX×]\s*(\d+)/);
    if (match) {
      const width = parseInt(match[1], 10);
      const height = parseInt(match[2], 10);
      return { width, height, aspectRatio: width / height };
    }
    return null;
  }

  disconnect() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
    
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
  }

  resetup() {
    this.disconnect();
    this.setupObservers();
  }
}

class CropFrame {
  
  /**
   * Manages the interactive crop frame overlay for selecting image regions.
   * 
   * This class handles:
   * - Displaying a resizable crop frame with locked aspect ratio from grid settings
   * - Drag and drop functionality for moving the crop frame within image boundaries
   * - Resize operations on all edges and corners with proper cursor feedback
   * - Boundary constraints to keep the frame within the uploaded image area
   * - Coordinate conversion between display pixels and image pixels for accurate cropping
   * - Visual overlay to darken the unselected area outside the crop frame
   * - Touch and mouse event handling for desktop and mobile compatibility
   * - Saving crop coordinates to StateManager for use by MosaicRenderer
   * 
   * The crop frame maintains the aspect ratio determined by the selected grid size
   * and provides visual feedback through cursor changes and overlay effects.
   */
  constructor(stateManager, imageManager) {
    this.stateManager = stateManager;
    this.imageManager = imageManager;
    this.cropFrameElement = null;
    this.overlayElement = null;
    this.aspectRatio = 1;
    
    // Interaction state
    this.interaction = {
      isActive: false,
      type: null,
      edge: null,
      startPos: { x: 0, y: 0, left: 0, top: 0, width: 0, height: 0 }
    };
    
    // Event handlers
    this.boundHandlers = {};
    
    this.init();
  }

  init() {
    this.setupCropFrameElement();
    this.setupEventListeners();
    this.setupResizeListener();
    this.subscribeToStateChanges();

  }

  setupCropFrameElement() {
    this.cropFrameElement = document.getElementById('crop-frame');
    if (this.cropFrameElement) {
  
    } else {
      debugLog('WARN', 'CropFrame: #crop-frame element not found');
    }
    
    this.setupOverlayElement();
  }

  setupOverlayElement() {
    this.overlayElement = document.getElementById('crop-overlay');
    if (!this.overlayElement) {
      this.overlayElement = document.createElement('div');
      this.overlayElement.id = 'crop-overlay';
      this.overlayElement.className = 'crop-overlay';
      
      const canvas = this.imageManager.uploadCanvas;
      if (canvas && canvas.parentNode) {
        canvas.parentNode.insertBefore(this.overlayElement, canvas.nextSibling);
      }
    }
  }

  setupEventListeners() {
    if (!this.cropFrameElement) return;

    this.removeEventListeners();

    this.boundHandlers = {
      pointerDown: this._handlePointerDown.bind(this),
      pointerMove: this._handlePointerMove.bind(this),
      pointerUp: this._handlePointerUp.bind(this),
      cursorChange: this._handleCursorChange.bind(this)
    };

    this.cropFrameElement.addEventListener('mousedown', this.boundHandlers.pointerDown);
    this.cropFrameElement.addEventListener('touchstart', this.boundHandlers.pointerDown, { passive: false });
    window.addEventListener('mousemove', this.boundHandlers.pointerMove);
    window.addEventListener('touchmove', this.boundHandlers.pointerMove, { passive: false });
    window.addEventListener('mouseup', this.boundHandlers.pointerUp);
    window.addEventListener('touchend', this.boundHandlers.pointerUp);
    this.cropFrameElement.addEventListener('mousemove', this.boundHandlers.cursorChange);
  }

  removeEventListeners() {
    if (!this.cropFrameElement || !this.boundHandlers) return;

    this.cropFrameElement.removeEventListener('mousedown', this.boundHandlers.pointerDown);
    this.cropFrameElement.removeEventListener('touchstart', this.boundHandlers.pointerDown);
    window.removeEventListener('mousemove', this.boundHandlers.pointerMove);
    window.removeEventListener('touchmove', this.boundHandlers.pointerMove);
    window.removeEventListener('mouseup', this.boundHandlers.pointerUp);
    window.removeEventListener('touchend', this.boundHandlers.pointerUp);
    this.cropFrameElement.removeEventListener('mousemove', this.boundHandlers.cursorChange);

    if (this.boundResizeHandler) {
      window.removeEventListener('resize', this.boundResizeHandler);
    }

    this.boundHandlers = {};
  }

  setupResizeListener() {
    let resizeTimeout;
    this.boundResizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.handleViewportResize();
      }, 250);
    };
    
    window.addEventListener('resize', this.boundResizeHandler);
  }

  handleViewportResize() {
    if (!this.cropFrameElement) {
      return;
    }

    const canvas = this.imageManager.uploadCanvas;
    if (!canvas) {
      debugLog('WARN', '🔶 CropFrame: Canvas not found during resize');
      return;
    }

    this.imageManager.storeCanvasDimensions();
    
    const { width: canvasDisplayWidth, height: canvasDisplayHeight } = this.imageManager.getCanvasDimensions();
    
    if (!canvasDisplayWidth || !canvasDisplayHeight) {
      return;
    }
    
    const isCropFrameVisible = this.cropFrameElement.style.display !== 'none';
    
    if (isCropFrameVisible) {
      debugLog('INFO', '🔶 CropFrame: Handling viewport resize - scaling crop frame and updating overlay');
      
      if (!this.originalCanvasSize) {
        this.originalCanvasSize = {
          width: canvasDisplayWidth,
          height: canvasDisplayHeight
        };
        return;
      }
      
      const currentPos = this.getPositionAndSize();
      
      const scaleX = canvasDisplayWidth / this.originalCanvasSize.width;
      const scaleY = canvasDisplayHeight / this.originalCanvasSize.height;
      
      // Scale the crop frame position and size
      const scaledX = currentPos.x * scaleX;
      const scaledY = currentPos.y * scaleY;
      const scaledW = currentPos.w * scaleX;
      const scaledH = currentPos.h * scaleY;
      
      // Update the crop frame with scaled dimensions
      this.setPositionAndSize(scaledX, scaledY, scaledW, scaledH);
      
      // Save the updated crop state (without clearing toolEdits/adjustments)
      this.saveCropStateOnly();
    }
    
    // Always update the original canvas size for next resize
    this.originalCanvasSize = {
      width: canvasDisplayWidth,
      height: canvasDisplayHeight
    };
  }

  subscribeToStateChanges() {
    this.stateManager.subscribe('stateChanged', (data) => {
      const { oldState, newState } = data;
      
      // Update crop frame when grid size changes
      if (oldState.gridAspectRatio !== newState.gridAspectRatio) {
        this.setAspectRatio(newState.gridAspectRatio);
      }
      
      // Update crop frame when image changes
      if (oldState.uploadedImage !== newState.uploadedImage && newState.uploadedImage) {
        this.resetToDefault();
      }
      
    });
  }

  resetToDefault(saveState = true) {
    // Get the current canvas from ImageManager
    const canvas = this.imageManager.uploadCanvas;
    if (!canvas) {
      debugLog('WARN', '⚠️ Canvas element not found for crop frame reset');
      return;
    }
    
    // Use stored canvas dimensions when canvas is hidden
    const canvasDimensions = this.imageManager.getCanvasDimensions();
    const canvasDisplayWidth = canvasDimensions.width;
    const canvasDisplayHeight = canvasDimensions.height;
    
    // Initialize the original canvas size for scaling calculations
    this.originalCanvasSize = {
      width: canvasDisplayWidth,
      height: canvasDisplayHeight
    };
    
    let cropW = canvasDisplayWidth * 0.8; // Start with 80% of canvas display width
    let cropH = cropW / this.aspectRatio;
    
    if (cropH > canvasDisplayHeight) {
      cropH = canvasDisplayHeight * 0.8;
      cropW = cropH * this.aspectRatio;
    }
    
    const cropX = (canvasDisplayWidth - cropW) / 2;
    const cropY = (canvasDisplayHeight - cropH) / 2;
    
    this.setPositionAndSize(cropX, cropY, cropW, cropH);
    if (saveState) {
      this.saveCropState(); // Save the initial crop state
    }

  }

  setAspectRatio(ratio) {
    if (this.aspectRatio !== ratio) {
      this.aspectRatio = ratio;
          this.resetToDefault();
    }
  }

  restoreCropPosition(cropRect, imageBounds) {
    const canvas = this.imageManager.uploadCanvas;
    if (!canvas) {
      debugLog('WARN', '⚠️ Canvas element not found for crop position restoration');
      return;
    }
    
    // Convert image coordinates back to display coordinates
    const scaleX = canvas.offsetWidth / imageBounds.width;
    const scaleY = canvas.offsetHeight / imageBounds.height;
    
    const displayX = cropRect.x * scaleX;
    const displayY = cropRect.y * scaleY;
    const displayW = cropRect.width * scaleX;
    const displayH = cropRect.height * scaleY;
    
    this.setPositionAndSize(displayX, displayY, displayW, displayH);

  }

  setPositionAndSize(x, y, w, h) {
    // Only show the crop frame if the canvas wrapper is visible
    const isCanvasVisible = this.imageManager.canvasWrapper && 
                           !this.imageManager.canvasWrapper.classList.contains('hidden');
    
    if (isCanvasVisible) {
      this.cropFrameElement.style.display = 'block';
    }
    
    this.cropFrameElement.style.left = x + 'px';
    this.cropFrameElement.style.top = y + 'px';
    this.cropFrameElement.style.width = w + 'px';
    this.cropFrameElement.style.height = h + 'px';
    this.cropFrameElement.dataset.x = x;
    this.cropFrameElement.dataset.y = y;
    this.cropFrameElement.dataset.w = w;
    this.cropFrameElement.dataset.h = h;
    this.cropFrameElement.dataset.ratio = this.aspectRatio;
    
    // Always update overlay to match crop frame position (works with stored dimensions)
    this.updateOverlay();
  }

  getPositionAndSize() {
    return {
      x: parseFloat(this.cropFrameElement.dataset.x),
      y: parseFloat(this.cropFrameElement.dataset.y),
      w: parseFloat(this.cropFrameElement.dataset.w),
      h: parseFloat(this.cropFrameElement.dataset.h),
      ratio: parseFloat(this.cropFrameElement.dataset.ratio)
    };
  }

  _detectEdgeOrCorner(x, y, rect, isTouch = false) {
    const margin = isTouch ? 16 : 8;
    
    // Define corner and edge detection rules
    const corners = [
      { condition: x < margin && y < margin, edge: 'top-left' },
      { condition: x > rect.width - margin && y < margin, edge: 'top-right' },
      { condition: x < margin && y > rect.height - margin, edge: 'bottom-left' },
      { condition: x > rect.width - margin && y > rect.height - margin, edge: 'bottom-right' }
    ];
    
    const edges = [
      { condition: x < margin, edge: 'left' },
      { condition: x > rect.width - margin, edge: 'right' },
      { condition: y < margin, edge: 'top' },
      { condition: y > rect.height - margin, edge: 'bottom' }
    ];
    
    // Check corners first
    const corner = corners.find(c => c.condition);
    if (corner) {
      return { edge: corner.edge, isCorner: true };
    }
    
    // Then check edges
    const edge = edges.find(e => e.condition);
    if (edge) {
      return { edge: edge.edge, isCorner: false };
    }
    
    return { edge: null, isCorner: false };
  }

  // Unified pointer event handlers
  _handlePointerDown(e) {
    const rect = this.cropFrameElement.getBoundingClientRect();
    const isTouch = e.type === 'touchstart';
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    const x = isTouch ? clientX - rect.left : e.offsetX;
    const y = isTouch ? clientY - rect.top : e.offsetY;
    
    const { edge } = this._detectEdgeOrCorner(x, y, rect, isTouch);

    if (edge) {
      e.preventDefault();
      this.interaction.isActive = true;
      this.interaction.type = 'resize';
      this.interaction.edge = edge;
      this._startInteraction(clientX, clientY);
    } else if (x > 8 && x < rect.width - 8 && y > 8 && y < rect.height - 8) {
      e.preventDefault();
      this.interaction.isActive = true;
      this.interaction.type = 'move';
      this._startInteraction(clientX, clientY);
    }
  }

  _handlePointerMove(e) {
    if (!this.interaction.isActive) return;
    
    const isTouch = e.type === 'touchmove';
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    
    if (this.interaction.type === 'move') {
      this._handleMove(clientX, clientY);
    } else if (this.interaction.type === 'resize') {
      this._handleResize(clientX, clientY);
    }
  }

  _handlePointerUp(e) {
    if (this.interaction.isActive) {
      this._endInteraction();
    }
  }

  _startInteraction(clientX, clientY) {
    const pos = this.getPositionAndSize();
    this.interaction.startPos = { 
      x: clientX, 
      y: clientY, 
      left: pos.x, 
      top: pos.y, 
      width: pos.w, 
      height: pos.h 
    };
    document.body.classList.add('user-select-none');
  }

  _endInteraction() {
    this.interaction.isActive = false;
    this.interaction.type = null;
    this.interaction.edge = null;
    document.body.classList.remove('user-select-none');
    this.saveCropState();
  }

  _handleMove(clientX, clientY) {
    const { x: startX, y: startY, left, top, width, height } = this.interaction.startPos;
    const dx = clientX - startX;
    const dy = clientY - startY;
    
    let newLeft = left + dx;
    let newTop = top + dy;
    
    // Constrain to canvas boundaries
    const canvas = this.imageManager.uploadCanvas;
    if (canvas) {
      newLeft = Math.max(0, Math.min(canvas.offsetWidth - width, newLeft));
      newTop = Math.max(0, Math.min(canvas.offsetHeight - height, newTop));
    }
    
    this.setPositionAndSize(newLeft, newTop, width, height);
  }

  _handleResize(clientX, clientY) {
    const { x: startX, y: startY, left, top, width, height } = this.interaction.startPos;
    const dx = clientX - startX;
    const dy = clientY - startY;
    const ratio = this.aspectRatio;
    const canvas = this.imageManager.uploadCanvas;
    
    // Calculate initial dimensions based on resize edge
    const dimensions = this._calculateResizeDimensions(dx, dy, left, top, width, height, ratio);
    
    // Apply boundary constraints
    const constrained = this._applyBoundaryConstraints(dimensions, canvas, ratio);
    
    this.setPositionAndSize(constrained.left, constrained.top, constrained.width, constrained.height);
  }

  _calculateResizeDimensions(dx, dy, left, top, width, height, ratio) {
    let newLeft = left, newTop = top, newW = width, newH = height;

    switch (this.interaction.edge) {
      case 'right':
        newW = Math.max(20, width + dx);
        newH = newW / ratio;
        break;
      case 'left':
        newW = Math.max(20, width - dx);
        newH = newW / ratio;
        newLeft = left + (width - newW);
        break;
      case 'bottom':
        newH = Math.max(20, height + dy);
        newW = newH * ratio;
        break;
      case 'top':
        newH = Math.max(20, height - dy);
        newW = newH * ratio;
        newTop = top + (height - newH);
        break;
      case 'top-left':
        newH = Math.max(20, height - dy);
        newW = newH * ratio;
        newTop = top + (height - newH);
        newLeft = left + (width - newW);
        break;
      case 'top-right':
        newH = Math.max(20, height - dy);
        newW = newH * ratio;
        newTop = top + (height - newH);
        break;
      case 'bottom-left':
        newH = Math.max(20, height + dy);
        newW = newH * ratio;
        newLeft = left + (width - newW);
        break;
      case 'bottom-right':
        newH = Math.max(20, height + dy);
        newW = newH * ratio;
        break;
    }

    return { left: newLeft, top: newTop, width: newW, height: newH };
  }

  _applyBoundaryConstraints(dimensions, canvas, ratio) {
    if (!canvas) return dimensions;

    const { left, top, width, height } = dimensions;
    const { left: startLeft, top: startTop, width: startW, height: startH } = this.interaction.startPos;
    const canvasDisplayWidth = canvas.offsetWidth;
    const canvasDisplayHeight = canvas.offsetHeight;
    
    let newLeft = left, newTop = top, newW = width, newH = height;
    const edge = this.interaction.edge;

    // Apply specific boundary constraints for each edge type
    switch (edge) {
      case 'right':
        if (newLeft + newW > canvasDisplayWidth) {
          newW = canvasDisplayWidth - newLeft;
          newH = newW / ratio;
        }
        if (newTop + newH > canvasDisplayHeight) {
          newH = canvasDisplayHeight - newTop;
          newW = newH * ratio;
          if (newLeft + newW > canvasDisplayWidth) {
            newW = canvasDisplayWidth - newLeft;
            newH = newW / ratio;
          }
        }
        break;
      case 'left':
        if (newLeft < 0) {
          newLeft = 0;
          newW = startW + startLeft;
          newH = newW / ratio;
        }
        if (newTop + newH > canvasDisplayHeight) {
          newH = canvasDisplayHeight - newTop;
          newW = newH * ratio;
          newLeft = startLeft + (startW - newW);
          if (newLeft < 0) {
            newLeft = 0;
            newW = startW + startLeft;
            newH = newW / ratio;
          }
        }
        break;
      case 'bottom':
        if (newTop + newH > canvasDisplayHeight) {
          newH = canvasDisplayHeight - newTop;
          newW = newH * ratio;
        }
        if (newLeft + newW > canvasDisplayWidth) {
          newW = canvasDisplayWidth - newLeft;
          newH = newW / ratio;
          if (newTop + newH > canvasDisplayHeight) {
            newH = canvasDisplayHeight - newTop;
            newW = newH * ratio;
          }
        }
        break;
      case 'top':
        if (newTop < 0) {
          newTop = 0;
          newH = startH + startTop;
          newW = newH * ratio;
        }
        if (newLeft + newW > canvasDisplayWidth) {
          newW = canvasDisplayWidth - newLeft;
          newH = newW / ratio;
          newTop = startTop + (startH - newH);
          if (newTop < 0) {
            newTop = 0;
            newH = startH + startTop;
            newW = newH * ratio;
          }
        }
        break;
      case 'top-left':
        if (newTop < 0) {
          newTop = 0;
          newH = startH + startTop;
          newW = newH * ratio;
          newLeft = startLeft + (startW - newW);
        }
        if (newLeft < 0) {
          newLeft = 0;
          newW = startW + startLeft;
          newH = newW / ratio;
          newTop = startTop + (startH - newH);
          if (newTop < 0) {
            newTop = 0;
            newH = startH + startTop;
            newW = newH * ratio;
            newLeft = startLeft + (startW - newW);
          }
        }
        break;
      case 'top-right':
        if (newTop < 0) {
          newTop = 0;
          newH = startH + startTop;
          newW = newH * ratio;
        }
        if (newLeft + newW > canvasDisplayWidth) {
          newW = canvasDisplayWidth - newLeft;
          newH = newW / ratio;
          newTop = startTop + (startH - newH);
          if (newTop < 0) {
            newTop = 0;
            newH = startH + startTop;
            newW = newH * ratio;
          }
        }
        break;
      case 'bottom-left':
        if (newLeft < 0) {
          newLeft = 0;
          newW = startW + startLeft;
          newH = newW / ratio;
        }
        if (newTop + newH > canvasDisplayHeight) {
          newH = canvasDisplayHeight - newTop;
          newW = newH * ratio;
          newLeft = startLeft + (startW - newW);
          if (newLeft < 0) {
            newLeft = 0;
            newW = startW + startLeft;
            newH = newW / ratio;
          }
        }
        break;
      case 'bottom-right':
        if (newLeft + newW > canvasDisplayWidth) {
          newW = canvasDisplayWidth - newLeft;
          newH = newW / ratio;
        }
        if (newTop + newH > canvasDisplayHeight) {
          newH = canvasDisplayHeight - newTop;
          newW = newH * ratio;
          if (newLeft + newW > canvasDisplayWidth) {
            newW = canvasDisplayWidth - newLeft;
            newH = newW / ratio;
          }
        }
        break;
    }

    // Ensure minimum size
    if (newW < 20) { newW = 20; newH = newW / ratio; }
    if (newH < 20) { newH = 20; newW = newH * ratio; }

    return { left: newLeft, top: newTop, width: newW, height: newH };
  }



  _handleCursorChange(e) {
    const rect = this.cropFrameElement.getBoundingClientRect();
    const x = e.offsetX, y = e.offsetY;
    
    this.cropFrameElement.classList.remove('cursor-resize-horizontal', 'cursor-resize-vertical', 'cursor-resize-diagonal', 'cursor-resize-diagonal-alt', 'cursor-move');
    
    const { edge, isCorner } = this._detectEdgeOrCorner(x, y, rect, false);
    
    if (edge) {
      if (isCorner) {
        if (edge === 'top-left' || edge === 'bottom-right') {
          this.cropFrameElement.classList.add('cursor-resize-diagonal');
        } else {
          this.cropFrameElement.classList.add('cursor-resize-diagonal-alt');
        }
      } else {
        if (edge === 'left' || edge === 'right') {
          this.cropFrameElement.classList.add('cursor-resize-horizontal');
        } else {
          this.cropFrameElement.classList.add('cursor-resize-vertical');
        }
      }
    } else {
      this.cropFrameElement.classList.add('cursor-move');
    }
  }



  updateOverlay() {
    if (!this.overlayElement || !this.imageManager.uploadCanvas) return;
    
    const cropData = this.getPositionAndSize();
    const { width: canvasWidth, height: canvasHeight } = this.imageManager.getCanvasDimensions();
    
    // Set overlay to cover the entire canvas
    this.overlayElement.style.position = 'absolute';
    this.overlayElement.style.left = '0px';
    this.overlayElement.style.top = '0px';
    this.overlayElement.style.width = canvasWidth + 'px';
    this.overlayElement.style.height = canvasHeight + 'px';
    this.overlayElement.style.display = 'block';
    
    // Create the cutout effect using CSS clip-path
    const clipPath = `polygon(
      0% 0%, 
      0% 100%, 
      ${cropData.x}px 100%, 
      ${cropData.x}px ${cropData.y}px, 
      ${cropData.x + cropData.w}px ${cropData.y}px, 
      ${cropData.x + cropData.w}px ${cropData.y + cropData.h}px, 
      ${cropData.x}px ${cropData.y + cropData.h}px, 
      ${cropData.x}px 100%, 
      100% 100%, 
      100% 0%
    )`;
    
    this.overlayElement.style.clipPath = clipPath;
  }

  hideCropFrame() {
    if (this.cropFrameElement) {
      this.cropFrameElement.style.display = 'none';
    }
    if (this.overlayElement) {
      this.overlayElement.style.display = 'none';
    }
  }

  // Rescale crop frame when canvas becomes visible after being hidden
  rescaleOnShow() {
    if (!this.cropFrameElement || !this.imageManager.uploadCanvas) {
      return;
    }

    debugLog('INFO', '🔶 CropFrame: Rescaling crop frame on show');
    
    // Get current canvas dimensions
    const { width: currentWidth, height: currentHeight } = this.imageManager.getCanvasDimensions();
    
    // Check if we have stored dimensions to compare against
    if (this.originalCanvasSize && this.originalCanvasSize.width > 0 && this.originalCanvasSize.height > 0) {
      const scaleX = currentWidth / this.originalCanvasSize.width;
      const scaleY = currentHeight / this.originalCanvasSize.height;
      
      debugLog('INFO', '🔶 CropFrame: Scale factors:', { scaleX, scaleY });
      debugLog('INFO', '🔶 CropFrame: Original size:', this.originalCanvasSize);
      debugLog('INFO', '🔶 CropFrame: Current size:', { width: currentWidth, height: currentHeight });
      
      // Only rescale if there's a significant change
      if (Math.abs(scaleX - 1) > 0.01 || Math.abs(scaleY - 1) > 0.01) {
        const currentPos = this.getPositionAndSize();
        
        // Scale the crop frame position and size
        const scaledX = currentPos.x * scaleX;
        const scaledY = currentPos.y * scaleY;
        const scaledW = currentPos.w * scaleX;
        const scaledH = currentPos.h * scaleY;
        
        debugLog('INFO', '🔶 CropFrame: Scaling from', currentPos, 'to', { x: scaledX, y: scaledY, w: scaledW, h: scaledH });
        
        // Update the crop frame with scaled dimensions
        this.setPositionAndSize(scaledX, scaledY, scaledW, scaledH);
        
        // Save the updated crop state
        this.saveCropStateOnly();
      }
    }
    
    // Update the original canvas size for future comparisons
    this.originalCanvasSize = {
      width: currentWidth,
      height: currentHeight
    };
  }

  saveCropState() {
    const cropData = this.getPositionAndSize();
    const canvas = this.imageManager.uploadCanvas;
    

    
    // Convert display coordinates to image coordinates
    let imageX = cropData.x;
    let imageY = cropData.y;
    let imageWidth = cropData.w;
    let imageHeight = cropData.h;
    
    if (canvas && canvas.width && canvas.height) {
      const { width: displayW, height: displayH } = this.imageManager.getCanvasDimensions();
      
      // Guard against zero dimensions
      if (displayW > 0 && displayH > 0) {
        const scaleX = canvas.width / displayW;
        const scaleY = canvas.height / displayH;
        
        imageX = cropData.x * scaleX;
        imageY = cropData.y * scaleY;
        imageWidth = cropData.w * scaleX;
        imageHeight = cropData.h * scaleY;
      }
    }
    
    const cropRect = { x: imageX, y: imageY, width: imageWidth, height: imageHeight };
    const imageBounds = { x: 0, y: 0, width: canvas?.width || 0, height: canvas?.height || 0 };
    

    
    this.stateManager.updateState({
      cropRect,
      imageBounds
    });

    // Reset both tool edits and slider adjustments when crop frame changes
    if (window.mosaicApp && window.mosaicApp.undoRedoManager) {
      // Reset slider adjustments to defaults
      if (window.mosaicApp.adjustmentManager) {
        window.mosaicApp.adjustmentManager.resetAdjustments(false); // Don't save to undo stack
      }
      
      // Clear tool edits for new crop
      if (window.mosaicApp.mosaicRenderer) {
        debugLog('INFO', '🔶 CropFrame: Clearing toolEdits for crop change');
        window.mosaicApp.mosaicRenderer.toolEdits = [];
        debugLog('INFO', '🔶 CropFrame: toolEdits cleared, length:', window.mosaicApp.mosaicRenderer.toolEdits.length);
        
        // Re-composite layers to show cleared toolEdits
        window.mosaicApp.mosaicRenderer.compositeLayers();
      } else {
        debugLog('WARN', '🔶 CropFrame: mosaicRenderer not available for toolEdits clearing');
      }
      
      // Clear undo/redo history and reset first-edit warning flag
      window.mosaicApp.undoRedoManager.clear();
      window.mosaicApp.undoRedoManager.warningShownThisPage = false;
      
      // State zero will be saved by generateMosaic() when the mosaic is regenerated
    }
  }

  /**
   * Save crop state without clearing toolEdits or adjustments
   * Used for viewport resize where we only need to update crop coordinates
   */
  saveCropStateOnly() {
    const cropData = this.getPositionAndSize();
    const canvas = this.imageManager.uploadCanvas;
    
    // Convert display coordinates to image coordinates
    let imageX = cropData.x;
    let imageY = cropData.y;
    let imageWidth = cropData.w;
    let imageHeight = cropData.h;
    
    if (canvas && canvas.width && canvas.height) {
      const displayW = canvas.offsetWidth;
      const displayH = canvas.offsetHeight;
      // Minimal guard against transient zero layout sizes
      if (!displayW || !displayH) {
        return;
      }
      const scaleX = canvas.width / displayW;
      const scaleY = canvas.height / displayH;
      imageX = cropData.x * scaleX;
      imageY = cropData.y * scaleY;
      imageWidth = cropData.w * scaleX;
      imageHeight = cropData.h * scaleY;
    }
    
    // Minimal clamp within image bounds to avoid invalid getImageData
    const imgW = canvas?.width || 0;
    const imgH = canvas?.height || 0;
    if (!imgW || !imgH) {
      return;
    }
    imageX = Math.max(0, Math.min(imgW, Math.floor(imageX)));
    imageY = Math.max(0, Math.min(imgH, Math.floor(imageY)));
    imageWidth = Math.max(1, Math.min(imgW - imageX, Math.floor(imageWidth)));
    imageHeight = Math.max(1, Math.min(imgH - imageY, Math.floor(imageHeight)));
    
    const cropRect = { x: imageX, y: imageY, width: imageWidth, height: imageHeight };
    const imageBounds = { x: 0, y: 0, width: imgW, height: imgH };
    
    // Only update the crop coordinates, don't clear toolEdits or adjustments
    this.stateManager.updateState({
      cropRect,
      imageBounds
    });
  }

  getCropData() {
    const cropData = this.getPositionAndSize();
    return {
      cropRect: { x: cropData.x, y: cropData.y, width: cropData.w, height: cropData.h },
      imageBounds: { x: 0, y: 0, width: this.imageManager.uploadCanvas?.width || 0, height: this.imageManager.uploadCanvas?.height || 0 }
    };
  }

  // Cleanup method to remove all event listeners and reset state
  destroy() {
    this.removeEventListeners();
    this.hideCropFrame();
    this.interaction = { isActive: false, type: null, edge: null, startPos: { x: 0, y: 0, left: 0, top: 0, width: 0, height: 0 } };
    
    // Remove overlay element if it exists
    if (this.overlayElement && this.overlayElement.parentNode) {
      this.overlayElement.parentNode.removeChild(this.overlayElement);
      this.overlayElement = null;
    }
    

  }
}

class MosaicRenderer {
  constructor(stateManager) {
    this.stateManager = stateManager;
    this.mosaicCanvas = null;
    this.mosaicCtx = null;
    this.mosaicColors = this.initializeMosaicColors();
    this.colorCache = new Map(); // Cache for color matching like v1
    this.mosaicData = null; // Mosaic colors without frame
    this.currentMosaic = []; // Single source of truth - exactly what is displayed
    this.currentlyAdjustingMosaic = []; // Live preview during slider adjustments
    this.toolEdits = []; // Tool-based changes only (new for non-cumulative system)
    this.tempCanvas = null; // Track temporary canvas for cleanup
    this.tempCtx = null; // Track temporary context for cleanup
    
    // Centralized configuration
    this.GRID_SPACE_RATIO = 0.01; // 1% of cell size for grid line thickness
    this.GRID_LINE_COLOR = '#ffffff'; // Color for grid lines between mosaic cells
    this.STUD_RADIUS_RATIO = 0.38; // Radius of stud overlays as ratio of cell size
    this.STUD_CENTER_OFFSET = 0.5; // Center offset for stud positioning
    
    this.init();

  }

  // Helper method to calculate grid space consistently
  getGridSpace(cellSize) {
    return cellSize * this.GRID_SPACE_RATIO;
  }

  // Helper method to get grid line color consistently
  getGridLineColor() {
    return this.GRID_LINE_COLOR;
  }

  // Helper method to get stud radius consistently
  getStudRadius(cellSize) {
    return cellSize * this.STUD_RADIUS_RATIO;
  }

  // Helper method to get stud center offset consistently
  getStudCenterOffset(cellSize) {
    return cellSize * this.STUD_CENTER_OFFSET;
  }

  // Helper method to get frame width consistently
  getFrameWidth() {
    return this.stateManager.isFramed() ? CONFIG.FRAME_WIDTH : 0;
  }

  // Helper method to calculate total grid dimensions including frame
  getTotalGridDimensions(gridWidth, gridHeight) {
    const frameWidth = this.getFrameWidth();
    return {
      width: gridWidth + (frameWidth * 2),
      height: gridHeight + (frameWidth * 2),
      frameWidth
    };
  }

  // Helper method to check if a cell position is a frame cell
  isFrameCell(cellPos, gridWidth, gridHeight) {
    const frameWidth = this.getFrameWidth();
    return frameWidth && (
      cellPos.y < frameWidth || 
      cellPos.y >= gridHeight + frameWidth || 
      cellPos.x < frameWidth || 
      cellPos.x >= gridWidth + frameWidth
    );
  }

  init() {
    this.setupCanvas();
    this.subscribeToStateChanges();
    this.setupResizeListener();
  }

  setupCanvas() {
    this.mosaicCanvas = document.getElementById('image-edit-canvas');
    if (this.mosaicCanvas) {
      this.mosaicCtx = this.mosaicCanvas.getContext('2d', { willReadFrequently: true });
  
    } else {
      debugLog('WARN', 'Mosaic canvas not found');
    }
  }

  setupResizeListener() {
    // Debounced resize handler to avoid excessive re-rendering
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.handleViewportResize();
      }, 250);
    });
  }

  // Handle viewport resize - only recalculate cell size and re-render existing mosaic
  handleViewportResize() {
    // Only proceed if we have a mosaic to resize
    if (!this.currentMosaic || this.currentMosaic.length === 0) {
      debugLog('INFO', '🔵 MosaicRenderer: No mosaic to resize, skipping viewport resize');
      return;
    }

    debugLog('INFO', '🔵 MosaicRenderer: Handling viewport resize - preserving edits');
    
    // Use renderMosaicFromCellColors to preserve all edits without changing appearance
    this.renderMosaicFromCellColors();
  }

  subscribeToStateChanges() {
    this.stateManager.subscribe('stateChanged', (data) => {
      const { oldState, newState } = data;
      
      debugLog('INFO', '🔵 MosaicRenderer: State change detected');
      debugLog('INFO', '🔵 MosaicRenderer: isUndoRedoOperation:', window.mosaicApp?.undoRedoManager?.isUndoRedoOperation);
      debugLog('INFO', '🔵 MosaicRenderer: shouldGenerateMosaic result:', this.shouldGenerateMosaic(oldState, newState));
      debugLog('INFO', '🔵 MosaicRenderer: adjustments changed:', JSON.stringify(oldState.adjustments) !== JSON.stringify(newState.adjustments));
      debugLog('INFO', '🔵 MosaicRenderer: baseplate changed:', oldState.baseplate !== newState.baseplate);
      
      // Skip rendering during undo/redo operations (already handled by setMosaicState)
      if (window.mosaicApp?.undoRedoManager?.isUndoRedoOperation) {
        debugLog('INFO', '🔵 MosaicRenderer: Skipping re-render during undo/redo operation (already handled by setMosaicState)');
        return;
      }
      
      // Determine which rendering method to use
      const renderingMethod = this.getRenderingMethod(oldState, newState);
      if (renderingMethod) {
        debugLog('INFO', '🔵 MosaicRenderer: Calling', renderingMethod);
        this[renderingMethod]();
      }
    });
  }

  // Determine which rendering method to use based on state changes
  getRenderingMethod(oldState, newState) {
    // Generate new mosaic from image when fundamental changes occur
    if (this.shouldGenerateMosaic(oldState, newState)) {
      return 'generateMosaic';
    }
    
    // Render from stored colors when preserving edits
    if (oldState.baseplate !== newState.baseplate) {
      return 'renderMosaicFromCellColors';
    }
    
    // Apply adjustments when slider values change
    if (JSON.stringify(oldState.adjustments) !== JSON.stringify(newState.adjustments)) {
      return 'renderStoredMosaic';
    }
    
    return null;
  }

  shouldGenerateMosaic(oldState, newState) {
    // Don't generate if the image is not ready
    if (!newState.uploadedImage || !newState.uploadedImage.complete || newState.uploadedImage.naturalWidth === 0) {
      return false;
    }
    
    return (
      oldState.cropRect !== newState.cropRect ||
      oldState.gridWidth !== newState.gridWidth ||
      oldState.gridHeight !== newState.gridHeight ||
      oldState.uploadedImage !== newState.uploadedImage
      // Note: baseplate changes are handled separately to preserve adjustments and edits
    );
  }

  initializeMosaicColors() {
    // Comprehensive mosaic color palette from v1
    return [
      { name: 'Black', hex: '#000000', rgb: [0, 0, 0] },
      { name: 'Blue', hex: '#0057A6', rgb: [0, 87, 166] },
      { name: 'Bright Green', hex: '#4B9F4A', rgb: [75, 159, 74] },
      { name: 'Bright Light Blue', hex: '#88C4DC', rgb: [136, 196, 220] },
      { name: 'Bright Light Orange', hex: '#F8BB3D', rgb: [248, 187, 61] },
      { name: 'Bright Light Yellow', hex: '#F6D01D', rgb: [246, 208, 29] },
      { name: 'Brown', hex: '#583927', rgb: [88, 57, 39] },
      { name: 'Coral', hex: '#FF6E6E', rgb: [255, 110, 110] },
      { name: 'Dark Azure', hex: '#078BC9', rgb: [7, 139, 201] },
      { name: 'Dark Blue', hex: '#0A3463', rgb: [10, 52, 99] },
      { name: 'Dark Bluish Grey', hex: '#6D6E6C', rgb: [109, 110, 108] },
      { name: 'Dark Brown', hex: '#352100', rgb: [53, 33, 0] },
      { name: 'Dark Green', hex: '#184632', rgb: [24, 70, 50] },
      { name: 'Dark Orange', hex: '#A95500', rgb: [169, 85, 0] },
      { name: 'Dark Pink', hex: '#D45472', rgb: [212, 84, 114] },
      { name: 'Dark Purple', hex: '#6C3082', rgb: [108, 48, 130] },
      { name: 'Dark Red', hex: '#720E0F', rgb: [114, 14, 15] },
      { name: 'Dark Tan', hex: '#92804b', rgb: [146, 128, 75] },
      { name: 'Dark Turquoise', hex: '#008590', rgb: [0, 133, 144] },
      { name: 'Flat Silver', hex: '#898788', rgb: [137, 135, 136] },
      { name: 'Green', hex: '#00852B', rgb: [0, 133, 43] },
      { name: 'Lavender', hex: '#CDA4DE', rgb: [205, 164, 222] },
      { name: 'Light Aqua', hex: '#d0f1ec', rgb: [208, 241, 236] },
      { name: 'Light Bluish Grey', hex: '#c3d2e0', rgb: [195, 210, 224] },
      { name: 'Light Grey', hex: '#A19E9F', rgb: [161, 158, 159] },
      { name: 'Light Green', hex: '#C2DAB8', rgb: [194, 218, 184] },
      { name: 'Light Nougat', hex: '#F6D7B3', rgb: [246, 215, 179] },
      { name: 'Light Pink', hex: '#FECCCF', rgb: [254, 204, 207] },
      { name: 'Maersk Blue', hex: '#54A9C8', rgb: [84, 169, 200] },
      { name: 'Magenta', hex: '#C9006B', rgb: [201, 0, 107] },
      { name: 'Medium Dark Pink', hex: '#DF6695', rgb: [223, 102, 149] },
      { name: 'Medium Lavender', hex: '#8C71CB', rgb: [140, 113, 203] },
      { name: 'Medium Violet', hex: '#9391E4', rgb: [147, 145, 228] },
      { name: 'Nougat', hex: '#CE9E6F', rgb: [206, 158, 111] },
      { name: 'Olive Green', hex: '#677430', rgb: [103, 116, 48] },
      { name: 'Orange', hex: '#DC851E', rgb: [220, 133, 30] },
      { name: 'Pearl Dark Grey', hex: '#3E3C39', rgb: [62, 60, 57] },
      { name: 'Pearl Gold', hex: '#96781c', rgb: [150, 120, 28] },
      { name: 'Pink', hex: '#FF9ECE', rgb: [255, 158, 206] },
      { name: 'Purple', hex: '#81007B', rgb: [129, 0, 123] },
      { name: 'Red', hex: '#DF0101', rgb: [223, 1, 1] },
      { name: 'Reddish Brown', hex: '#7d3d28', rgb: [125, 61, 40] },
      { name: 'Sand Blue', hex: '#6A739C', rgb: [106, 115, 156] },
      { name: 'Sand Green', hex: '#789F90', rgb: [120, 159, 144] },
      { name: 'Sand Purple', hex: '#A4659A', rgb: [164, 101, 154] },
      { name: 'Sand Red', hex: '#7d4f4f', rgb: [125, 79, 79] },
      { name: 'Tan', hex: '#DFCFAE', rgb: [223, 207, 174] },
      { name: 'White', hex: '#FFFFFF', rgb: [255, 255, 255] },
      { name: 'Yellow', hex: '#F5CD30', rgb: [245, 205, 48] },
      { name: 'Yellowish Green', hex: '#C8F94C', rgb: [200, 249, 76] }
    ];
  }

  // TEST METHOD: Apply all colors to current mosaic for testing color legend
  testApplyAllColors() {
    console.log('🧪 TEST: Applying all colors to current mosaic...');
    
    if (!this.currentMosaic || this.currentMosaic.length === 0) {
      console.warn('🧪 TEST: No mosaic available. Please generate a mosaic first.');
      return;
    }

    const colors = this.mosaicColors;
    const mosaicHeight = this.currentMosaic.length;
    const mosaicWidth = this.currentMosaic[0]?.length || 0;
    
    console.log(`🧪 TEST: Applying ${colors.length} colors to ${mosaicWidth}x${mosaicHeight} mosaic`);
    
    // Create a new mosaic with all colors applied
    const testMosaic = [];
    let colorIndex = 0;
    
    for (let row = 0; row < mosaicHeight; row++) {
      testMosaic[row] = [];
      for (let col = 0; col < mosaicWidth; col++) {
        // Cycle through all colors
        const color = colors[colorIndex % colors.length];
        testMosaic[row][col] = {
          r: color.rgb[0],
          g: color.rgb[1],
          b: color.rgb[2]
        };
        colorIndex++;
      }
    }
    
    // Update the current mosaic
    this.currentMosaic = testMosaic;
    
    // Re-render the mosaic
    this.renderMosaicFromCellColors();
    
    console.log('🧪 TEST: All colors applied! Check the assembly instructions to see the full color legend.');
    
    // Also log the color count for verification
    const uniqueColors = new Set();
    testMosaic.forEach(row => {
      row.forEach(cell => {
        const colorKey = `${cell.r},${cell.g},${cell.b}`;
        uniqueColors.add(colorKey);
      });
    });
    console.log(`🧪 TEST: Mosaic now contains ${uniqueColors.size} unique colors`);
  }

  // This method will be called when generating the mosaic
  generateMosaic() {
    debugLog('INFO', '🔴 generateMosaic: Starting mosaic generation');
    debugLog('INFO', '🔴 generateMosaic: isUndoRedoOperation:', window.mosaicApp?.undoRedoManager?.isUndoRedoOperation);
    
    const state = this.stateManager.getState();
    
    // Required data from state manager:
    const {
      uploadedImage,    // The source image to sample from
      cropRect,         // { x, y, width, height } - area to crop from image
      imageBounds,      // { x, y, width, height } - full image dimensions
      gridWidth,        // Number of studs horizontally
      gridHeight,       // Number of studs vertically
      gridAspectRatio   // Aspect ratio for validation
    } = state;

    // Validate that we have all required data
    if (!uploadedImage || !cropRect || !imageBounds) {
      debugLog('WARN', 'MosaicRenderer: Missing required data for mosaic generation');
  
      return;
    }
    
    // Additional validation for image completeness
    if (!uploadedImage.complete || uploadedImage.naturalWidth === 0) {
      debugLog('WARN', 'MosaicRenderer: Image not ready for mosaic generation');
      return;
    }

    if (!this.mosaicCanvas || !this.mosaicCtx) {
      debugLog('WARN', 'MosaicRenderer: Canvas not initialized');
      return;
    }

    // Calculate frame dimensions
    const { width: totalGridWidth, height: totalGridHeight, frameWidth } = this.getTotalGridDimensions(gridWidth, gridHeight);

    // Calculate cell size to fit viewport while maintaining aspect ratio
    const cellSize = this.calculateCellSize(totalGridWidth, totalGridHeight);
    
    // Setup canvas dimensions and clear with background
    this.setupCanvasDimensions(totalGridWidth, totalGridHeight, cellSize);

    // Clear color cache for fresh calculations
    this.clearColorCache();

    // Sample colors from cropped region and render mosaic
    const originalMosaic = this.renderMosaicGrid(uploadedImage, cropRect, gridWidth, gridHeight, cellSize, frameWidth);
    
    // Save original mosaic to stateZero (this is the immutable base)
    if (window.mosaicApp?.undoRedoManager) {
      window.mosaicApp.undoRedoManager.stateZero = {
        mosaicState: originalMosaic,
        adjustments: { brightness: 0, contrast: 0, saturation: 0, hue: 0 },
        timestamp: Date.now()
      };
      debugLog('INFO', '🔴 generateMosaic: Saved original mosaic to stateZero');
    }
    
    // Create final currentMosaic by compositing layers
    this.compositeLayers();
    
    // Finalize rendering with stud overlays and notifications
    this.finalizeRendering(totalGridWidth, totalGridHeight, cellSize, frameWidth);
    
    // Also notify painting manager that mosaic is ready for color palette
    this.notifyPaintingManagerMosaicReady();
    
    // Clean up temporary canvas after mosaic generation
    this.cleanupTempCanvas();
    
    debugLog('INFO', '🔴 generateMosaic: Mosaic generation completed');

  }

  renderMosaicGrid(image, cropRect, gridWidth, gridHeight, cellSize, frameWidth = 0) {
    // Validate that we have a valid image
    if (!image || !image.complete || image.naturalWidth === 0) {
      debugLog('WARN', 'MosaicRenderer: Invalid or incomplete image, skipping mosaic generation');
      return;
    }
    // Validate cropRect and grid
    if (!cropRect || !Number.isFinite(cropRect.x) || !Number.isFinite(cropRect.y) || !Number.isFinite(cropRect.width) || !Number.isFinite(cropRect.height)) {
      debugLog('WARN', 'MosaicRenderer: Invalid cropRect values, skipping mosaic generation', cropRect);
      return;
    }
    if (cropRect.width <= 0 || cropRect.height <= 0) {
      debugLog('WARN', 'MosaicRenderer: Non-positive cropRect, skipping mosaic generation', cropRect);
      return;
    }
    if (!Number.isFinite(gridWidth) || !Number.isFinite(gridHeight) || gridWidth <= 0 || gridHeight <= 0) {
      debugLog('WARN', 'MosaicRenderer: Invalid grid size, skipping mosaic generation', { gridWidth, gridHeight });
      return;
    }
    
    // Clean up previous temporary canvas
    this.cleanupTempCanvas();
    
    // Create a temporary canvas to sample from the cropped region
    this.tempCanvas = document.createElement('canvas');
    this.tempCtx = this.tempCanvas.getContext('2d', { willReadFrequently: true });
    const tempCanvas = this.tempCanvas;
    const tempCtx = this.tempCtx;
    
    // Set temp canvas to crop dimensions (integers)
    tempCanvas.width = Math.max(1, Math.floor(cropRect.width));
    tempCanvas.height = Math.max(1, Math.floor(cropRect.height));
    
    // Draw the cropped region to temp canvas
    tempCtx.drawImage(
      image,
      Math.floor(cropRect.x), Math.floor(cropRect.y), Math.floor(cropRect.width), Math.floor(cropRect.height),
      0, 0, tempCanvas.width, tempCanvas.height
    );

    // Note: Adjustments are no longer applied here during mosaic generation
    // They are only applied when explicitly requested via renderMosaicFromCellColors()

    // Initialize temporary mosaic for the original (will be saved to stateZero)
    const originalMosaic = [];
    for (let y = 0; y < gridHeight; y++) {
      originalMosaic[y] = [];
      for (let x = 0; x < gridWidth; x++) {
        originalMosaic[y][x] = null;
      }
    }

    // Calculate total grid dimensions including frame
    const { width: totalGridWidth, height: totalGridHeight } = this.getTotalGridDimensions(gridWidth, gridHeight);

    // Sample colors and render mosaic
    for (let y = 0; y < totalGridHeight; y++) {
      for (let x = 0; x < totalGridWidth; x++) {
        // Check if this position is part of the frame
        const isFrameStud = this.isFrameStud(x, y, frameWidth, totalGridWidth, totalGridHeight);

        if (isFrameStud) {
          // Draw black frame stud
          this.drawFrameStud(x, y, cellSize);
        } else {
          // Calculate mosaic position (accounting for frame offset)
          const mosaicX = x - frameWidth;
          const mosaicY = y - frameWidth;
          
          // Sample multiple pixels from the cell area and average them (like v1)
          const cellStartX = Math.floor((mosaicX / gridWidth) * tempCanvas.width);
          const cellStartY = Math.floor((mosaicY / gridHeight) * tempCanvas.height);
          const cellEndX = Math.min(Math.floor(((mosaicX + 1) / gridWidth) * tempCanvas.width), tempCanvas.width);
          const cellEndY = Math.min(Math.floor(((mosaicY + 1) / gridHeight) * tempCanvas.height), tempCanvas.height);
          
          // Get the entire cell area in one call for better performance
          let cellWidth = cellEndX - cellStartX;
          let cellHeight = cellEndY - cellStartY;
          if (cellWidth <= 0 || cellHeight <= 0) {
            // Nothing to sample for this cell; skip drawing
            continue;
          }
          // Clamp within bounds and ensure integers
          cellWidth = Math.min(tempCanvas.width - cellStartX, cellWidth)|0;
          cellHeight = Math.min(tempCanvas.height - cellStartY, cellHeight)|0;
          const cellImageData = tempCtx.getImageData(cellStartX|0, cellStartY|0, cellWidth, cellHeight);
          const cellData = cellImageData.data;
          
          // Average all pixels in the cell area
          let r = 0, g = 0, b = 0, count = 0;
          for (let i = 0; i < cellData.length; i += 4) {
            r += cellData[i];     // Red
            g += cellData[i + 1]; // Green
            b += cellData[i + 2]; // Blue
            // Skip alpha channel (i + 3)
            count++;
          }
          
          // Calculate average color
          r = Math.round(r / count);
          g = Math.round(g / count);
          b = Math.round(b / count);
          
          // Map to closest mosaic color using simple Euclidean distance like v1 (more realistic colors)
          const mosaicColor = this.findClosestMosaicColorV1(r, g, b);
          
          // Store the original cell color for grid line calculations (with frame)
          const cellEntry = {
            r: mosaicColor.rgb[0],
            g: mosaicColor.rgb[1], 
            b: mosaicColor.rgb[2]
          };

          // Store mosaic color in originalMosaic (will be saved to stateZero)
          originalMosaic[mosaicY][mosaicX] = { ...cellEntry };
          
          // Draw mosaic cell with consistent spacing
          const spacing = this.getGridSpace(cellSize);
          const hexColor = this.rgbToHex(mosaicColor.rgb[0], mosaicColor.rgb[1], mosaicColor.rgb[2]);
          this.mosaicCtx.fillStyle = hexColor;
          const cellX = x * cellSize;
          const cellY = y * cellSize;
          const cellW = cellSize - spacing;
          const cellH = cellSize - spacing;
          this.mosaicCtx.fillRect(cellX + spacing / 2, cellY + spacing / 2, cellW, cellH);
          
        }
      }
    }
    
    // Return the original mosaic for saving to stateZero
    return originalMosaic;
  }



  drawStudOverlays(gridWidth, gridHeight, cellSize, frameWidth = 0) {
    // Configuration constants - improved to match v1
    const STUD_GRADIENT_OPACITY = 0.35; // More subtle like v1
    const STUD_GRADIENT_CUTOFF = 0.2; // Different gradient pattern like v1
    
    const studRadius = this.getStudRadius(cellSize);
    const studCenterOffset = this.getStudCenterOffset(cellSize);
    
    for (let y = 0; y < gridHeight; y++) {
      for (let x = 0; x < gridWidth; x++) {
        const centerX = x * cellSize + studCenterOffset;
        const centerY = y * cellSize + studCenterOffset;
        
        // Create 3D stud effect similar to v1
        const gradient = this.mosaicCtx.createRadialGradient(
          centerX, centerY, studRadius * 0.2,
          centerX, centerY, studRadius
        );
        
        // Use white highlight and black shadow like v1
        gradient.addColorStop(0, 'rgba(255,255,255,0.35)');
        gradient.addColorStop(0.5, 'rgba(255,255,255,0.10)');
        gradient.addColorStop(1, 'rgba(0,0,0,0.18)');
        
        this.mosaicCtx.fillStyle = gradient;
        this.mosaicCtx.beginPath();
        this.mosaicCtx.arc(centerX, centerY, studRadius, 0, 2 * Math.PI);
        this.mosaicCtx.fill();
      }
    }
  }

  // Draw a single stud overlay at specific coordinates (for tool painting)
  drawSingleStudOverlay(ctx, cellX, cellY, cellSize) {
    // Configuration constants - matching the main stud overlay method
    const STUD_GRADIENT_OPACITY = 0.35;
    
    const studRadius = this.getStudRadius(cellSize);
    const studCenterOffset = this.getStudCenterOffset(cellSize);
    const centerX = cellX + studCenterOffset;
    const centerY = cellY + studCenterOffset;
    
    // Create 3D stud effect matching the mosaic renderer
    const gradient = ctx.createRadialGradient(
      centerX, centerY, studRadius * 0.2,
      centerX, centerY, studRadius
    );
    
    // Use white highlight and black shadow like the mosaic renderer
    gradient.addColorStop(0, 'rgba(255,255,255,0.35)');
    gradient.addColorStop(0.5, 'rgba(255,255,255,0.10)');
    gradient.addColorStop(1, 'rgba(0,0,0,0.18)');
    
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(centerX, centerY, studRadius, 0, 2 * Math.PI);
    ctx.fill();
  }



  // Clear color cache (useful when regenerating mosaic)
  clearColorCache() {
    this.colorCache.clear();
  }

  // V1-style color matching using simple Euclidean distance (more realistic colors)
  findClosestMosaicColorV1(r, g, b) {
    // Use cache for performance like v1
    const key = `${r},${g},${b}`;
    if (this.colorCache.has(key)) {
      return this.colorCache.get(key);
    }

    // Use simple Euclidean distance in RGB space like v1 (more realistic color matching)
    let minDist = Infinity;
    let best = this.mosaicColors[0];
    
    for (const color of this.mosaicColors) {
      const dr = r - color.rgb[0];
      const dg = g - color.rgb[1];
      const db = b - color.rgb[2];
      const dist = dr * dr + dg * dg + db * db;
      
      if (dist < minDist) {
        minDist = dist;
        best = color;
      }
    }
    
    // Cache the result
    this.colorCache.set(key, best);
    return best;
  }





  // Manual trigger for testing - call from console
  forceRender() {

    const state = this.stateManager.getState();

    this.generateMosaic();
  }

  // Get current mosaic data for debugging
  getMosaicData() {
    if (!this.mosaicCanvas) return null;
    
    return {
      canvasWidth: this.mosaicCanvas.width,
      canvasHeight: this.mosaicCanvas.height,
      gridSize: `${this.stateManager.getState().gridWidth} × ${this.stateManager.getState().gridHeight}`,
      cellSize: 20
    };
  }

  // Notify painting manager of grid dimensions update
  notifyPaintingManagerGridUpdate() {
    // This will be called by the main app to update painting manager
    if (window.mosaicApp && window.mosaicApp.paintingManager) {
      window.mosaicApp.paintingManager.updateGridDimensions();
    }
  }

  // Notify painting manager that mosaic is ready
  notifyPaintingManagerMosaicReady() {
    // This will be called by the main app to update painting manager
    if (window.mosaicApp && window.mosaicApp.paintingManager) {
      window.mosaicApp.paintingManager.refreshColorPalette();
    }
  }

  // Check if adjustments are applied (not default values)
  hasAdjustments(adjustments) {
    return adjustments.brightness !== 0 || adjustments.contrast !== 0 || 
           adjustments.saturation !== 0 || adjustments.hue !== 0;
  }


  // Normalize adjustment values to processing ranges
  normalizeAdjustments(adjustments) {
    return {
      brightness: adjustments.brightness / 100, // -1 to 1
      contrast: adjustments.contrast / 100,     // -1 to 1
      saturation: adjustments.saturation / 100, // -1 to 1
      hue: (adjustments.hue / 100) * 180         // -180 to 180 degrees
    };
  }

  // Apply brightness adjustment
  applyBrightness(r, g, b, brightness) {
    if (brightness === 0) return [r, g, b];
    const delta = brightness * 255;
    return [
      this.clamp(r + delta),
      this.clamp(g + delta),
      this.clamp(b + delta)
    ];
  }

  // Apply contrast adjustment
  applyContrast(r, g, b, contrast) {
    if (contrast === 0) return [r, g, b];
    const factor = (259 * (contrast * 255 + 255)) / (255 * (259 - contrast * 255));
    return [
      this.clamp(factor * (r - 128) + 128),
      this.clamp(factor * (g - 128) + 128),
      this.clamp(factor * (b - 128) + 128)
    ];
  }

  // Apply saturation adjustment
  applySaturation(r, g, b, saturation) {
    if (saturation === 0) return [r, g, b];
    const factor = 1 + saturation;
    const gray = 0.299 * r + 0.587 * g + 0.114 * b;
    return [
      this.clamp(gray + (r - gray) * factor),
      this.clamp(gray + (g - gray) * factor),
      this.clamp(gray + (b - gray) * factor)
    ];
  }

  // Apply hue adjustment
  applyHue(r, g, b, hue) {
    if (hue === 0) return [r, g, b];
    const hsl = this.rgbToHsl(r, g, b);
    hsl.h = (hsl.h + hue + 360) % 360;
    const rgb = this.hslToRgb(hsl.h, hsl.s, hsl.l);
    return [this.clamp(rgb.r), this.clamp(rgb.g), this.clamp(rgb.b)];
  }

  // Clamp value to 0-255 range
  clamp(value) {
    return Math.max(0, Math.min(255, value));
  }

  // RGB to HSL conversion
  rgbToHsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    
    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }
    
    return { h: h * 360, s: s * 100, l: l * 100 };
  }

  // HSL to RGB conversion
  hslToRgb(h, s, l) {
    // Ensure valid input values
    h = Math.max(0, Math.min(360, h));
    s = Math.max(0, Math.min(100, s));
    l = Math.max(0, Math.min(100, l));
    
    h /= 360;
    s /= 100;
    l /= 100;
    
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    
    let r, g, b;
    
    if (s === 0) {
      r = g = b = l;
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;
      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }
    
    // Ensure valid output values
    r = Math.max(0, Math.min(1, r));
    g = Math.max(0, Math.min(1, g));
    b = Math.max(0, Math.min(1, b));
    
    return {
      r: Math.round(r * 255),
      g: Math.round(g * 255),
      b: Math.round(b * 255)
    };
  }

  // ============================================================================
  // HELPER METHODS - Common rendering logic extracted from generateMosaic() and renderMosaicFromCellColors()
  // ============================================================================

  // Calculate cell size to fit viewport while maintaining aspect ratio
  calculateCellSize(totalGridWidth, totalGridHeight) {
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Calculate cell size based on viewport height (target 100vh)
    const cellSizeByHeight = viewportHeight / totalGridHeight;
    
    // Calculate what the width would be with this cell size
    const resultingWidth = totalGridWidth * cellSizeByHeight;
    
    // If resulting width exceeds viewport, use width constraint instead
    if (resultingWidth > viewportWidth) {
      // Width constraint: use viewport width as max
      return viewportWidth / totalGridWidth;
    } else {
      // Height constraint: use 100vh as target
      return cellSizeByHeight;
    }
  }

  // Setup canvas dimensions and clear with background
  setupCanvasDimensions(totalGridWidth, totalGridHeight, cellSize) {
    // Set canvas dimensions with integer values to prevent gaps
    this.mosaicCanvas.width = Math.floor(totalGridWidth * cellSize);
    this.mosaicCanvas.height = Math.floor(totalGridHeight * cellSize);
    
    // Clear canvas and fill with solid background to prevent gaps showing through
    this.mosaicCtx.clearRect(0, 0, this.mosaicCanvas.width, this.mosaicCanvas.height);
    this.mosaicCtx.fillStyle = this.getGridLineColor(); // Fill with grid line color
    this.mosaicCtx.fillRect(0, 0, this.mosaicCanvas.width, this.mosaicCanvas.height);
  }

  // Check if a position is a frame stud
  isFrameStud(x, y, frameWidth, totalGridWidth, totalGridHeight) {
    return frameWidth && (
      y < frameWidth || 
      y >= totalGridHeight - frameWidth || 
      x < frameWidth || 
      x >= totalGridWidth - frameWidth
    );
  }

  // Draw a single frame stud
  drawFrameStud(x, y, cellSize) {
    const spacing = this.getGridSpace(cellSize);
    const cellX = x * cellSize;
    const cellY = y * cellSize;
    const cellW = cellSize - spacing;
    const cellH = cellSize - spacing;
    
    this.mosaicCtx.fillStyle = '#000000';
    this.mosaicCtx.fillRect(cellX + spacing / 2, cellY + spacing / 2, cellW, cellH);
  }

  // Finalize rendering with stud overlays and notifications
  finalizeRendering(totalGridWidth, totalGridHeight, cellSize, frameWidth) {
    // Draw stud overlays to make cells look like LEGO bricks
    this.drawStudOverlays(totalGridWidth, totalGridHeight, cellSize, frameWidth);
    
    // Notify painting manager of grid dimensions update
    this.notifyPaintingManagerGridUpdate();
  }

  // Common validation logic for rendering methods
  validateRenderingState() {
    // Validate canvas and context
    if (!this.mosaicCanvas || !this.mosaicCtx) {
      debugLog('WARN', 'MosaicRenderer: Canvas not initialized');
      return false;
    }

    // Validate currentMosaic array
    if (!this.currentMosaic || !Array.isArray(this.currentMosaic) || this.currentMosaic.length === 0) {
      debugLog('WARN', 'MosaicRenderer: Cannot render from current mosaic - missing or invalid currentMosaic array');
      return false;
    }

    const state = this.stateManager.getState();
    const { gridWidth, gridHeight } = state;

    // Validate grid dimensions
    if (!gridWidth || !gridHeight || gridWidth <= 0 || gridHeight <= 0) {
      debugLog('WARN', 'MosaicRenderer: Invalid grid dimensions:', { gridWidth, gridHeight });
      return false;
    }

    return true;
  }

  // Render the stored mosaic (single source of truth - exactly what is displayed)
  renderStoredMosaic() {
    // Common validation
    if (!this.validateRenderingState()) return;

    const state = this.stateManager.getState();
    const { gridWidth, gridHeight, adjustments } = state;
    const { width: totalGridWidth, height: totalGridHeight, frameWidth } = this.getTotalGridDimensions(gridWidth, gridHeight);

    // Calculate cell size to fit viewport while maintaining aspect ratio
    const cellSize = this.calculateCellSize(totalGridWidth, totalGridHeight);
    
    // Setup canvas dimensions and clear with background
    this.setupCanvasDimensions(totalGridWidth, totalGridHeight, cellSize);

    // Render mosaic from currentMosaic (single source of truth - exactly what is displayed)
    let paintedCellsFound = 0;
    
    // Render mosaic cells (from currentMosaic - no frame cells)
    for (let y = 0; y < gridHeight; y++) {
      for (let x = 0; x < gridWidth; x++) {
        const cellColor = this.currentMosaic[y]?.[x];
        if (!cellColor) continue;

        // Use the stored color directly (adjustments are already baked in)
        const displayedColor = { r: cellColor.r, g: cellColor.g, b: cellColor.b };
        
        // Draw mosaic cell from stored color (accounting for frame offset)
        const spacing = this.getGridSpace(cellSize);
        const cellX = (x + frameWidth) * cellSize;
        const cellY = (y + frameWidth) * cellSize;
        const cellW = cellSize - spacing;
        const cellH = cellSize - spacing;
        
        // Convert RGB to hex for fillStyle
        const hexColor = this.rgbToHex(displayedColor.r, displayedColor.g, displayedColor.b);
        this.mosaicCtx.fillStyle = hexColor;
        this.mosaicCtx.fillRect(cellX + spacing / 2, cellY + spacing / 2, cellW, cellH);
        
        // Count painted cells (non-white, non-frame)
        if (hexColor !== '#FFFFFF' && hexColor !== '#000000') {
          paintedCellsFound++;
        }
      }
    }
    
    // Render frame cells separately (if frame is enabled)
    if (frameWidth) {
      for (let y = 0; y < totalGridHeight; y++) {
        for (let x = 0; x < totalGridWidth; x++) {
          const isFrameStud = this.isFrameStud(x, y, frameWidth, totalGridWidth, totalGridHeight);
          if (isFrameStud) {
            this.drawFrameStud(x, y, cellSize);
          }
        }
      }
    }
    
    // Finalize rendering with stud overlays and notifications
    this.finalizeRendering(totalGridWidth, totalGridHeight, cellSize, frameWidth);
  }

  // Render mosaic from existing cell colors (for undo/redo operations)
  renderMosaicFromCellColors() {
    // Common validation
    if (!this.validateRenderingState()) return;

    const state = this.stateManager.getState();
    const { gridWidth, gridHeight } = state;
    const { width: totalGridWidth, height: totalGridHeight, frameWidth } = this.getTotalGridDimensions(gridWidth, gridHeight);



    // Calculate cell size to fit viewport while maintaining aspect ratio
    const cellSize = this.calculateCellSize(totalGridWidth, totalGridHeight);
    
    // Setup canvas dimensions and clear with background
    this.setupCanvasDimensions(totalGridWidth, totalGridHeight, cellSize);

    // Note: currentMosaic contains the displayed colors (exactly what is shown)

    // Render mosaic from currentMosaic (single source of truth)
    let paintedCellsFound = 0;
    
    // Render mosaic cells (from currentMosaic - no frame cells)
    for (let y = 0; y < gridHeight; y++) {
      for (let x = 0; x < gridWidth; x++) {
        const cellColor = this.currentMosaic[y]?.[x];
        if (!cellColor) continue;

        // Use the stored color directly (it's exactly what is displayed)
        const displayedColor = { r: cellColor.r, g: cellColor.g, b: cellColor.b };
        
        // Draw mosaic cell from stored color (accounting for frame offset)
        const spacing = this.getGridSpace(cellSize);
        const cellX = (x + frameWidth) * cellSize;
        const cellY = (y + frameWidth) * cellSize;
        const cellW = cellSize - spacing;
        const cellH = cellSize - spacing;
        
        // Convert RGB to hex for fillStyle
        const hexColor = this.rgbToHex(displayedColor.r, displayedColor.g, displayedColor.b);
        this.mosaicCtx.fillStyle = hexColor;
        this.mosaicCtx.fillRect(cellX + spacing / 2, cellY + spacing / 2, cellW, cellH);
        
        // Count painted cells (non-white, non-frame)
        if (hexColor !== '#FFFFFF' && hexColor !== '#000000') {
          paintedCellsFound++;
        }
      }
    }
    
    // Render frame cells separately (if frame is enabled)
    if (frameWidth) {
      for (let y = 0; y < totalGridHeight; y++) {
        for (let x = 0; x < totalGridWidth; x++) {
          const isFrameStud = this.isFrameStud(x, y, frameWidth, totalGridWidth, totalGridHeight);
          if (isFrameStud) {
            this.drawFrameStud(x, y, cellSize);
          }
        }
      }
    }
    
    // Finalize rendering with stud overlays and notifications
    this.finalizeRendering(totalGridWidth, totalGridHeight, cellSize, frameWidth);
  }



  // Convert RGB values to hex color
  rgbToHex(r, g, b) {
    const toHex = (c) => {
      const hex = Math.round(c).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  // Copy currentMosaic to currentlyAdjustingMosaic for live preview
  initializeAdjustingMosaic() {
    if (this.currentMosaic && this.currentMosaic.length > 0) {
      // Deep copy currentMosaic to currentlyAdjustingMosaic
      this.currentlyAdjustingMosaic = this.currentMosaic.map(row => 
        row.map(cell => cell ? { ...cell } : null)
      );
      debugLog('INFO', '🔵 MosaicRenderer: Initialized adjusting mosaic with', this.currentlyAdjustingMosaic.length, 'rows');
    } else {
      debugLog('WARN', '🔵 MosaicRenderer: Cannot initialize adjusting mosaic - currentMosaic is empty');
    }
  }

  // Render the adjusting mosaic with live preview adjustments
  renderAdjustingMosaic() {
    // Common validation
    if (!this.validateRenderingState()) return;

    const state = this.stateManager.getState();
    const { gridWidth, gridHeight, adjustments } = state;
    const { width: totalGridWidth, height: totalGridHeight, frameWidth } = this.getTotalGridDimensions(gridWidth, gridHeight);

    // Calculate cell size to fit viewport while maintaining aspect ratio
    const cellSize = this.calculateCellSize(totalGridWidth, totalGridHeight);
    
    // Setup canvas dimensions and clear with background
    this.setupCanvasDimensions(totalGridWidth, totalGridHeight, cellSize);

    // Apply adjustments to cell colors for live preview
    const normalizedAdjustments = this.hasAdjustments(adjustments) ? this.normalizeAdjustments(adjustments) : null;

    // Render mosaic from currentlyAdjustingMosaic with live adjustments
    let paintedCellsFound = 0;
    
    // Render mosaic cells (from currentlyAdjustingMosaic - no frame cells)
    for (let y = 0; y < gridHeight; y++) {
      for (let x = 0; x < gridWidth; x++) {
        const cellColor = this.currentlyAdjustingMosaic[y]?.[x];
        if (!cellColor) continue;

        // Apply live adjustments to cell color for preview
        let displayedColor = { r: cellColor.r, g: cellColor.g, b: cellColor.b };
        if (normalizedAdjustments) {
          let [r, g, b] = [cellColor.r, cellColor.g, cellColor.b];
          [r, g, b] = this.applyBrightness(r, g, b, normalizedAdjustments.brightness);
          [r, g, b] = this.applyContrast(r, g, b, normalizedAdjustments.contrast);
          [r, g, b] = this.applySaturation(r, g, b, normalizedAdjustments.saturation);
          [r, g, b] = this.applyHue(r, g, b, normalizedAdjustments.hue);
          
          // CRITICAL: Map adjusted color back to closest palette color for live preview
          const closestPaletteColor = this.findClosestMosaicColorV1(Math.round(r), Math.round(g), Math.round(b));
          displayedColor = { 
            r: closestPaletteColor.rgb[0], 
            g: closestPaletteColor.rgb[1], 
            b: closestPaletteColor.rgb[2] 
          };
        }
        
        // Draw mosaic cell from stored color (accounting for frame offset)
        const spacing = this.getGridSpace(cellSize);
        const cellX = (x + frameWidth) * cellSize;
        const cellY = (y + frameWidth) * cellSize;
        const cellW = cellSize - spacing;
        const cellH = cellSize - spacing;
        
        // Convert RGB to hex for fillStyle
        const hexColor = this.rgbToHex(displayedColor.r, displayedColor.g, displayedColor.b);
        this.mosaicCtx.fillStyle = hexColor;
        this.mosaicCtx.fillRect(cellX + spacing / 2, cellY + spacing / 2, cellW, cellH);
        
        // Count painted cells (non-white, non-frame)
        if (hexColor !== '#FFFFFF' && hexColor !== '#000000') {
          paintedCellsFound++;
        }
      }
    }
    
    // Render frame cells separately (if frame is enabled)
    if (frameWidth) {
      for (let y = 0; y < totalGridHeight; y++) {
        for (let x = 0; x < totalGridWidth; x++) {
          const isFrameStud = this.isFrameStud(x, y, frameWidth, totalGridWidth, totalGridHeight);
          if (isFrameStud) {
            this.drawFrameStud(x, y, cellSize);
          }
        }
      }
    }
    
    // Finalize rendering with stud overlays and notifications
    this.finalizeRendering(totalGridWidth, totalGridHeight, cellSize, frameWidth);
    
    debugLog('INFO', '🔵 MosaicRenderer: Rendered adjusting mosaic with live adjustments');
  }


  // NEW METHODS FOR NON-CUMULATIVE ADJUSTMENTS SYSTEM
  
  /**
   * Composite layers to create the final currentMosaic
   * This is the core method for the new non-cumulative system
   */
  compositeLayers() {
    // Get original mosaic from stateZero
    const originalMosaic = window.mosaicApp?.undoRedoManager?.stateZero?.mosaicState;
    if (!originalMosaic) {
      debugLog('WARN', 'MosaicRenderer.compositeLayers: No original mosaic available');
      return;
    }

    // Get current adjustments from state manager
    const adjustments = this.stateManager.getState().adjustments;
    
    // Apply adjustments to original mosaic
    const adjustedMosaic = this.applyAdjustmentsToOriginal(originalMosaic, adjustments);
    
    // Debug: Check toolEdits state
    debugLog('INFO', '🔵 compositeLayers: toolEdits length:', this.toolEdits.length);
    debugLog('INFO', '🔵 compositeLayers: toolEdits content:', this.toolEdits);
    
    // Overlay tool edits on adjusted mosaic
    this.currentMosaic = this.overlayToolEdits(adjustedMosaic, this.toolEdits);
    
    // Render the updated mosaic visually
    this.renderMosaicFromCellColors();
    
    // Layers composited successfully
  }

  /**
   * Apply adjustments to the original mosaic (non-cumulative)
   * @param {Array} originalMosaic - The original mosaic array
   * @param {Object} adjustments - The current adjustment values
   * @returns {Array} The adjusted mosaic
   */
  applyAdjustmentsToOriginal(originalMosaic, adjustments) {
    if (!this.hasAdjustments(adjustments)) {
      // No adjustments, return original mosaic
      return originalMosaic.map(row => [...row]); // Deep clone
    }

    const state = this.stateManager.getState();
    const { gridWidth, gridHeight } = state;
    const normalizedAdjustments = this.normalizeAdjustments(adjustments);
    
    // Create adjusted mosaic by applying adjustments to original
    const adjustedMosaic = [];
    for (let y = 0; y < gridHeight; y++) {
      adjustedMosaic[y] = [];
      for (let x = 0; x < gridWidth; x++) {
        const originalColor = originalMosaic[y][x];
        if (originalColor && originalColor.r !== undefined) {
          // Apply adjustments to original color
          let r = originalColor.r;
          let g = originalColor.g;
          let b = originalColor.b;
          
          [r, g, b] = this.applyBrightness(r, g, b, normalizedAdjustments.brightness);
          [r, g, b] = this.applyContrast(r, g, b, normalizedAdjustments.contrast);
          [r, g, b] = this.applySaturation(r, g, b, normalizedAdjustments.saturation);
          [r, g, b] = this.applyHue(r, g, b, normalizedAdjustments.hue);
          
          // Clamp values
          r = this.clamp(r);
          g = this.clamp(g);
          b = this.clamp(b);
          
          // Find closest mosaic color from palette (same as current system)
          const closestColor = this.findClosestMosaicColorV1(r, g, b);
          
          // Extract RGB values and store in the same format as originalMosaic
          adjustedMosaic[y][x] = {
            r: closestColor.rgb[0],
            g: closestColor.rgb[1],
            b: closestColor.rgb[2]
          };
        } else {
          // No original color, keep as is
          adjustedMosaic[y][x] = originalColor;
        }
      }
    }
    
    return adjustedMosaic;
  }

  /**
   * Overlay tool edits on the adjusted mosaic
   * @param {Array} adjustedMosaic - The mosaic with adjustments applied
   * @param {Array} toolEdits - The tool edits layer
   * @returns {Array} The final composited mosaic
   */
  overlayToolEdits(adjustedMosaic, toolEdits) {
    if (!toolEdits || toolEdits.length === 0) {
      // No tool edits, return adjusted mosaic
      return adjustedMosaic.map(row => [...row]); // Deep clone
    }

    const state = this.stateManager.getState();
    const { gridWidth, gridHeight } = state;
    
    // Start with adjusted mosaic
    const finalMosaic = adjustedMosaic.map(row => [...row]); // Deep clone
    
    // Apply tool edits on top
    for (let y = 0; y < gridHeight; y++) {
      for (let x = 0; x < gridWidth; x++) {
        const toolEdit = toolEdits[y]?.[x];
        if (toolEdit) {
          // Tool edit exists at this position, use it
          finalMosaic[y][x] = toolEdit;
        }
      }
    }
    
    return finalMosaic;
  }

  // Clean up temporary canvas and context
  cleanupTempCanvas() {
    if (this.tempCtx) {
      // Clear the context to free associated resources
      this.tempCtx.clearRect(0, 0, this.tempCanvas?.width || 0, this.tempCanvas?.height || 0);
      this.tempCtx = null;
    }
    if (this.tempCanvas) {
      // Reset canvas dimensions to free memory
      this.tempCanvas.width = 1;
      this.tempCanvas.height = 1;
      this.tempCanvas = null;
    }
  }

  // Cleanup method for canvas contexts and ImageData
  destroy() {
    // Clean up temporary canvas
    this.cleanupTempCanvas();
    
    // Clean up main canvas references and release native resources
    this.mosaicCtx = null;
    if (this.mosaicCanvas) {
      this.mosaicCanvas.width = 1;
      this.mosaicCanvas.height = 1;
    }
    this.mosaicCanvas = null;
    
    // Clear caches and arrays
    this.colorCache.clear();
    this.mosaicData = null;
    this.currentMosaic = [];
    this.currentlyAdjustingMosaic = [];
    this.toolEdits = [];
  }
}

class UndoRedoManager {
  constructor(stateManager) {
    this.stateManager = stateManager;
    this.undoStack = [];
    this.redoStack = [];
    this.maxUndoSteps = 30;
    this.stateZero = null; // The initial state after image/crop/grio changes
    this.isUndoRedoOperation = false; // Flag to prevent generateMosaic during undo/redo
    this.warningShownThisPage = false; // Track if warning has been shown on this page load
    this.operationTimer = null;
    this.messageTimer = null;
    this.warningTimer = null;
    
    this.setupElements();
    this.setupEventListeners();
    this.updateButtonStates();
  }

  setupElements() {
    // Use classes to find all instances of utility buttons
    this.elements = {
      undo: Array.from(document.querySelectorAll('.undo-btn')),
      redo: Array.from(document.querySelectorAll('.redo-btn')),
      reset: Array.from(document.querySelectorAll('.reset-btn'))
    };
  }

  setupEventListeners() {
    if (this.elements.undo && this.elements.undo.length) {
      this.elements.undo.forEach((btn) => btn.addEventListener('click', (e) => {
        if (this.undoStack.length === 0) {
          this.showMessage('Nothing to undo');
          return;
        }
        this.undo();
      }));
    }
    
    if (this.elements.redo && this.elements.redo.length) {
      this.elements.redo.forEach((btn) => btn.addEventListener('click', (e) => {
        if (this.redoStack.length === 0) {
          this.showMessage('Nothing to redo');
          return;
        }
        this.redo();
      }));
    }
    
    if (this.elements.reset && this.elements.reset.length) {
      this.elements.reset.forEach((btn) => btn.addEventListener('click', (e) => {
        const hasHistory = this.undoStack.length > 0 || this.redoStack.length > 0;
        
        if (!hasHistory) {
          this.showMessage('Nothing to reset');
          return;
        }
        
        // If there's history, let the main app handle the reset with confirmation
        if (window.mosaicApp && window.mosaicApp.reset) {
          window.mosaicApp.reset();
        }
      }));
    }
  }

  // Save the current tool edits and adjustments as a step
  saveState(adjustments = null) {
    // Get current tool edits (deep clone)
    const currentToolEdits = window.mosaicApp?.mosaicRenderer?.toolEdits;
    if (!currentToolEdits) return;
    
    // Use provided adjustments or get from state manager
    const currentAdjustments = adjustments || this.stateManager.getState().adjustments;
    
    // Deep clone tool edits
    const clonedToolEdits = currentToolEdits.map(row => 
      row ? row.map(cell => cell ? { ...cell } : null) : null
    );
    
    debugLog('INFO', '🔵 saveState: Saving toolEdits and adjustments');
    
    const step = {
      toolEdits: clonedToolEdits,
      adjustments: { ...currentAdjustments }
    };
    
    this.undoStack.push(step);
    
    if (this.undoStack.length > this.maxUndoSteps) {
      this.undoStack.shift();
    }
    
    this.redoStack = []; // Clear redo stack when new changes are made
    this.updateButtonStates();
    
    // Notify that mosaic has been saved (currentMosaic updated)
    this.stateManager.notifyListeners('mosaicRendered', {});
    
    // Invalidate image capture cache when mosaic changes (only when saved)
    if (window.mosaicApp && window.mosaicApp.invalidateCache) {
      window.mosaicApp.invalidateCache();
    }
  }

  // Set the mosaic state from stored tool edits and adjustments
  setMosaicState(toolEdits, adjustments = null) {
    debugLog('INFO', '🔴 setMosaicState: Restoring tool edits and adjustments');
    
    if (!window.mosaicApp?.mosaicRenderer) {
      debugLog('WARN', '🔴 setMosaicState: Missing mosaicRenderer');
      return;
    }
    
    // Restore tool edits (deep clone)
    if (toolEdits) {
      window.mosaicApp.mosaicRenderer.toolEdits = toolEdits.map(row => 
        row ? row.map(cell => cell ? { ...cell } : null) : null
      );
    } else {
      // Clear tool edits if none provided
      window.mosaicApp.mosaicRenderer.toolEdits = [];
    }
    
    // Update adjustments first (this will trigger UI update)
    if (adjustments && window.mosaicApp?.adjustmentManager) {
      window.mosaicApp.adjustmentManager.adjustments = { ...adjustments };
      window.mosaicApp.adjustmentManager.updateUI();
      // Update state manager without triggering re-render since we'll render manually
      window.mosaicApp.stateManager.updateState({ adjustments: { ...adjustments } }, true);
    }
    
    // Recreate currentMosaic by compositing layers
    window.mosaicApp.mosaicRenderer.compositeLayers();
  }



  // Reset to state zero
  resetToStateZero() {
    if (!this.stateZero) return;
    
    // Clear tool edits (reset to original mosaic only)
    if (window.mosaicApp?.mosaicRenderer?.toolEdits) {
      window.mosaicApp.mosaicRenderer.toolEdits = [];
    }
    
    // Reset to state zero: clear tool edits and restore original adjustments
    this.setMosaicState([], this.stateZero.adjustments);
    
    // Clear history
    this.clear();
  }

  updateButtonStates() {
    // Use CSS classes instead of inline styles
    const applyState = (btns, enabled) => {
      if (!btns || !btns.length) return;
      btns.forEach((btn) => {
        btn.disabled = false;
        btn.classList.remove('disabled', 'enabled');
        btn.classList.add(enabled ? 'enabled' : 'disabled');
      });
    };
    applyState(this.elements.undo, this.undoStack.length !== 0);
    applyState(this.elements.redo, this.redoStack.length !== 0);
    const hasHistory = this.undoStack.length > 0 || this.redoStack.length > 0;
    applyState(this.elements.reset, hasHistory);
  }

  clear() {
    this.undoStack = [];
    this.redoStack = [];
    this.updateButtonStates();
  }

  undo() {
    if (this.undoStack.length === 0) {
      this.showMessage('Nothing to undo');
      return false;
    }

    // Set flag to prevent generateMosaic during undo operation
    this.isUndoRedoOperation = true;

    // The state we are currently at (the one we are moving *from*)
    const currentStateInUndoStack = this.undoStack[this.undoStack.length - 1];

    // Pop the current state from undoStack. This state will be pushed to redoStack.
    this.undoStack.pop();

    // Push the popped state (which was the current state) to the redoStack.
    this.redoStack.push(currentStateInUndoStack);

    // Determine the state to restore.
    // If undoStack is now empty, it means we've undone all user actions and should revert to stateZero.
    let stateToRestore;
    if (this.undoStack.length === 0) {
      if (!this.stateZero) {
        this.showMessage('Reached initial state, no state zero available.');
        this.updateButtonStates();
        this.isUndoRedoOperation = false;
        return false;
      }
      stateToRestore = this.stateZero;
    } else {
      // Otherwise, restore the new last state in undoStack.
      stateToRestore = this.undoStack[this.undoStack.length - 1];
    }

    // Handle different state structures: stateZero has mosaicState, undo steps have toolEdits
    if (stateToRestore === this.stateZero) {
      // stateZero: clear tool edits and restore original adjustments
      this.setMosaicState([], stateToRestore.adjustments);
    } else {
      // Regular undo step: restore toolEdits and adjustments
      this.setMosaicState(stateToRestore.toolEdits, stateToRestore.adjustments);
    }
    
    // Update UI from state to ensure proper re-render
    if (stateToRestore.adjustments) {
      this.updateUIFromState(stateToRestore.adjustments);
    }
    
    this.updateButtonStates();
    
    // Clear flag after a brief delay to allow state updates to complete
    if (this.operationTimer) {
      clearTimeout(this.operationTimer);
    }
    this.operationTimer = setTimeout(() => {
      this.isUndoRedoOperation = false;
      this.operationTimer = null;
    }, 100);
    
    return true;
  }

  redo() {
    if (this.redoStack.length === 0) {
      this.showMessage('Nothing to redo');
      return false;
    }

    debugLog('INFO', '🔴 REDO: Starting redo operation');
    debugLog('INFO', '🔴 REDO: isUndoRedoOperation before setting:', this.isUndoRedoOperation);

    // Set flag to prevent generateMosaic during redo operation
    this.isUndoRedoOperation = true;
    debugLog('INFO', '🔴 REDO: isUndoRedoOperation after setting:', this.isUndoRedoOperation);

    // Get the state we want to restore (the next state) from redoStack.
    const nextStepToRestore = this.redoStack.pop();
    debugLog('INFO', '🔴 REDO: Restoring state:', nextStepToRestore);

    // Push this state to undoStack, as we are now moving forward to it.
    this.undoStack.push(nextStepToRestore);

    debugLog('INFO', '🔴 REDO: About to call setMosaicState with toolEdits and adjustments');
    this.setMosaicState(nextStepToRestore.toolEdits, nextStepToRestore.adjustments);
    debugLog('INFO', '🔴 REDO: setMosaicState completed');
    
    // Update UI from state to ensure proper re-render
    if (nextStepToRestore.adjustments) {
      this.updateUIFromState(nextStepToRestore.adjustments);
    }
    
    this.updateButtonStates();
    
    // Clear flag after a brief delay to allow state updates to complete
    if (this.operationTimer) {
      clearTimeout(this.operationTimer);
    }
    this.operationTimer = setTimeout(() => {
      debugLog('INFO', '🔴 REDO: Clearing isUndoRedoOperation flag');
      this.isUndoRedoOperation = false;
      this.operationTimer = null;
    }, 100);
    
    return true;
  }

  // Update UI controls from state
  updateUIFromState(adjustments) {
    if (!adjustments || !window.mosaicApp?.adjustmentManager) return;
    window.mosaicApp.adjustmentManager.updateUIFromState(adjustments);
  }

  // Show temporary message
  showMessage(text) {
    // Remove existing message
    const existingMessage = document.getElementById('undo-redo-message');
    if (existingMessage) {
      existingMessage.remove();
    }
    
    // Create new message
    const message = document.createElement('div');
    message.id = 'undo-redo-message';
    message.textContent = text;
    message.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 12px 20px;
      border-radius: 6px;
      font-size: 14px;
      z-index: 10000;
      pointer-events: none;
    `;
    
    document.body.appendChild(message);
    
    // Clear previous message timer
    if (this.messageTimer) {
      clearTimeout(this.messageTimer);
    }
    
    // Remove after 5 seconds
    this.messageTimer = setTimeout(() => {
      if (message.parentNode) {
        message.remove();
      }
      this.messageTimer = null;
    }, 5000);
  }

  // Show first edit warning message (once per page load)
  showFirstEditWarning() {
    // Check if user has already seen this warning on this page load
    if (this.warningShownThisPage) {
      return;
    }
    
    // Check if warning already exists
    if (document.getElementById('first-edit-warning')) return;
    
    // Mark as shown on this page load
    this.warningShownThisPage = true;
    
    // Ensure mosaic status container exists
    let container = document.getElementById('mosaic-status-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'mosaic-status-container';
      document.body.appendChild(container);
    }
    
    // Create warning message
    const warning = document.createElement('div');
    warning.id = 'first-edit-warning';
    warning.className = 'first-edit-warning';
    warning.innerHTML = `
      <div class="warning-content">
        <div class="warning-icon">⚠️</div>
        <div class="warning-text">
          <strong>Before you continue:</strong><br>
          Make sure you've selected the right grid size and crop area. 
          If you change these later, any slider adjustments or tool edits will be reset.
        </div>
        <button class="warning-close" onclick="this.parentElement.parentElement.remove()">×</button>
      </div>
    `;
    
    // Add to mosaic status container
    container.appendChild(warning);
    
    // Clear previous warning timer
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
    }
    
    // Auto-collapse after 8 seconds
    this.warningTimer = setTimeout(() => {
      if (warning.parentElement) {
        this.collapseWarning(warning);
      }
      this.warningTimer = null;
    }, 8000);
  }

  // Collapse warning to a small tab
  collapseWarning(warning) {
    warning.classList.add('collapsed');
    warning.innerHTML = `
      <div class="warning-tab" onclick="window.mosaicApp.undoRedoManager.expandWarning(this.parentElement)">
        <div class="warning-icon">⚠️</div>
        <span>Don't lose your edits!</span>
      </div>
    `;
  }

  // Expand warning from tab
  expandWarning(warning) {
    warning.classList.remove('collapsed');
    warning.innerHTML = `
      <div class="warning-content">
        <div class="warning-icon">⚠️</div>
        <div class="warning-text">
          <strong>Before you continue:</strong><br>
          Make sure you've selected the right grid size and crop area. 
          Any slider adjustments or color edits will be lost if you change these later.
        </div>
        <button class="warning-close" onclick="this.parentElement.parentElement.remove()">×</button>
      </div>
    `;
  }

  // Cleanup method for timer cleanup
  destroy() {
    if (this.operationTimer) {
      clearTimeout(this.operationTimer);
      this.operationTimer = null;
    }
    if (this.messageTimer) {
      clearTimeout(this.messageTimer);
      this.messageTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }
}

class AdjustmentManager {
  /**
   * Adjustment Manager for the Mosaic Creator application.
   * 
   * This class handles all image adjustment functionality including brightness, contrast,
   * saturation, and hue modifications. It provides a user-friendly interface with sliders
   * and integrates with the mosaic rendering pipeline to apply adjustments in real-time.
   * 
   * Key Features:
   * - Real-time image adjustments (brightness, contrast, saturation, hue)
   * - Slider-based UI with plus/minus buttons for fine control
   * - Debounced input handling to prevent excessive re-rendering
   * - Integration with UndoRedoManager for state history
   * - Automatic UI updates and state synchronization
   * - Range conversion between UI values (-100 to 100) and internal processing ranges
   * 
   * Adjustment Types:
   * - Brightness: -100 to 100 (maps to -1 to 1 internally)
   * - Contrast: -100 to 100 (maps to -1 to 1 internally) 
   * - Saturation: -100 to 100 (maps to -1 to 1 internally)
   * - Hue: -100 to 100 (maps to -180 to 180 degrees internally)
   * 
   * UI Elements:
   * - Sliders for each adjustment type with real-time value display
   * - Plus/minus buttons for incremental adjustments (±5 steps)
   * - Reset button to restore all adjustments to defaults
   * - Integration with universal undo/redo system
   * 
   * Integration:
   * - Works with MosaicRenderer to apply adjustments during mosaic generation
   * - Uses UndoRedoManager for state history management
   * - Updates StateManager to trigger mosaic re-rendering
   * - Provides adjustment data to other components as needed
   */
  
  constructor(stateManager, mosaicRenderer, undoRedoManager) {
    this.stateManager = stateManager;
    this.mosaicRenderer = mosaicRenderer;
    this.undoRedoManager = undoRedoManager;
    
    // Default adjustment values
    this.adjustments = { brightness: 0, contrast: 0, saturation: 0, hue: 0 };
    
    // UI element selectors
    this.selectors = {
      sliders: ['brightness', 'contrast', 'saturation', 'hue']
    };
    
    this.elements = {};
    this.sliderTimeout = null;
    this.syncTimer = null;
    this.flagTimer = null;
    this.boundHandlers = {}; // Store bound handlers for cleanup
    
    this.init();

  }

  init() {
    this.setupElements();
    this.setupEventListeners();
    this.subscribeToStateChanges();
    this.updateUI();
  }

  setupElements() {
    // Cache DOM elements
    this.selectors.sliders.forEach(type => {
      this.elements[type] = {
        slider: document.getElementById(`slider-${type}`),
        label: document.getElementById(`slider-${type}-value`),
        minus: document.querySelector(`[data-slider="${type}"].slider-minus`),
        plus: document.querySelector(`[data-slider="${type}"].slider-plus`)
      };
    });
    
    // Adjustment-specific reset button
    this.elements.reset = document.getElementById('reset-image-adjustments');
  }

  setupEventListeners() {
    // Clear existing listeners first
    this.removeEventListeners();
    
    // Setup slider events
    this.selectors.sliders.forEach(type => {
      const element = this.elements[type];
      if (!element.slider) return;
      
      // Store bound handlers
      this.boundHandlers[type] = {
        input: (e) => {
          clearTimeout(this.sliderTimeout);
          this.sliderTimeout = setTimeout(() => {
            this.handleSliderInput(type, e.target.value); // Live preview only
          }, 150);
        },
        change: (e) => {
          // Skip if this is a programmatic change (from plus/minus buttons)
          if (e.target._isProgrammaticChange) {
            debugLog('INFO', `AdjustmentManager: Skipping change event for ${type} (programmatic change)`);
            return;
          }
          // Slider released, saving state
          this.handleSliderRelease(type, e.target.value); // Save state and finalize
        },
        minusClick: () => this.adjustSlider(type, -CONFIG.ADJUSTMENT_STEP_SIZE),
        plusClick: () => this.adjustSlider(type, CONFIG.ADJUSTMENT_STEP_SIZE)
      };
      
      // Add event listeners
      element.slider.addEventListener('input', this.boundHandlers[type].input);
      element.slider.addEventListener('change', this.boundHandlers[type].change);
      
      // Plus/minus buttons
      if (element.minus) {
        element.minus.addEventListener('click', this.boundHandlers[type].minusClick);
      }
      if (element.plus) {
        element.plus.addEventListener('click', this.boundHandlers[type].plusClick);
      }
    });
    
    // Setup adjustment-specific reset button
    if (this.elements.reset) {
      this.boundHandlers.reset = () => this.resetAdjustments();
      this.elements.reset.addEventListener('click', this.boundHandlers.reset);
    }
  }

  removeEventListeners() {
    // Remove event listeners from all elements
    this.selectors.sliders.forEach(type => {
      const element = this.elements[type];
      if (!element || !this.boundHandlers[type]) return;
      
      if (element.slider) {
        if (this.boundHandlers[type].input) element.slider.removeEventListener('input', this.boundHandlers[type].input);
        if (this.boundHandlers[type].change) element.slider.removeEventListener('change', this.boundHandlers[type].change);
      }
      
      if (element.minus && this.boundHandlers[type].minusClick) {
        element.minus.removeEventListener('click', this.boundHandlers[type].minusClick);
      }
      if (element.plus && this.boundHandlers[type].plusClick) {
        element.plus.removeEventListener('click', this.boundHandlers[type].plusClick);
      }
    });
    
    // Remove reset button listener
    if (this.elements.reset && this.boundHandlers.reset) {
      this.elements.reset.removeEventListener('click', this.boundHandlers.reset);
    }
    
    this.boundHandlers = {};
  }

  adjustSlider(type, step) {
    const element = this.elements[type];
    if (!element.slider) return;
    
    const currentValue = parseFloat(element.slider.value);
    const newValue = Math.max(-100, Math.min(100, currentValue + step));
    
    // Set a flag to prevent the change event from processing
    element.slider._isProgrammaticChange = true;
    
    element.slider.value = newValue;
    // Plus/minus button clicked for slider adjustment
    
    // Update adjustments and UI
    this.adjustments[type] = newValue;
    this.updateUI();
    
    // Update state manager with new adjustments
    this.stateManager.updateState({ adjustments: { ...this.adjustments } });
    
    // Use new non-cumulative system: composite layers
    this.mosaicRenderer.compositeLayers();
    
    // Show warning message on first edit (when undo stack is empty)
    if (this.undoRedoManager.undoStack.length === 0) {
      this.undoRedoManager.showFirstEditWarning();
    }
    
    // Save current state to undo stack
    this.undoRedoManager.saveState({ ...this.adjustments });
    
    // Clear the flag after a brief delay
    if (this.flagTimer) {
      clearTimeout(this.flagTimer);
    }
    this.flagTimer = setTimeout(() => {
      element.slider._isProgrammaticChange = false;
      this.flagTimer = null;
    }, 50);
  }

  subscribeToStateChanges() {
    this.stateManager.subscribe('stateChanged', (data) => {
      const { oldState, newState } = data;
      
      // State change detected - adjustments changed
      
      // Re-render mosaic when adjustments change
      if (oldState.adjustments !== newState.adjustments) {
        // During undo/redo operations, the mosaic is already rendered by setMosaicState
        // so we don't need to re-render here to avoid duplicate renders
        if (window.mosaicApp?.undoRedoManager?.isUndoRedoOperation) {
          // Skip re-render during undo/redo operation (already handled by setMosaicState)
        } else {
          // Skip renderStoredMosaic - using compositeLayers instead
          // this.mosaicRenderer.renderStoredMosaic(); // Disabled - using compositeLayers instead
        }
      }
    });
  }

  // Cleanup method for timer cleanup
  destroy() {
    if (this.sliderTimeout) {
      clearTimeout(this.sliderTimeout);
      this.sliderTimeout = null;
    }
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
    if (this.flagTimer) {
      clearTimeout(this.flagTimer);
      this.flagTimer = null;
    }
  }

  handleSliderChange(type, value, saveState = false) {
    const numValue = parseFloat(value);
    
    // Store the UI value directly (no conversion needed)
    this.adjustments[type] = numValue;
    
    // Update UI and state manager (updateUI handles both)
    this.updateUI();
    
    // Update state manager with new adjustments
    this.stateManager.updateState({ adjustments: { ...this.adjustments } });
    
    // Use new non-cumulative system: composite layers
    this.mosaicRenderer.compositeLayers();
    
    // Show warning message on first edit (when undo stack is empty)
    if (saveState && this.undoRedoManager.undoStack.length === 0) {
      this.undoRedoManager.showFirstEditWarning();
    }
    
    // Save current state to undo stack after making changes (only if saveState is true)
    if (saveState) {
      // Save the current mosaic state (with adjustments already applied)
      this.undoRedoManager.saveState({ ...this.adjustments });
    }
  }

  updateSliderLabel(type, value) {
    const element = this.elements[type];
    if (element?.label) {
      element.label.textContent = type === 'hue' ? `${value}°` : value;
    }
  }

  // Handle slider input for live preview using new non-cumulative system
  handleSliderInput(type, value) {
    const numValue = parseFloat(value);
    
    // Store the UI value directly (no conversion needed)
    this.adjustments[type] = numValue;
    
    // Update UI and state manager (updateUI handles both)
    this.updateUI();
    
    // Update state manager with new adjustments
    this.stateManager.updateState({ adjustments: { ...this.adjustments } });
    
    // Use new non-cumulative system: composite layers
    this.mosaicRenderer.compositeLayers();
    
    // Live preview for slider adjustment
  }

  // NEW: Handle slider release for final save (not used yet)
  handleSliderRelease(type, value) {
    const numValue = parseFloat(value);
    
    // Store the UI value directly (no conversion needed)
    this.adjustments[type] = numValue;
    
    // Update UI and state manager (updateUI handles both)
    this.updateUI();
    
    // Update state manager with new adjustments
    this.stateManager.updateState({ adjustments: { ...this.adjustments } });
    
    // Use new non-cumulative system: composite layers
    this.mosaicRenderer.compositeLayers();
    
    // Show warning message on first edit (when undo stack is empty)
    if (this.undoRedoManager.undoStack.length === 0) {
      this.undoRedoManager.showFirstEditWarning();
    }
    
    // Save current state to undo stack
    this.undoRedoManager.saveState({ ...this.adjustments });
    
    // Adjustment finalized
  }

  updateUI() {
    // Update slider values and labels from internal values
    this.selectors.sliders.forEach(type => {
      const element = this.elements[type];
      if (!element?.slider) return;
      
      // Use the internal value directly (no conversion needed)
      const value = Math.round(this.adjustments[type]);
      element.slider.value = value;
      this.updateSliderLabel(type, value);
      
      debugLog('INFO', `AdjustmentManager.updateUI: ${type} - value: ${value}`);
    });
    
    // Always update state manager to keep everything in sync
    this.stateManager.updateState({ adjustments: { ...this.adjustments } });
  }

  resetAdjustments(skipUndoSave = false) {
    // Reset to default values
    this.adjustments = { brightness: 0, contrast: 0, saturation: 0, hue: 0 };
    
    // Update UI and state manager (updateUI handles both)
    this.updateUI();
    
    // Update state manager with reset adjustments
    this.stateManager.updateState({ adjustments: { ...this.adjustments } });
    
    // Use new non-cumulative system: composite layers
    this.mosaicRenderer.compositeLayers();
    
    // Save current state to undo stack after resetting (unless skipped)
    if (!skipUndoSave) {
      this.undoRedoManager.saveState({ ...this.adjustments });
    }
  }

  // Get current adjustments for external use
  getAdjustments() {
    return { ...this.adjustments };
  }

  // Update UI from state (for undo/redo operations)
  updateUIFromState(adjustments) {
    if (!adjustments) return;
    
    debugLog('INFO', 'AdjustmentManager.updateUIFromState: Updating from state:', adjustments);
    
    // Update internal adjustments
    this.adjustments = { ...adjustments };
    
    // Update UI elements and state manager (updateUI handles both)
    this.updateUI();
    
    debugLog('INFO', 'AdjustmentManager.updateUIFromState: Updated internal, UI, and state');
  }

  // Cleanup method for timer cleanup  
  destroy() {
    this.removeEventListeners();
    if (this.sliderTimeout) {
      clearTimeout(this.sliderTimeout);
      this.sliderTimeout = null;
    }
    if (this.syncTimer) {
      clearTimeout(this.syncTimer);
      this.syncTimer = null;
    }
    if (this.flagTimer) {
      clearTimeout(this.flagTimer);
      this.flagTimer = null;
    }
  }
}

class PaintingManager {
  constructor(stateManager, mosaicRenderer, undoRedoManager) {
    this.stateManager = stateManager;
    this.mosaicRenderer = mosaicRenderer;
    this.undoRedoManager = undoRedoManager;
    
    // Tool state
    this.currentTool = (window.innerWidth <= 750) ? null : 'paintbrush';
    this.currentColor = null;
    this.isDrawing = false;
    this.lastDrawnCell = null;
    
    // Panning state
    this.isPanning = false;
    this.panStartX = null;
    this.panStartY = null;
    this.panStartScrollLeft = null;
    this.panStartScrollTop = null;
    
    // Canvas and interaction state
    this.mosaicCanvas = null;
    this.mosaicCtx = null;
    this.cellSize = 0;
    this.gridWidth = 0;
    this.gridHeight = 0;
    
    // Event handlers
    this.boundHandlers = {};
    this.paletteTimer = null; // For delayed color palette setup
    
    this.init();

  }

  init() {
    this.setupCanvas();
    this.setupEventListeners();
    this.setupToolButtons();
    this.setupColorPalette();
    this.subscribeToStateChanges();
    
    // On mobile, start with no active tool selected to allow scrolling
    if (window.innerWidth <= 750) {
      document.querySelectorAll('.edit-btn').forEach(btn => btn.classList.remove('active'));
    }
    
    // Initialize tool status display
    this.updateToolStatus();
    
    // Try to set up color palette again after a short delay in case mosaic isn't ready yet
    setTimeout(() => {
      this.setupColorPalette();
    }, 500);
  }

  setupCanvas() {
    this.mosaicCanvas = document.getElementById('image-edit-canvas');
    if (this.mosaicCanvas) {
      this.mosaicCtx = this.mosaicCanvas.getContext('2d', { willReadFrequently: true });
  
    } else {
      debugLog('WARN', 'PaintingManager: Mosaic canvas not found');
    }
  }

  setupEventListeners() {
    if (!this.mosaicCanvas) return;

    // Create bound event handlers
    this.boundHandlers = {
      pointerDown: this.handlePointerDown.bind(this),
      pointerMove: this.handlePointerMove.bind(this),
      pointerUp: this.handlePointerUp.bind(this),
      contextMenu: this.handleContextMenu.bind(this)
    };

    this.mosaicCanvas.addEventListener('mousedown', this.boundHandlers.pointerDown);
    this.mosaicCanvas.addEventListener('touchstart', this.boundHandlers.pointerDown, { passive: false });
    window.addEventListener('mousemove', this.boundHandlers.pointerMove);
    window.addEventListener('touchmove', this.boundHandlers.pointerMove, { passive: false });
    window.addEventListener('mouseup', this.boundHandlers.pointerUp);
    window.addEventListener('touchend', this.boundHandlers.pointerUp);
    this.mosaicCanvas.addEventListener('contextmenu', this.boundHandlers.contextMenu);
  }

  setupToolButtons() {
    const toolButtons = document.querySelectorAll('.edit-btn');
    toolButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const toolId = e.currentTarget.id;
        this.selectTool(toolId);
      });
    });
  }

  setupColorPalette() {
    const colorPalette = document.getElementById('color-palette');
    if (!colorPalette) return;

    // Generate color swatches from mosaic colors
    const mosaicColors = this.mosaicRenderer.mosaicColors;
    if (!mosaicColors || mosaicColors.length === 0) {
      debugLog('WARN', 'PaintingManager: No mosaic colors available yet');
      return;
    }

    // Sort colors by visual similarity and color families
    const sortedColors = [...mosaicColors].sort((a, b) => {
      // Convert RGB to HSL to get hue values
      const hslA = this.rgbToHsl(a.rgb[0], a.rgb[1], a.rgb[2]);
      const hslB = this.rgbToHsl(b.rgb[0], b.rgb[1], b.rgb[2]);
      
      // Handle grayscale colors (saturation near 0) - put them at the beginning
      if (hslA.s < 10 && hslB.s < 10) {
        // Both are grayscale, sort by lightness (dark to light)
        return hslA.l - hslB.l; // Dark to light for grays
      } else if (hslA.s < 10) {
        return -1; // Put grayscale before colored
      } else if (hslB.s < 10) {
        return 1; // Put colored after grayscale
      }
      
      // Group colors by visual color families first
      const familyA = this.getColorFamily(hslA, a.name);
      const familyB = this.getColorFamily(hslB, b.name);
      
      if (familyA !== familyB) {
        return familyA - familyB; // Sort by color family
      }
      
      // Within the same family, sort by lightness (dark to light)
      return hslA.l - hslB.l;
    });

    colorPalette.innerHTML = '';

    sortedColors.forEach((color, index) => {
      const swatch = document.createElement('div');
      swatch.className = 'color-palette-swatch';
      swatch.style.setProperty('--swatch-color', color.hex);
      swatch.dataset.colorIndex = index;
      swatch.dataset.colorHex = color.hex;
      swatch.dataset.colorName = color.name;
      
      // Add tooltip with color name
      swatch.title = color.name;
      
      // Add non-breaking space to prevent CSS from hiding empty divs
      swatch.innerHTML = '&nbsp;';
      
      swatch.addEventListener('click', () => {
        this.selectColor(color);
      });
      
      colorPalette.appendChild(swatch);
    });

    // Set default color (darkest color)
    if (sortedColors.length > 0) {
      this.selectColor(sortedColors[0]);
    }


  }

  subscribeToStateChanges() {
    this.stateManager.subscribe('stateChanged', (data) => {
      const { oldState, newState } = data;
      
      // Update grid dimensions when they change
      if (oldState.gridWidth !== newState.gridWidth || 
          oldState.gridHeight !== newState.gridHeight ||
          oldState.baseplate !== newState.baseplate) {
        this.updateGridDimensions();
      }
    });
  }

  updateGridDimensions() {
    const state = this.stateManager.getState();
    this.gridWidth = state.gridWidth;
    this.gridHeight = state.gridHeight;
    
    // Calculate cell size using the same method as MosaicRenderer
    if (this.mosaicRenderer) {
      const { width: totalGridWidth, height: totalGridHeight } = this.mosaicRenderer.getTotalGridDimensions(this.gridWidth, this.gridHeight);
      this.cellSize = this.mosaicRenderer.calculateCellSize(totalGridWidth, totalGridHeight);
    }
  }

  refreshColorPalette() {
    // Re-setup color palette when mosaic is generated
    this.setupColorPalette();
  }

  selectTool(toolId) {
    // Update tool buttons
    document.querySelectorAll('.edit-btn').forEach(btn => btn.classList.remove('active'));
    document.getElementById(toolId)?.classList.add('active');
    
    // Update current tool
    const toolMap = {
      'paintbrush-toggle': 'paintbrush',
      'bucket-toggle': 'bucket',
      'pipette-toggle': 'pipette',
      'pan-toggle': 'pan',
      'painter-reset': 'reset'
    };
    
    this.currentTool = toolMap[toolId] || 'paintbrush';
    
    // Handle reset tool immediately
    if (this.currentTool === 'reset') {
      this.handleReset();
      // Switch back to paintbrush after reset
      this.currentTool = 'paintbrush';
      document.querySelectorAll('.edit-btn').forEach(btn => btn.classList.remove('active'));
      document.getElementById('paintbrush-toggle')?.classList.add('active');
    }
    
    // Update cursor
    this.updateCursor();
    
    // Update tool status display
    this.updateToolStatus();
  }

  selectColor(color) {
    this.currentColor = color;
    
    // Update color swatch selection
    document.querySelectorAll('.color-palette-swatch').forEach(swatch => {
      swatch.classList.remove('selected');
    });
    
    const selectedSwatch = document.querySelector(`[data-color-hex="${color.hex}"]`);
    if (selectedSwatch) {
      selectedSwatch.classList.add('selected');
    }
    
    // Update color status display
    this.updateColorStatus();
  }

  updateCursor() {
    if (!this.mosaicCanvas) return;
    
    const cursorMap = {
      'paintbrush': 'crosshair',
      'bucket': 'crosshair',
      'pipette': 'crosshair',
      'pan': 'grab',
      'reset': 'default'
    };
    
    this.mosaicCanvas.style.cursor = cursorMap[this.currentTool] || 'default';
  }

  handlePointerDown(e) {
    // If no tool is selected (e.g., on mobile initial state), allow native scrolling
    if (!this.currentTool) {
      return;
    }
    const isTouch = e.type === 'touchstart';
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    
    // Handle pan tool
    if (this.currentTool === 'pan') {
      this.startPanning(clientX, clientY);
      e.preventDefault();
      return;
    }
    
    const cellPos = this.getCellPosition(clientX, clientY);
    if (!cellPos) return;

    e.preventDefault();
    
    if (this.currentTool === 'paintbrush') {
      this.startDrawing(cellPos);
    } else if (this.currentTool === 'bucket') {
      this.bucketFill(cellPos);
    } else if (this.currentTool === 'pipette') {
      this.pickColor(cellPos);
    }
  }

  handlePointerMove(e) {
    // Handle panning
    if (this.isPanning) {
      const isTouch = e.type === 'touchmove';
      const clientX = isTouch ? e.touches[0].clientX : e.clientX;
      const clientY = isTouch ? e.touches[0].clientY : e.clientY;
      this.updatePanning(clientX, clientY);
      e.preventDefault();
      return;
    }
    
    if (!this.isDrawing || this.currentTool !== 'paintbrush') return;
    
    const isTouch = e.type === 'touchmove';
    const clientX = isTouch ? e.touches[0].clientX : e.clientX;
    const clientY = isTouch ? e.touches[0].clientY : e.clientY;
    
    const cellPos = this.getCellPosition(clientX, clientY);
    if (!cellPos) return;

    e.preventDefault();
    this.continueDrawing(cellPos);
  }

  handlePointerUp(e) {
    if (this.isPanning) {
      this.endPanning();
    } else if (this.isDrawing) {
      this.endDrawing();
    }
  }

  handleContextMenu(e) {
    e.preventDefault();
    // Prevent context menu on canvas
  }

  getCellPosition(clientX, clientY) {
    if (!this.mosaicCanvas) return null;
    
    const rect = this.mosaicCanvas.getBoundingClientRect();
    let x = clientX - rect.left;
    let y = clientY - rect.top;
    
    // Account for CSS transform scale if present
    const transform = this.mosaicCanvas.style.transform;
    if (transform && transform.includes('scale')) {
      const scaleMatch = transform.match(/scale\(([^)]+)\)/);
      if (scaleMatch) {
        const scale = parseFloat(scaleMatch[1]);
        x = x / scale;
        y = y / scale;
      }
    }
    
    // Convert from displayed coordinates to internal canvas coordinates
    const scaleX = this.mosaicCanvas.width / this.mosaicCanvas.offsetWidth;
    const scaleY = this.mosaicCanvas.height / this.mosaicCanvas.offsetHeight;
    const canvasX = x * scaleX;
    const canvasY = y * scaleY;
    
    // Convert to grid coordinates using internal canvas dimensions
    const gridX = Math.floor(canvasX / this.cellSize);
    const gridY = Math.floor(canvasY / this.cellSize);
    
    // Check if within bounds
    if (this.mosaicRenderer) {
      const { width: totalGridWidth, height: totalGridHeight } = this.mosaicRenderer.getTotalGridDimensions(this.gridWidth, this.gridHeight);
      
      if (gridX < 0 || gridX >= totalGridWidth || gridY < 0 || gridY >= totalGridHeight) {
        return null;
      }
    }
    
    return { x: gridX, y: gridY };
  }

  startDrawing(cellPos) {
    if (!this.currentColor) return;
    
    this.isDrawing = true;
    this.lastDrawnCell = null;
    
    this.paintCell(cellPos);
  }

  continueDrawing(cellPos) {
    if (!this.currentColor || !this.isDrawing) return;
    
    // Only paint if we moved to a different cell
    if (!this.lastDrawnCell || 
        this.lastDrawnCell.x !== cellPos.x || 
        this.lastDrawnCell.y !== cellPos.y) {
      this.paintCell(cellPos);
    }
  }

  endDrawing() {
    this.isDrawing = false;
    this.lastDrawnCell = null;
    
    // Save state after the entire stroke is complete
    // Save current mosaic state to undo stack
    const currentAdjustments = this.stateManager.getState().adjustments;
    this.undoRedoManager.saveState({ ...currentAdjustments });
  }

  startPanning(clientX, clientY) {
    this.isPanning = true;
    this.panStartX = clientX;
    this.panStartY = clientY;
    
    // Get current scroll position
    const container = this.mosaicCanvas.parentElement; // .canvas-zoom-container
    if (container) {
      this.panStartScrollLeft = container.scrollLeft;
      this.panStartScrollTop = container.scrollTop;
    }
    
    // Update cursor to show grabbing
    this.mosaicCanvas.style.cursor = 'grabbing';
  }

  updatePanning(clientX, clientY) {
    if (!this.isPanning) return;
    
    const deltaX = this.panStartX - clientX;
    const deltaY = this.panStartY - clientY;
    
    const container = this.mosaicCanvas.parentElement; // .canvas-zoom-container
    if (container) {
      container.scrollLeft = this.panStartScrollLeft + deltaX;
      container.scrollTop = this.panStartScrollTop + deltaY;
    }
  }

  endPanning() {
    this.isPanning = false;
    this.panStartX = null;
    this.panStartY = null;
    this.panStartScrollLeft = null;
    this.panStartScrollTop = null;
    
    // Update cursor back to grab
    this.mosaicCanvas.style.cursor = 'grab';
  }

  paintCell(cellPos) {
    // Don't paint frame cells
    if (this.isFrameCell(cellPos)) return;
    
    this.lastDrawnCell = cellPos;
    
    // Show warning message on first edit (when undo stack is empty)
    if (this.undoRedoManager.undoStack.length === 0) {
      this.undoRedoManager.showFirstEditWarning();
    }
    
    // Paint the cell
    this.drawCell(cellPos, this.currentColor);
    
  }

    bucketFill(cellPos) {
    if (!this.currentColor) return;
    
    // Don't fill frame cells
    if (this.isFrameCell(cellPos)) return;
    
    // Get current color at this position
    const currentColor = this.getCellColor(cellPos);
    if (!currentColor) return;
    
    // If already the target color, no need to fill
    if (this.colorsEqual(currentColor, this.currentColor)) return;
    
    // Perform flood fill
    const cellsToFill = this.floodFill(cellPos, currentColor, this.currentColor);
    
    // Batch update toolEdits for all cells (much more efficient)
    const frameWidth = this.mosaicRenderer.getFrameWidth();
    cellsToFill.forEach(pos => {
      const mosaicX = pos.x - frameWidth;
      const mosaicY = pos.y - frameWidth;
      
      // Ensure toolEdits array exists and has the right dimensions
      if (!this.mosaicRenderer.toolEdits[mosaicY]) {
        this.mosaicRenderer.toolEdits[mosaicY] = [];
      }
      
      // Store the tool edit
      this.mosaicRenderer.toolEdits[mosaicY][mosaicX] = {
        r: this.currentColor.rgb[0],
        g: this.currentColor.rgb[1],
        b: this.currentColor.rgb[2]
      };
    });
    
    // Update currentMosaic by compositing layers (only once!)
    this.mosaicRenderer.compositeLayers();
    
    // Notify project summary that mosaic has changed (debounced)
    if (window.mosaicApp && window.mosaicApp.projectSummary) {
      window.mosaicApp.projectSummary.onMosaicChanged();
    }
    
    // Notify mosaic image capture that mosaic has changed (debounced)
    if (window.mosaicApp && window.mosaicApp.mosaicImageCapture) {
      window.mosaicApp.mosaicImageCapture.debouncedOnMosaicChanged();
    }
    
    // Save current mosaic state to undo stack (only once!)
    const currentAdjustments = this.stateManager.getState().adjustments;
    this.undoRedoManager.saveState({ ...currentAdjustments });
  }

  floodFill(startPos, targetColor, fillColor) {
    const visited = new Set();
    const toVisit = [startPos];
    const cellsToFill = [];
    
    while (toVisit.length > 0) {
      const pos = toVisit.pop();
      const key = `${pos.x},${pos.y}`;
      
      if (visited.has(key)) continue;
      visited.add(key);
      
      // Check bounds and frame
      if (this.isFrameCell(pos)) continue;
      
      // Check if this cell matches the target color
      const cellColor = this.getCellColor(pos);
      if (!cellColor || !this.colorsEqual(cellColor, targetColor)) continue;
      
      cellsToFill.push(pos);
      
      // Add 8-way neighbors
      const neighbors = [
        { x: pos.x - 1, y: pos.y - 1 }, { x: pos.x, y: pos.y - 1 }, { x: pos.x + 1, y: pos.y - 1 },
        { x: pos.x - 1, y: pos.y },                               { x: pos.x + 1, y: pos.y },
        { x: pos.x - 1, y: pos.y + 1 }, { x: pos.x, y: pos.y + 1 }, { x: pos.x + 1, y: pos.y + 1 }
      ];
      
      neighbors.forEach(neighbor => {
        const neighborKey = `${neighbor.x},${neighbor.y}`;
        if (!visited.has(neighborKey)) {
          toVisit.push(neighbor);
        }
      });
    }
    
    return cellsToFill;
  }

  pickColor(cellPos) {
    // Don't pick color from frame cells
    if (this.isFrameCell(cellPos)) return;
    
    // Get frame width to convert from total grid coordinates to mosaic coordinates
    const frameWidth = this.mosaicRenderer.getFrameWidth();
    
    // Convert from total grid coordinates (including frame) to mosaic coordinates (no frame)
    const mosaicX = cellPos.x - frameWidth;
    const mosaicY = cellPos.y - frameWidth;
    
    // Read the color directly from currentMosaic
    const cellColor = this.mosaicRenderer.currentMosaic[mosaicY]?.[mosaicX];
    if (!cellColor) return;
    
    // Convert to color object format expected by selectColor
    const color = {
      name: 'Picked Color', // Will be replaced by palette lookup
      hex: this.mosaicRenderer.rgbToHex(cellColor.r, cellColor.g, cellColor.b),
      rgb: [cellColor.r, cellColor.g, cellColor.b]
    };
    
    // Find the exact palette color that matches these RGB values
    const paletteColor = this.mosaicRenderer.mosaicColors.find(paletteColor => 
      paletteColor.rgb[0] === cellColor.r && 
      paletteColor.rgb[1] === cellColor.g && 
      paletteColor.rgb[2] === cellColor.b
    );
    
    // Use the palette color if found, otherwise use the direct color
    this.selectColor(paletteColor || color);
  }

  handleReset() {
    // Save state for undo with current adjustments
    const currentAdjustments = this.stateManager.getState().adjustments;
    this.undoRedoManager.saveState({ ...currentAdjustments });
    
    // Reset to state zero (which includes regenerating from original image)
    this.undoRedoManager.resetToStateZero();
  }



  isFrameCell(cellPos) {
    if (!this.mosaicRenderer) return false;
    
    return this.mosaicRenderer.isFrameCell(cellPos, this.gridWidth, this.gridHeight);
  }

  getCellColor(cellPos) {
    // Convert from total grid coordinates (including frame) to mosaic coordinates (no frame)
    const frameWidth = this.mosaicRenderer.getFrameWidth();
    const mosaicX = cellPos.x - frameWidth;
    const mosaicY = cellPos.y - frameWidth;
    
    // Get the color from the mosaic renderer's current mosaic
    if (this.mosaicRenderer.currentMosaic && 
        this.mosaicRenderer.currentMosaic[mosaicY] && 
        this.mosaicRenderer.currentMosaic[mosaicY][mosaicX]) {
      
      const cellColor = this.mosaicRenderer.currentMosaic[mosaicY][mosaicX];
      
      // Find matching mosaic color
      return this.mosaicRenderer.mosaicColors.find(color => 
        color.rgb[0] === cellColor.r && 
        color.rgb[1] === cellColor.g && 
        color.rgb[2] === cellColor.b
      );
    }
    
    return null;
  }


  colorsEqual(color1, color2) {
    return color1.hex === color2.hex;
  }

  // RGB to HSL conversion for color sorting
  rgbToHsl(r, g, b) {
    r /= 255;
    g /= 255;
    b /= 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    
    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }
    
    return { h: h * 360, s: s * 100, l: l * 100 };
  }

  // Group colors by visual similarity and color families
  getColorFamily(hsl, colorName) {
    const { h, s, l } = hsl;
    
    // Define color families with priority order (lower number = earlier in palette)
    const families = {
      // Grayscale (handled separately above)
      grayscale: 0,
      
      // Browns and earth tones
      brown: 1,
      
      // Reds and pinks
      red: 2,
      
      // Oranges
      orange: 3,
      
      // Yellows and light yellows
      yellow: 4,
      
      // Greens (including olive and yellowish greens)
      green: 5,
      
      // Blues and cyans
      blue: 6,
      
      // Purples and magentas
      purple: 7
    };
    
    // Use color name for more accurate grouping
    const name = colorName.toLowerCase();
    
    // Brown family
    if (name.includes('brown') || name.includes('tan') || name.includes('nougat')) {
      return families.brown;
    }
    
    // Red family
    if (name.includes('red') || name.includes('pink') || name.includes('coral') || name.includes('magenta')) {
      return families.red;
    }
    
    // Orange family
    if (name.includes('orange')) {
      return families.orange;
    }
    
    // Yellow family (including light yellows)
    if (name.includes('yellow') || name.includes('light') && (h > 40 && h < 80)) {
      return families.yellow;
    }
    
    // Green family (including olive, yellowish green, and all greens)
    if (name.includes('green') || name.includes('olive') || (h > 60 && h < 180)) {
      return families.green;
    }
    
    // Blue family
    if (name.includes('blue') || name.includes('azure') || name.includes('turquoise') || (h > 180 && h < 280)) {
      return families.blue;
    }
    
    // Purple family
    if (name.includes('purple') || name.includes('violet') || name.includes('lavender') || (h > 280 && h < 360)) {
      return families.purple;
    }
    
    // Fallback to hue-based grouping
    if (h < 30 || h > 330) return families.red;      // Red range
    if (h >= 30 && h < 60) return families.orange;    // Orange range
    if (h >= 60 && h < 90) return families.yellow;    // Yellow range
    if (h >= 90 && h < 150) return families.green;    // Green range
    if (h >= 150 && h < 210) return families.blue;    // Blue range
    if (h >= 210 && h < 270) return families.blue;    // Cyan range
    if (h >= 270 && h < 330) return families.purple;  // Purple range
    
    return families.green; // Default fallback
  }

  drawCell(cellPos, color) {
    if (!this.mosaicCtx) return;
    
    // Calculate cell position on canvas
    const cellX = cellPos.x * this.cellSize;
    const cellY = cellPos.y * this.cellSize;
    
    // Draw cell with consistent spacing (matching mosaic renderer)
    const spacing = this.mosaicRenderer.getGridSpace(this.cellSize);
    const cellW = this.cellSize - spacing;
    const cellH = this.cellSize - spacing;
    
    // Draw the selected color directly (no adjustments applied during drawing)
    const hexColor = this.mosaicRenderer.rgbToHex(color.rgb[0], color.rgb[1], color.rgb[2]);
    this.mosaicCtx.fillStyle = hexColor;
    this.mosaicCtx.fillRect(
      cellX + spacing / 2, 
      cellY + spacing / 2, 
      cellW, 
      cellH
    );
    
    
    // Draw stud overlay to match the original mosaic style
    this.mosaicRenderer.drawSingleStudOverlay(this.mosaicCtx, cellX, cellY, this.cellSize);
    
    // Update toolEdits with the selected color (new non-cumulative system)
    // Convert from total grid coordinates (including frame) to mosaic coordinates (no frame)
    const frameWidth = this.mosaicRenderer.getFrameWidth();
    const mosaicX = cellPos.x - frameWidth;
    const mosaicY = cellPos.y - frameWidth;
    
    // Ensure toolEdits array exists and has the right dimensions
    if (!this.mosaicRenderer.toolEdits[mosaicY]) {
      this.mosaicRenderer.toolEdits[mosaicY] = [];
    }
    
    // Store the tool edit (this will override the adjusted mosaic in compositeLayers)
    this.mosaicRenderer.toolEdits[mosaicY][mosaicX] = {
      r: color.rgb[0],
      g: color.rgb[1],
      b: color.rgb[2]
    };
    
    // Update currentMosaic by compositing layers (this will trigger visual update)
    this.mosaicRenderer.compositeLayers();
    
    // Notify project summary that mosaic has changed (debounced)
    if (window.mosaicApp && window.mosaicApp.projectSummary) {
      window.mosaicApp.projectSummary.onMosaicChanged();
    }
    
    // Notify mosaic image capture that mosaic has changed (debounced)
    if (window.mosaicApp && window.mosaicApp.mosaicImageCapture) {
      window.mosaicApp.mosaicImageCapture.debouncedOnMosaicChanged();
    }
  }

  ensureMosaicStatusContainer() {
    let container = document.getElementById('mosaic-status-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'mosaic-status-container';
      container.classList.add('hidden'); // Hide by default until image is uploaded
      document.body.appendChild(container);
      
      // Set up cart drawer observer
      this.setupCartDrawerObserver(container);
    }
    return container;
  }

  setupCartDrawerObserver(container) {
    // Watch for cart drawer active state changes
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    // Initial state check
    this.updateStatusContainerVisibility(container, cartDrawer.classList.contains('active'));

    // Watch for class changes on cart-drawer element
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          this.updateStatusContainerVisibility(container, cartDrawer.classList.contains('active'));
        }
      });
    });

    observer.observe(cartDrawer, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  updateStatusContainerVisibility(container, isCartDrawerActive, hasUploadedImage = null) {
    // If hasUploadedImage is not provided, check the state
    if (hasUploadedImage === null) {
      hasUploadedImage = this.stateManager.state.uploadedImage !== null;
    }
    
    if (isCartDrawerActive || !hasUploadedImage) {
      container.classList.add('hidden');
    } else {
      container.classList.remove('hidden');
    }
  }

  // Status display methods for UI feedback
  updateToolStatus() {
    // Add visual feedback for current tool
    const toolStatus = document.getElementById('tool-status');
    if (!toolStatus) {
      // Ensure mosaic status container exists
      this.ensureMosaicStatusContainer();
      
      // Create tool status element if it doesn't exist
      const statusDiv = document.createElement('div');
      statusDiv.id = 'tool-status';
      statusDiv.style.cssText = 'background: rgba(0,0,0,0.8); color: white; padding: 8px; border-radius: 4px; font-size: 12px; min-height: 20px;';
      document.getElementById('mosaic-status-container').appendChild(statusDiv);
    }
    
    const statusElement = document.getElementById('tool-status');
    if (statusElement) {
      if (!this.currentTool) {
        statusElement.textContent = 'Tool: None';
      } else {
        const capitalizedTool = this.currentTool.charAt(0).toUpperCase() + this.currentTool.slice(1);
        statusElement.textContent = `Tool: ${capitalizedTool}`;
      }
    }
  }

  updateColorStatus() {
    // Add visual feedback for current color
    const colorStatus = document.getElementById('color-status');
    if (!colorStatus) {
      // Ensure mosaic status container exists
      this.ensureMosaicStatusContainer();
      
      // Create color status element if it doesn't exist
      const statusDiv = document.createElement('div');
      statusDiv.id = 'color-status';
      statusDiv.style.cssText = 'background: rgba(0,0,0,0.8); color: white; padding: 8px; border-radius: 4px; font-size: 12px; min-height: 20px;';
      document.getElementById('mosaic-status-container').appendChild(statusDiv);
    }
    
    const statusElement = document.getElementById('color-status');
    if (statusElement) {
      if (this.currentColor) {
        statusElement.innerHTML = `Color: <span style="display: inline-block; width: 18px; height: 18px; background-color: ${this.currentColor.hex}; border: 1px solid white; border-radius: 2px; margin: 0 4px; vertical-align: middle;"></span> ${this.currentColor.name}`;
        statusElement.style.display = 'flex';
      } else {
        statusElement.style.display = 'none';
      }
    }
  }

  // Cleanup method
  destroy() {
    if (this.mosaicCanvas && this.boundHandlers) {
      this.mosaicCanvas.removeEventListener('mousedown', this.boundHandlers.pointerDown);
      this.mosaicCanvas.removeEventListener('touchstart', this.boundHandlers.pointerDown);
      window.removeEventListener('mousemove', this.boundHandlers.pointerMove);
      window.removeEventListener('touchmove', this.boundHandlers.pointerMove);
      window.removeEventListener('mouseup', this.boundHandlers.pointerUp);
      window.removeEventListener('touchend', this.boundHandlers.pointerUp);
      this.mosaicCanvas.removeEventListener('contextmenu', this.boundHandlers.contextMenu);
    }
    
    this.boundHandlers = {};
  }

  // Cleanup method for timer cleanup
  destroy() {
    if (this.paletteTimer) {
      clearTimeout(this.paletteTimer);
      this.paletteTimer = null;
    }
  }
}

class CanvasInteractionManager {
  constructor() {
    // CanvasInteractionManager class initialized
  }
}

class ExportManager {
  constructor() {
    // ExportManager class initialized
  }
}

class ProjectSummaryManager {
  constructor(stateManager, mosaicRenderer) {
    this.stateManager = stateManager;
    this.mosaicRenderer = mosaicRenderer;
    this.warningTimer = null; // For auto-remove warning timer
    this.copyFeedbackTimer = null; // For copy button feedback
    this.downloadCleanupTimer = null; // For download link cleanup
    this.dynamicCanvas = null; // Track dynamically created canvas for cleanup
    this.dynamicCtx = null; // Track dynamically created context for cleanup
    this.setupElements();
    this.setupButtons();
    this.setupDebouncedUpdates();
  }
  
  // Constants for layout
  get LOGO_CONTAINER_WIDTH() { return 400; }

  setupElements() {
    this.elements = {
      summaryDimensions: document.getElementById('summary-dimensions'),
      summaryBaseplate: document.getElementById('summary-baseplate'),
      summaryTotalBricks: document.getElementById('summary-total-bricks'),
      summaryTotalPrice: document.getElementById('summary-total-price'),
      summaryColorBreakdown: document.getElementById('summary-color-breakdown'),
      copySummaryBtn: document.getElementById('copy-summary-btn'),
      exportPartsBtn: document.getElementById('export-parts-btn'),
      downloadInstructionsBtn: document.getElementById('download-instructions-btn')
    };
  }

  setupDebouncedUpdates() {
    // Debounced update function with 500ms delay
    this.debouncedUpdate = this.debounce(() => {
      this.updateProjectSummary();
    }, 500);
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  updateProjectSummary() {
    this.updateDimensions();
    this.updateBaseplate();
    this.updateTotalBricks();
    this.updateTotalPrice();
    this.updateColorBreakdown();
  }

  // Method to be called when mosaic changes (for painting operations)
  onMosaicChanged() {
    // Use debounced update for painting operations
    this.debouncedUpdate();
  }

  updateDimensions() {
    const state = this.stateManager.getState();
    const dimensionsSpan = this.elements.summaryDimensions;
    if (dimensionsSpan) dimensionsSpan.textContent = state.gridSizeString || '';
  }

  updateBaseplate() {
    const state = this.stateManager.getState();
    const baseplateSummarySpan = this.elements.summaryBaseplate;
    if (baseplateSummarySpan) {
      baseplateSummarySpan.textContent = state.baseplate ? 'Framed' : 'Regular';
    }
  }

  updateTotalBricks() {
    const mosaicCanvas = document.getElementById('image-edit-canvas');
    let totalStuds = 0;
    let actualColoredCells = 0;
    
    if (mosaicCanvas && this.mosaicRenderer.currentMosaic) {
      const grid = this.mosaicRenderer.currentMosaic;
      
      // Count total grid cells
      if (grid.length > 0 && grid[0].length > 0) {
        totalStuds = grid.length * grid[0].length;
      }
      
      // Count actual colored cells (same logic as color breakdown)
      for (let row = 0; row < grid.length; row++) {
        for (let col = 0; col < grid[row].length; col++) {
          if (grid[row][col]) {
            actualColoredCells++;
          }
        }
      }
      
      // Validate counts match
      if (totalStuds !== actualColoredCells || CONFIG.DEV.FORCE_VALIDATION_WARNING) {
        debugLog('WARN', `Mosaic validation failed: Expected ${totalStuds} cells, found ${actualColoredCells} colored cells`);
        this.showMosaicValidationWarning(totalStuds, actualColoredCells);
        // Use the actual count for display
        totalStuds = actualColoredCells;
      }
    }

    const totalBricksSpan = this.elements.summaryTotalBricks;
    if (totalBricksSpan) totalBricksSpan.textContent = totalStuds;
  }

  showMosaicValidationWarning(expected, actual) {
    // Create or update validation warning
    let warningDiv = document.getElementById('mosaic-validation-warning');
    if (!warningDiv) {
      warningDiv = document.createElement('div');
      warningDiv.id = 'mosaic-validation-warning';
      warningDiv.style.cssText = `
        position: fixed;
        bottom: 20px;
        right: 20px;
        background: #ff6b6b;
        color: white;
        padding: 15px;
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        z-index: 10000;
        max-width: 300px;
        font-family: Arial, sans-serif;
        font-size: 14px;
      `;
      document.body.appendChild(warningDiv);
    }
    
    warningDiv.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 6px;">⚠️ Mosaic Validation Warning</div>
      <div>Expected ${expected} cells, found ${actual} colored cells.</div>
      <div style="margin-top: 6px; font-size: 12px; opacity: 0.9;">This may indicate an incomplete mosaic generation.</div>
      <div style="text-align: right; margin-top: 8px;">
        <button onclick="this.parentElement.parentElement.remove()" style="
          background: rgba(255,255,255,0.2);
          border: 1px solid rgba(255,255,255,0.3);
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
        ">Dismiss</button>
      </div>
    `;
    
    // Clear previous warning timer
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
    }
    
    // Auto-remove после 20 seconds
    this.warningTimer = setTimeout(() => {
      if (warningDiv && warningDiv.parentElement) {
        warningDiv.remove();
      }
      this.warningTimer = null;
    }, 20000);
  }

  updateTotalPrice() {
    const state = this.stateManager.getState();
    const priceSpan = this.elements.summaryTotalPrice;
    if (priceSpan) priceSpan.textContent = state.currentPrice || '-';
  }

  getColorCountsFromMosaic() {
    if (!this.mosaicRenderer.currentMosaic) {
      return [];
    }

    const grid = this.mosaicRenderer.currentMosaic;
    const state = this.stateManager.getState();
    
    // Count colors from all cells in currentMosaic (frame cells are already excluded from storage)
    const colorMap = new Map();
    for (let row = 0; row < grid.length; row++) {
      for (let col = 0; col < grid[row].length; col++) {
        const cellColor = grid[row][col];
        if (!cellColor) continue;
        
        // Convert RGB values to color key for counting
        const colorKey = `${cellColor.r},${cellColor.g},${cellColor.b}`;
        if (!colorMap.has(colorKey)) colorMap.set(colorKey, 0);
        colorMap.set(colorKey, colorMap.get(colorKey) + 1);
      }
    }
    
    // Convert color keys back to full color objects and return sorted counts
    return Array.from(colorMap.entries())
      .map(([colorKey, count]) => {
        const [r, g, b] = colorKey.split(',').map(Number);
        const color = this.findMosaicColorByRgb(r, g, b);
        return { color, count };
      })
      .sort((a, b) => b.count - a.count);
  }

  findMosaicColorByRgb(r, g, b) {
    // Find the matching color in the mosaic colors palette
    const mosaicColor = this.mosaicRenderer.mosaicColors.find(color => 
      color.rgb[0] === r && color.rgb[1] === g && color.rgb[2] === b
    );
    
    // Return the full color object if found, otherwise create a fallback
    if (mosaicColor) {
      return mosaicColor;
    }
    
    // Fallback: create a color object from RGB values
    return {
      name: `Custom (${r},${g},${b})`,
      hex: this.rgbToHex(r, g, b),
      rgb: [r, g, b]
    };
  }

  rgbToHex(r, g, b) {
    const toHex = (c) => {
      const hex = Math.round(c).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  updateColorBreakdown() {
    const colorBreakdownDiv = this.elements.summaryColorBreakdown;
    
    if (!colorBreakdownDiv) return;

    if (!this.mosaicRenderer.currentMosaic) {
      colorBreakdownDiv.innerHTML = '<em>No mosaic generated yet.</em>';
      return;
    }

    const colorCounts = this.getColorCountsFromMosaic();

    if (colorCounts.length > 0) {
      colorBreakdownDiv.innerHTML = `
        <table class="color-breakdown-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Color</th>
              <th>Name</th>
              <th>Count</th>
            </tr>
          </thead>
          <tbody>
            ${colorCounts.map(({ color, count }, idx) =>
              `<tr>
                <td>${idx + 1}</td>
                <td>
                  <span class="color-swatch" style="background:${color.hex};"></span>
                </td>
                <td>${color.name}</td>
                <td>${count}</td>
              </tr>`
            ).join('')}
          </tbody>
        </table>`;
    } else {
      colorBreakdownDiv.innerHTML = '<em>No mosaic generated yet.</em>';
    }
  }

  setupButtons() {
    this.setupCopyButton();
    this.setupExportButton();
    this.setupInstructionsButton();
  }

  setupCopyButton() {
    const copyBtn = this.elements.copySummaryBtn;
    if (!copyBtn) return;
    
    copyBtn.onclick = () => {
      const gridSize = this.elements.summaryDimensions?.textContent || '';
      const baseplate = this.elements.summaryBaseplate?.textContent || '';
      const totalStuds = this.elements.summaryTotalBricks?.textContent || '';
      
      let text = `Dimensions: ${gridSize}\nBaseplate: ${baseplate}\nTotal Bricks: ${totalStuds}\nColor Breakdown:`;
      
      // Use shared color counting method
      const colorCounts = this.getColorCountsFromMosaic();
      colorCounts.forEach(({ color, count }) => {
        text += `\n${color.name}: ${count}`;
      });
      
      navigator.clipboard.writeText(text);
      copyBtn.textContent = 'Copied!';
      
      if (this.copyFeedbackTimer) {
        clearTimeout(this.copyFeedbackTimer);
      }
      this.copyFeedbackTimer = setTimeout(() => { 
        copyBtn.textContent = 'Copy Summary';
        this.copyFeedbackTimer = null;
      }, 1200);
    };
  }

  setupExportButton() {
    const exportBtn = this.elements.exportPartsBtn;
    if (!exportBtn) return;
    
    exportBtn.onclick = () => {
      let csv = 'Color,Count\n';
      
      // Use shared color counting method
      const colorCounts = this.getColorCountsFromMosaic();
      colorCounts.forEach(({ color, count }) => {
        csv += `"${color.name}",${count}\n`;
      });
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'brick-mosaic-parts-list.csv';
      document.body.appendChild(a);
      a.click();
      
      if (this.downloadCleanupTimer) {
        clearTimeout(this.downloadCleanupTimer);
      }
      this.downloadCleanupTimer = setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        this.downloadCleanupTimer = null;
      }, 100);
    };
  }

  setupInstructionsButton() {
    const instructionsBtn = this.elements.downloadInstructionsBtn;
    if (!instructionsBtn) return;
    
    instructionsBtn.onclick = async () => {
      try {
        const gridSize = this.elements.summaryDimensions?.textContent || '';
        const baseplate = this.elements.summaryBaseplate?.textContent || '';
        
        // Use shared color counting method
        const colorCounts = this.getColorCountsFromMosaic();
        
        const blob = await this.generateAssemblyInstructions(colorCounts, gridSize, baseplate);
        
        // Download the blob
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `brick-mosaic-instructions-${gridSize.replace(/\s+/g, '-')}-for-screen-view.png`;
        document.body.appendChild(a);
        a.click();
        
        if (this.downloadCleanupTimer) {
          clearTimeout(this.downloadCleanupTimer);
        }
        this.downloadCleanupTimer = setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          this.downloadCleanupTimer = null;
        }, 100);
      } catch (error) {
        debugLog('ERROR', 'Error generating instructions:', error);
        alert('Error generating instructions. Please try again.');
      }
    };
  }

  async generateAssemblyInstructions(colorCounts, gridSize, baseplate) {
    if (!window.mosaicApp?.mosaicRenderer?.mosaicCanvas || !colorCounts.length) {
      throw new Error('No mosaic available for instructions generation.');
    }

    // Route 32x32 and 48x48 to the new method
    if (gridSize === '32 × 32' || gridSize === '48 × 48') {
      return this.generateAssemblyInstructionsSplit(colorCounts, gridSize, baseplate);
    }

    return new Promise((resolve, reject) => {
      const logoUrl = document.getElementById('project-summary')?.dataset?.logoUrl;

      const logoImg = new Image();
      logoImg.onload = () => {
        try {
          const blob = this.generateInstructionsWithLogo(colorCounts, gridSize, baseplate, logoImg);
          resolve(blob);
        } catch (error) {
          reject(error);
        }
      };
      logoImg.onerror = () => {
        try {
          const blob = this.generateInstructionsWithLogo(colorCounts, gridSize, baseplate, null);
          resolve(blob);
        } catch (error) {
          reject(error);
        }
      };
      if (logoUrl) {
        logoImg.src = logoUrl;
      } else {
        // Fallback to default logo if no Shopify logo is available
        logoImg.src = 'assets/stereo-hoare-logo.png';
      }
    });
  }

  generateInstructionsWithLogo(colorCounts, gridSize, baseplate, logoImg) {
    // Clean up previous canvas
    this.cleanupDynamicCanvas();
    
    // Create new canvas and track for cleanup
    this.dynamicCanvas = document.createElement('canvas');
    this.dynamicCtx = this.dynamicCanvas.getContext('2d');
    const instructionsCanvas = this.dynamicCanvas;
    const ctx = this.dynamicCtx;

    const headingFont = getComputedStyle(document.documentElement).getPropertyValue('--font-body-family').trim();
    
    const mosaicCanvas = window.mosaicApp.mosaicRenderer.mosaicCanvas;
    const grid = window.mosaicApp.mosaicRenderer.currentMosaic;
    
    // Check if grid is valid
    if (!grid || !Array.isArray(grid) || grid.length === 0) {
      throw new Error('No mosaic data available for instructions generation');
    }
    
    // SIMPLE: Just check the actual state directly
    const state = this.stateManager.getState();
    const isFramed = state.baseplate === 'Framed';
    const frameWidth = isFramed ? 2 : 0;
    
    // SIMPLE: Always use mosaic dimensions, add frame if needed
    const mosaicWidth = grid[0]?.length || 0;
    const mosaicHeight = grid.length;
    const studsX = mosaicWidth + (frameWidth * 2);
    const studsY = mosaicHeight + (frameWidth * 2);
    
    const coordinateSpace = 40;
    const topMargin = 500;
    const rightMargin = 400;
    const bottomMargin = 40;
    const canvasScaleFactor = 4; // Increased for better resolution
    
    // Calculate minimum width needed for instructions (3500px + margins)
    const minInstructionWidth = 3500 + coordinateSpace + rightMargin;
    const mosaicBasedWidth = (mosaicCanvas.width * canvasScaleFactor) + coordinateSpace + rightMargin;
    
    // Calculate minimum height needed for color legend
    const legendStartY = topMargin + 20;
    const legendItemHeight = 45;
    const legendHeight = 40 + (colorCounts.length * legendItemHeight);
    const minLegendHeight = legendStartY + legendHeight + bottomMargin;
    
    const mosaicBasedHeight = (mosaicCanvas.height * canvasScaleFactor) + coordinateSpace + topMargin + bottomMargin;
    
    // Use the larger of the two widths and heights to ensure everything fits properly
    instructionsCanvas.width = Math.max(mosaicBasedWidth, minInstructionWidth);
    instructionsCanvas.height = Math.max(mosaicBasedHeight, minLegendHeight);
    
    // Fill with white background (like v1)
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, instructionsCanvas.width, instructionsCanvas.height);
    
    const studW = (mosaicCanvas.width * canvasScaleFactor) / studsX;
    const studH = (mosaicCanvas.height * canvasScaleFactor) / studsY;
    
    const colorNumberMap = new Map();
    colorCounts.forEach(({ color }, idx) => {
      // Use RGB values as key to match the counting logic in getColorCountsFromMosaic
      const colorKey = `${color.rgb[0]},${color.rgb[1]},${color.rgb[2]}`;
      colorNumberMap.set(colorKey, idx + 1);
    });
    
    this.drawMosaicGrid(ctx, grid, studsX, studsY, studW, studH, coordinateSpace, topMargin, isFramed, frameWidth, colorNumberMap);
    this.drawHeader(ctx, logoImg, rightMargin, headingFont);
    this.drawInstructions(ctx, instructionsCanvas.width, rightMargin, rightMargin);
    this.drawColorLegend(ctx, colorCounts, instructionsCanvas.width, rightMargin, topMargin, headingFont);
    this.drawGridCoordinates(ctx, studsX, studsY, studW, studH, coordinateSpace, topMargin, isFramed, frameWidth);
    
    // Return a promise that resolves with the blob
    return new Promise((resolve, reject) => {
      instructionsCanvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to generate blob from canvas'));
        }
        
        // Clean up canvas after blob generation
        this.cleanupDynamicCanvas();
      }, 'image/png');
    });
  }

  async generateAssemblyInstructionsSplit(colorCounts, gridSize, baseplate) {
    if (!window.mosaicApp?.mosaicRenderer?.mosaicCanvas || !colorCounts.length) {
      throw new Error('No mosaic available for instructions generation.');
    }

    return new Promise((resolve, reject) => {
      const logoUrl = document.getElementById('project-summary')?.dataset?.logoUrl;

      const logoImg = new Image();
      logoImg.onload = () => {
        try {
          const blob = this.generateInstructionsSplit(colorCounts, gridSize, baseplate, logoImg);
          resolve(blob);
        } catch (error) {
          reject(error);
        }
      };
      logoImg.onerror = () => {
        try {
          const blob = this.generateInstructionsSplit(colorCounts, gridSize, baseplate, null);
          resolve(blob);
        } catch (error) {
          reject(error);
        }
      };
      if (logoUrl) {
        logoImg.src = logoUrl;
      } else {
        // Fallback to default logo if no Shopify logo is available
        logoImg.src = 'assets/stereo-hoare-logo.png';
      }
    });
  }

  generateInstructionsSplit(colorCounts, gridSize, baseplate, logoImg) {
    // Clean up previous canvas
    this.cleanupDynamicCanvas();
    
    // Create new canvas and track for cleanup
    this.dynamicCanvas = document.createElement('canvas');
    this.dynamicCtx = this.dynamicCanvas.getContext('2d');
    const instructionsCanvas = this.dynamicCanvas;
    const ctx = this.dynamicCtx;

    const headingFont = getComputedStyle(document.documentElement).getPropertyValue('--font-body-family').trim();
    
    const mosaicCanvas = window.mosaicApp.mosaicRenderer.mosaicCanvas;
    const grid = window.mosaicApp.mosaicRenderer.currentMosaic;
    
    // Check if grid is valid
    if (!grid || !Array.isArray(grid) || grid.length === 0) {
      throw new Error('No mosaic data available for instructions generation');
    }
    
    // SIMPLE: Just check the actual state directly
    const state = this.stateManager.getState();
    const isFramed = state.baseplate === 'Framed';
    const frameWidth = isFramed ? 2 : 0;
    
    // SIMPLE: Always use mosaic dimensions, add frame if needed
    const mosaicWidth = grid[0]?.length || 0;
    const mosaicHeight = grid.length;
    const studsX = mosaicWidth + (frameWidth * 2);
    const studsY = mosaicHeight + (frameWidth * 2);
    
    // Determine quadrant size based on grid size
    const quadrantSize = gridSize === '32 × 32' ? 16 : 24; // 16x16 for 32x32, 24x24 for 48x48
    
    // A4 landscape dimensions at 300 DPI: 297mm × 210mm = 3508px × 2480px
    // Using a reasonable size that maintains aspect ratio for web/print
    const A4_LANDSCAPE_ASPECT_RATIO = 297 / 210; // ≈ 1.414
    const A4_LANDSCAPE_HEIGHT = 2480 + 400; // Increased by 350px 
    const A4_LANDSCAPE_WIDTH = A4_LANDSCAPE_HEIGHT * A4_LANDSCAPE_ASPECT_RATIO; // Maintain aspect ratio ≈ 3932px
    
    // Define padding (simple: top, right, bottom, left)
    const paddingTop = 1;
    const paddingRight = 20;
    const paddingBottom = 40;
    const paddingLeft = 20;
    
    // Coordinate space for row/column numbers (fixed, not scaled)
    const coordinateSpace = 40;
    
    // Each quadrant is quadrantSize x quadrantSize (excluding frame)
    const quadrantMosaicSize = quadrantSize; // Mosaic size per quadrant
    // For split mosaic, frame is only on outer perimeter, not on inner edges
    // So each quadrant only needs frame space on 2 sides (the outer edges)
    // But we use the same stud dimensions for all quadrants for consistency
    // The actual frame drawing will be conditional based on quadrant position
    const quadrantStudsX = quadrantMosaicSize + frameWidth; // Frame on one side only (left or right)
    const quadrantStudsY = quadrantMosaicSize + frameWidth; // Frame on one side only (top or bottom)
    
    // Calculate scale based on height (the limiting factor in landscape mode)
    // Use a temporary height to calculate scale, then we'll adjust canvas height
    const tempAvailableHeight = A4_LANDSCAPE_HEIGHT - paddingTop - paddingBottom;
    const quadrantAreaHeight = (tempAvailableHeight - coordinateSpace) / 2; // Space for 2 quadrants with spacing
    
    // Calculate scale based on quadrant size
    const originalQuadrantWidth = (mosaicCanvas.width / mosaicWidth) * quadrantMosaicSize;
    const originalQuadrantHeight = (mosaicCanvas.height / mosaicHeight) * quadrantMosaicSize;
    const originalQuadrantContentHeight = originalQuadrantHeight + coordinateSpace;
    
    // Height is the limiting factor, so calculate scale from height only
    const contentScale = quadrantAreaHeight / originalQuadrantContentHeight;
    
    // Calculate scaled quadrant dimensions
    const scaledQuadrantWidth = originalQuadrantWidth * contentScale;
    const scaledQuadrantHeight = originalQuadrantHeight * contentScale;
    // Coordinate space stays fixed (not scaled)
    const scaledCoordinateSpace = coordinateSpace;
    
    // Calculate stud dimensions for each quadrant
    // Note: stud dimensions are based on quadrantStudsX/Y which only include frame on one side
    const studW = scaledQuadrantWidth / quadrantStudsX;
    const studH = scaledQuadrantHeight / quadrantStudsY;
    
    // Calculate positions for 2x2 grid of quadrants to determine actual content height
    const quadrantWithSpacingWidth = scaledQuadrantWidth + scaledCoordinateSpace;
    const quadrantWithSpacingHeight = scaledQuadrantHeight + scaledCoordinateSpace;
    const startX = paddingLeft + scaledCoordinateSpace;
    const startY = paddingTop + scaledCoordinateSpace;
    
    // Calculate actual content bottom (mosaic bottom + padding)
    const actualMosaicBottomY = startY + quadrantWithSpacingHeight + scaledCoordinateSpace + scaledQuadrantHeight;
    const actualContentHeight = actualMosaicBottomY + paddingBottom;
    
    // Set canvas dimensions: use actual content height, maintain A4 aspect ratio for width
    instructionsCanvas.width = actualContentHeight * A4_LANDSCAPE_ASPECT_RATIO;
    instructionsCanvas.height = actualContentHeight;
    
    // Fill with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, instructionsCanvas.width, instructionsCanvas.height);
    
    const colorNumberMap = new Map();
    colorCounts.forEach(({ color }, idx) => {
      // Use RGB values as key to match the counting logic in getColorCountsFromMosaic
      const colorKey = `${color.rgb[0]},${color.rgb[1]},${color.rgb[2]}`;
      colorNumberMap.set(colorKey, idx + 1);
    });
    
    // Positions already calculated above
    
    // Draw 4 quadrants in 2x2 layout
    // Top-left: rows 0 to quadrantSize-1, cols 0 to quadrantSize-1
    // Top-right: rows 0 to quadrantSize-1, cols quadrantSize to 2*quadrantSize-1
    // Bottom-left: rows quadrantSize to 2*quadrantSize-1, cols 0 to quadrantSize-1
    // Bottom-right: rows quadrantSize to 2*quadrantSize-1, cols quadrantSize to 2*quadrantSize-1
    
    const quadrants = [
      { rowStart: 0, rowEnd: quadrantSize, colStart: 0, colEnd: quadrantSize, x: startX, y: startY, label: 'A' },
      { rowStart: 0, rowEnd: quadrantSize, colStart: quadrantSize, colEnd: quadrantSize * 2, x: startX + quadrantWithSpacingWidth, y: startY, label: 'B' },
      { rowStart: quadrantSize, rowEnd: quadrantSize * 2, colStart: 0, colEnd: quadrantSize, x: startX, y: startY + quadrantWithSpacingHeight, label: 'C' },
      { rowStart: quadrantSize, rowEnd: quadrantSize * 2, colStart: quadrantSize, colEnd: quadrantSize * 2, x: startX + quadrantWithSpacingWidth, y: startY + quadrantWithSpacingHeight, label: 'D' }
    ];
    
    quadrants.forEach((quad) => {
      // Extract quadrant grid data
      const quadrantGrid = [];
      for (let row = quad.rowStart; row < quad.rowEnd; row++) {
        const rowData = [];
        for (let col = quad.colStart; col < quad.colEnd; col++) {
          rowData.push(grid[row]?.[col] || null);
        }
        quadrantGrid.push(rowData);
      }
      
      // Determine which edges should have frames (only outer edges of entire mosaic)
      // Top-left (A): top and left edges
      // Top-right (B): top and right edges
      // Bottom-left (C): bottom and left edges
      // Bottom-right (D): bottom and right edges
      const isTopRow = quad.rowStart === 0;
      const isBottomRow = quad.rowStart === quadrantSize;
      const isLeftCol = quad.colStart === 0;
      const isRightCol = quad.colStart === quadrantSize;
      
      const frameEdges = {
        top: isTopRow,
        bottom: isBottomRow,
        left: isLeftCol,
        right: isRightCol
      };
      
      // Pass quadrant position info to exclude inner corners where quadrants meet
      const quadrantPosition = {
        isTopRow,
        isBottomRow,
        isLeftCol,
        isRightCol,
        quadrantSize
      };
      
      // Draw this quadrant
      this.drawMosaicGridQuadrant(ctx, quadrantGrid, quadrantStudsX, quadrantStudsY, studW, studH, quad.x, quad.y, isFramed, frameWidth, colorNumberMap, scaledCoordinateSpace, frameEdges, quadrantPosition);
      
      // Draw coordinates for this quadrant
      this.drawGridCoordinatesQuadrant(ctx, quadrantStudsX, quadrantStudsY, studW, studH, quad.x, quad.y, quad.rowStart, quad.colStart, isFramed, frameWidth, scaledCoordinateSpace, contentScale, frameEdges);
    });
    
    // Calculate total mosaic area for legend positioning
    const totalMosaicWidth = quadrantWithSpacingWidth * 2;
    // Height: 2 quadrants + 1 coordinateSpace between them (not at bottom)
    const totalMosaicHeight = (scaledQuadrantHeight * 2) + scaledCoordinateSpace;
    const mosaicStartY = startY;
    // The actual grid cells start at startY + coordinateSpace
    const actualGridStartY = startY + scaledCoordinateSpace; // Grid cells start here
    // Bottom of mosaic grid cells:
    // - Bottom row quadrant starts at: startY + quadrantWithSpacingHeight
    // - Within quadrant, grid cells start at: coordinateSpace + offsetY = scaledCoordinateSpace + (startY + quadrantWithSpacingHeight)
    // - Grid cells end at: startY + quadrantWithSpacingHeight + scaledCoordinateSpace + scaledQuadrantHeight
    // Which equals: startY + scaledQuadrantHeight + scaledCoordinateSpace + scaledCoordinateSpace + scaledQuadrantHeight
    // Which equals: startY + (scaledQuadrantHeight * 2) + (scaledCoordinateSpace * 2)
    // But we want the bottom of the actual drawn cells, not including the coordinate space area
    // The last row of cells ends at: startY + quadrantWithSpacingHeight + scaledCoordinateSpace + scaledQuadrantHeight
    // actualMosaicBottomY was already calculated above
    const mosaicBottomY = actualMosaicBottomY; // Use actual bottom for logo alignment
    
    // No instructions text for 32x32 and 48x48
    // Pass actualGridStartY - 20 as topMargin since drawColorLegend adds +20, so legend aligns with grid start
    const legendInfo = this.drawColorLegend(ctx, colorCounts, instructionsCanvas.width, paddingRight, actualGridStartY - 20, headingFont, totalMosaicWidth, scaledCoordinateSpace);
    // Logo left-aligned at bottom of mosaic
    this.drawLogoAtBottomRight(ctx, logoImg, legendInfo.legendX, mosaicBottomY, totalMosaicWidth, scaledCoordinateSpace, contentScale);
    
    // Return a promise that resolves with the blob
    return new Promise((resolve, reject) => {
      instructionsCanvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to generate blob from canvas'));
        }
        
        // Clean up canvas after blob generation
        this.cleanupDynamicCanvas();
      }, 'image/png');
    });
  }

  drawMosaicGrid(ctx, grid, studsX, studsY, studW, studH, coordinateSpace, topMargin, isFramed, frameWidth, colorNumberMap) {
    const studSpacing = 2;
    
    for (let row = 0; row < studsY; row++) {
      for (let col = 0; col < studsX; col++) {
        const x = col * studW + coordinateSpace;
        const y = row * studH + coordinateSpace + topMargin;
        const spacing = studSpacing;
        const studWidth = studW - spacing;
        const studHeight = studH - spacing;
        
        // SIMPLE: If framed and this is a frame cell, draw black
        if (isFramed && (row < frameWidth || row >= studsY - frameWidth || col < frameWidth || col >= studsX - frameWidth)) {
          ctx.fillStyle = '#000000';
          ctx.fillRect(x + spacing / 2, y + spacing / 2, studWidth, studHeight);
        } else {
          // SIMPLE: Draw mosaic cell
          const mosaicRow = row - frameWidth;
          const mosaicCol = col - frameWidth;
          const cellColor = grid[mosaicRow]?.[mosaicCol];
          
          if (cellColor) {
            const hex = this.rgbToHex(cellColor.r, cellColor.g, cellColor.b);
            ctx.fillStyle = hex;
            ctx.fillRect(x + spacing / 2, y + spacing / 2, studWidth, studHeight);
            
            if (hex === '#FFFFFF') {
              ctx.strokeStyle = '#000000';
              ctx.lineWidth = 1;
              ctx.strokeRect(x + spacing / 2, y + spacing / 2, studWidth, studHeight);
            }
            
            const colorKey = `${cellColor.r},${cellColor.g},${cellColor.b}`;
            const number = colorNumberMap.get(colorKey);
            if (number) {
              ctx.fillStyle = this.getTextColor(hex);
              const minStudSize = Math.min(studWidth, studHeight);
              const fontSize = Math.max(16, Math.min(minStudSize * 0.6, minStudSize * 0.8));
              ctx.font = `${fontSize}px "Roboto Condensed", Arial, sans-serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(number.toString(), x + studW / 2, y + studH / 2);
            }
          }
        }
      }
    }
  }

  drawMosaicGridQuadrant(ctx, quadrantGrid, studsX, studsY, studW, studH, offsetX, offsetY, isFramed, frameWidth, colorNumberMap, scaledCoordinateSpace, frameEdges = null, quadrantPosition = null) {
    const studSpacing = 2;
    const coordinateSpace = scaledCoordinateSpace || 40; // Use scaled coordinate space if provided
    
    for (let row = 0; row < studsY; row++) {
      for (let col = 0; col < studsX; col++) {
        const x = col * studW + coordinateSpace + offsetX;
        const y = row * studH + coordinateSpace + offsetY;
        const spacing = studSpacing;
        const studWidth = studW - spacing;
        const studHeight = studH - spacing;
        
        // Check if this is a frame cell
        // For split mosaic, frame is only on outer edges (2 sides per quadrant)
        // studsX = quadrantMosaicSize + frameWidth (frame on one side only)
        // studsY = quadrantMosaicSize + frameWidth (frame on one side only)
        let isFrameCell = false;
        if (isFramed && frameEdges) {
          // With the new dimensions, frame is on one side only:
          // - Left edge: frame at col [0, frameWidth), mosaic at col [frameWidth, studsX)
          // - Right edge: frame at col [studsX - frameWidth, studsX), mosaic at col [0, studsX - frameWidth)
          // - Top edge: frame at row [0, frameWidth), mosaic at row [frameWidth, studsY)
          // - Bottom edge: frame at row [studsY - frameWidth, studsY), mosaic at row [0, studsY - frameWidth)
          
          // Top edge: frame cells in top frameWidth rows, only if this quadrant has top edge
          const isTopEdge = row < frameWidth && frameEdges.top;
          
          // Bottom edge: frame cells in bottom frameWidth rows, only if this quadrant has bottom edge
          const isBottomEdge = row >= studsY - frameWidth && frameEdges.bottom;
          
          // Left edge: frame cells in left frameWidth columns, only if this quadrant has left edge
          const isLeftEdge = col < frameWidth && frameEdges.left;
          
          // Right edge: frame cells in right frameWidth columns, only if this quadrant has right edge
          const isRightEdge = col >= studsX - frameWidth && frameEdges.right;
          
          // Outside corners: only draw if both edges are outer edges
          const isTopLeftCorner = row < frameWidth && col < frameWidth && frameEdges.top && frameEdges.left;
          const isTopRightCorner = row < frameWidth && col >= studsX - frameWidth && frameEdges.top && frameEdges.right;
          const isBottomLeftCorner = row >= studsY - frameWidth && col < frameWidth && frameEdges.bottom && frameEdges.left;
          const isBottomRightCorner = row >= studsY - frameWidth && col >= studsX - frameWidth && frameEdges.bottom && frameEdges.right;
          
          isFrameCell = isTopEdge || isBottomEdge || isLeftEdge || isRightEdge || 
                       isTopLeftCorner || isTopRightCorner || isBottomLeftCorner || isBottomRightCorner;
        } else if (isFramed) {
          // Fallback: draw frame on all edges (for backward compatibility)
          isFrameCell = (row < frameWidth || row >= studsY - frameWidth || col < frameWidth || col >= studsX - frameWidth);
        }
        
        if (isFrameCell) {
          ctx.fillStyle = '#000000';
          ctx.fillRect(x + spacing / 2, y + spacing / 2, studWidth, studHeight);
        } else {
          // Draw mosaic cell
          // Mosaic cells are offset by frameWidth if frame is on left/top, or not offset if frame is on right/bottom
          let mosaicRow, mosaicCol;
          if (frameEdges) {
            // If frame is on left, mosaic starts at col = frameWidth
            // If frame is on right, mosaic starts at col = 0
            mosaicCol = frameEdges.left ? col - frameWidth : col;
            // If frame is on top, mosaic starts at row = frameWidth
            // If frame is on bottom, mosaic starts at row = 0
            mosaicRow = frameEdges.top ? row - frameWidth : row;
          } else {
            // Fallback: assume frame on left and top
            mosaicRow = row - frameWidth;
            mosaicCol = col - frameWidth;
          }
          const cellColor = quadrantGrid[mosaicRow]?.[mosaicCol];
          
          if (cellColor) {
            const hex = this.rgbToHex(cellColor.r, cellColor.g, cellColor.b);
            ctx.fillStyle = hex;
            ctx.fillRect(x + spacing / 2, y + spacing / 2, studWidth, studHeight);
            
            if (hex === '#FFFFFF') {
              ctx.strokeStyle = '#000000';
              ctx.lineWidth = 1;
              ctx.strokeRect(x + spacing / 2, y + spacing / 2, studWidth, studHeight);
            }
            
            const colorKey = `${cellColor.r},${cellColor.g},${cellColor.b}`;
            const number = colorNumberMap.get(colorKey);
            if (number) {
              ctx.fillStyle = this.getTextColor(hex);
              const minStudSize = Math.min(studWidth, studHeight);
              const fontSize = Math.max(16, Math.min(minStudSize * 0.6, minStudSize * 0.8));
              ctx.font = `${fontSize}px "Roboto Condensed", Arial, sans-serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(number.toString(), x + studW / 2, y + studH / 2);
            }
          }
        }
      }
    }
  }

  drawGridCoordinatesQuadrant(ctx, studsX, studsY, studW, studH, offsetX, offsetY, rowOffset, colOffset, isFramed, frameWidth, scaledCoordinateSpace, contentScale, frameEdges = null) {
    ctx.fillStyle = '#858585';
    // Fixed font size for coordinates (not scaled)
    const fontSize = 16;
    ctx.font = `${fontSize}px "Roboto Condensed", Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    const coordinateSpace = scaledCoordinateSpace || 40; // Use scaled coordinate space if provided
    
    // Calculate mosaic area based on frame position
    // With new dimensions: studsX = quadrantMosaicSize + frameWidth (frame on one side only)
    let mosaicStartX, mosaicEndX, mosaicStartY, mosaicEndY;
    if (isFramed && frameEdges) {
      // If frame is on left, mosaic starts at frameWidth
      // If frame is on right, mosaic starts at 0
      mosaicStartX = frameEdges.left ? frameWidth : 0;
      mosaicEndX = frameEdges.left ? studsX : studsX - frameWidth;
      
      // If frame is on top, mosaic starts at frameWidth
      // If frame is on bottom, mosaic starts at 0
      mosaicStartY = frameEdges.top ? frameWidth : 0;
      mosaicEndY = frameEdges.top ? studsY : studsY - frameWidth;
    } else {
      // Fallback: assume frame on left and top
      mosaicStartX = frameWidth;
      mosaicEndX = studsX - frameWidth;
      mosaicStartY = frameWidth;
      mosaicEndY = studsY - frameWidth;
    }
    
    // Draw column numbers (only for mosaic area, starting from colOffset + 1)
    for (let col = mosaicStartX; col < mosaicEndX; col++) {
      const x = col * studW + coordinateSpace + offsetX + studW / 2;
      const y = coordinateSpace + offsetY - 10;
      // Calculate mosaic column number: if frame on left, subtract frameWidth; if on right, no offset needed
      const colOffsetInQuadrant = frameEdges && frameEdges.left ? frameWidth : 0;
      const mosaicColNumber = col - colOffsetInQuadrant + colOffset + 1; // Start from colOffset + 1
      ctx.fillText(mosaicColNumber.toString(), x, y);
    }
    
    // Draw row numbers (only for mosaic area, starting from rowOffset + 1)
    for (let row = mosaicStartY; row < mosaicEndY; row++) {
      const x = coordinateSpace + offsetX - 20;
      const y = row * studH + coordinateSpace + offsetY + studH / 2;
      // Calculate mosaic row number: if frame on top, subtract frameWidth; if on bottom, no offset needed
      const rowOffsetInQuadrant = frameEdges && frameEdges.top ? frameWidth : 0;
      const mosaicRowNumber = row - rowOffsetInQuadrant + rowOffset + 1; // Start from rowOffset + 1
      ctx.fillText(mosaicRowNumber.toString(), x, y);
    }
  }

  drawHeader(ctx, logoImg, rightMargin, headingFont) {
    const leftColumnWidth = this.LOGO_CONTAINER_WIDTH;
    const rightColumnX = leftColumnWidth + 20;
    
    if (logoImg) {
      // Calculate dimensions to maintain aspect ratio
      const maxWidth = 360;
      const maxHeight = 360;
      
      let drawWidth = logoImg.naturalWidth;
      let drawHeight = logoImg.naturalHeight;
      
      // Scale down if image is too large while maintaining aspect ratio
      if (drawWidth > maxWidth || drawHeight > maxHeight) {
        const scaleX = maxWidth / drawWidth;
        const scaleY = maxHeight / drawHeight;
        const scale = Math.min(scaleX, scaleY);
        
        drawWidth = drawWidth * scale;
        drawHeight = drawHeight * scale;
      }
      
      ctx.drawImage(logoImg, 20, 20, drawWidth, drawHeight);
    } else {
      ctx.fillStyle = '#000000';
      ctx.font = `bold 50px ${headingFont || '"Anonymous Pro", monospace'}`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText('STEREO HOARE', 20, 20);
    }
    
    ctx.fillStyle = '#000000';
    ctx.font = `bold 90px ${headingFont || '"Anonymous Pro", monospace'}`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText('CUSTOM INSTRUCTIONS', rightColumnX, 20);
  }

  drawInstructions(ctx, canvasWidth, rightMargin, rightColumnX) {
    const instructions = {
      whatYouNeed: [
        'What you need:',
        'A clean, flat surface',
        '',
        'Good lighting',
        '',
        'The bricks we sent you',
        '',
        'A screen to display these instructions',
        '',
        'A little patience',
        '',
        'Your favourite playlist'
      ],
      letsBuild: [
        'Lets build:',
        'Just match the colour and position from the diagram to the baseplate and watch your image come to life brick by brick.',
        '',
        'We recommend using the most common colours first, but don\'t worry—there\'s no wrong way to build. Just have fun with it!',
        '',
        'Take breaks, enjoy the process, and listen to your favourite music. Building brick mosaics is meant to be meditative and relaxing.',
        '',
        'Some spare bricks will be provided, so don\'t worry if you\'ve placed all the bricks of one colour and still have some leftover.',
        '',
        'For the best experience, view these instructions on a screen rather than printing them. Don\'t forget you can zoom in and pan around to see every detail clearly.'
      ],
      showItOff: [
        'Show it off:',
        'When you\'re done, we\'d',
        'love to see your build!',
        '',
        'Tag us on Instagram',
        '@stereohoare'
      ],
      needHelp: [
        'Need Help?',
        'Got questions or missing bricks?',
        '',
        'Feel free to reach out on Instagram @stereohoare or send us an email on andrew@stereohoare.com and we will help you in any way we can.'
      ]
    };

    // Fixed widths instead of responsive calculation
    const columnSpacing = 20;
    const instructionRowWidth = 3500; // Fixed width for the entire instructions row
    const columnWidths = [0.2, 0.55, 0.10, 0.15]; // Percentage distribution: What you need, Let's build, Show it off, Need Help
    const bulletColumns = [0]; // Columns that use bullet points (0-indexed)
    
    // Calculate available width from the fixed instruction row width
    const totalColumnSpacing = columnSpacing * 3; // 3 spaces between 4 columns
    const availableWidth = instructionRowWidth - totalColumnSpacing;
    
    // Left align the instructions row to start at the left edge of the right column
    const startX = rightColumnX + 20;
    
    const columns = [];
    let currentX = startX;
    
    columnWidths.forEach((widthPercent, index) => {
      const isBulletColumn = bulletColumns.includes(index);
      const actualWidth = availableWidth * widthPercent;
      columns.push({
        width: actualWidth,
        x: currentX,
        isBulletColumn: isBulletColumn
      });
      currentX += actualWidth + columnSpacing;
    });

    const instructionKeys = ['whatYouNeed', 'letsBuild', 'showItOff', 'needHelp'];
    
    instructionKeys.forEach((key, columnIndex) => {
      // Enable text wrapping for all columns since they're all narrow
      this.drawInstructionColumn(ctx, instructions[key], columns[columnIndex], true);
    });
  }

  drawInstructionColumn(ctx, lines, column, isWideColumn) {
    let yPos = 120; // Increased from 80 to add space after the "CUSTOM INSTRUCTIONS" title
    
    lines.forEach((line, index) => {
      if (line === '') {
        yPos += 15;
        return;
      }
      
      const isBold = index === 0;
      let displayText = line;
      let xPos = column.x;
      
      // Add bullet point for bullet columns (except first line)
      if (column.isBulletColumn && index > 0) {
        displayText = line; // Just the text, no bullet
        xPos = column.x; // Both bullet and text start at same position
      }
      
      ctx.font = isBold ? 'bold 30px "Roboto Condensed", Arial, sans-serif' : '30px "Roboto Condensed", Arial, sans-serif';
      
      // Add extra spacing for bold headers
      if (isBold) {
        yPos += 10; // Add top margin for headers
      }
      
      // Draw bullet point first (for bullet columns, except first line and empty lines)
      if (column.isBulletColumn && index > 0 && line !== '') {
        ctx.fillText('•', column.x, yPos);
      }
      
      if (isWideColumn) {
        // Title line (index 0) gets no margin, bullet lines get 20px margin
        const textMargin = (column.isBulletColumn && index > 0) ? 20 : 0;
        yPos = this.drawWrappedText(ctx, displayText, xPos + textMargin, yPos, column.width - textMargin);
      } else {
        // Title line (index 0) gets no margin, bullet lines get 20px margin
        const textMargin = (column.isBulletColumn && index > 0) ? 20 : 0;
        ctx.fillText(displayText, xPos + textMargin, yPos);
        
        yPos += 32; // Increased line height to match v1
      }
      
      // Add extra spacing after bold headers
      if (isBold) {
        yPos += 15; // Add bottom margin for headers
      }
    });
  }

  drawWrappedText(ctx, text, x, y, maxWidth) {
    const words = text.split(' ');
    let line = '';
    let lineY = y;
    
    for (let n = 0; n < words.length; n++) {
      const testLine = line + words[n] + ' ';
      const metrics = ctx.measureText(testLine);
      const testWidth = metrics.width;
      
      if (testWidth > maxWidth && n > 0) {
        ctx.fillText(line, x, lineY);
        line = words[n] + ' ';
        lineY += 32; // Increased line height to match v1
      } else {
        line = testLine;
      }
    }
    ctx.fillText(line, x, lineY);
    return lineY + 32; // Return the new Y position
  }

  drawColorLegend(ctx, colorCounts, canvasWidth, rightMargin, topMargin, headingFont, scaledMosaicWidth, scaledCoordinateSpace) {
    // Calculate mosaic grid dimensions to position legend right next to it
    const mosaicCanvas = window.mosaicApp.mosaicRenderer.mosaicCanvas;
    const canvasScaleFactor = 4;
    const coordinateSpace = 40;
    
    // Use scaled dimensions if provided (for split version), otherwise use defaults
    const mosaicEndX = scaledMosaicWidth !== undefined && scaledCoordinateSpace !== undefined
      ? scaledCoordinateSpace + scaledMosaicWidth
      : coordinateSpace + (mosaicCanvas.width * canvasScaleFactor);
    
    // Position legend right after the mosaic with some spacing
    const spacing = scaledCoordinateSpace !== undefined ? scaledCoordinateSpace : 40;
    const legendX = mosaicEndX + spacing;
    const legendY = topMargin + 20;
    const legendItemHeight = 45;
    
    ctx.textAlign = 'left';
    ctx.font = `bold 30px ${headingFont || '"Anonymous Pro", monospace'}`;
    ctx.fillText('COLORS NEEDED:', legendX, legendY);
    
    ctx.font = '22px "Roboto Condensed", Arial, sans-serif';
    colorCounts.forEach(({ color, count }, idx) => {
      const itemY = legendY + 40 + (idx * legendItemHeight);
      
      this.drawColorLegendItem(ctx, color, count, idx, legendX, itemY);
    });
    
    // Return both the legend Y position (for logo alignment) and the end Y position
    const lastItemY = legendY + 40 + (colorCounts.length * legendItemHeight);
    return { legendY, legendX, endY: lastItemY + legendItemHeight };
  }

  drawLogoAtBottomRight(ctx, logoImg, legendX, mosaicBottomY, scaledMosaicWidth, scaledCoordinateSpace, contentScale) {
    if (!logoImg) return;
    
    // For 32x32 and 48x48 (3248), limit logo width to 200px
    const maxWidth = 300; // Fixed 200px width for split version
    const maxHeight = 300; // Limit height as well
    
    let drawWidth = logoImg.naturalWidth;
    let drawHeight = logoImg.naturalHeight;
    
    // Scale down if image is too large while maintaining aspect ratio
    if (drawWidth > maxWidth || drawHeight > maxHeight) {
      const scaleX = maxWidth / drawWidth;
      const scaleY = maxHeight / drawHeight;
      const scale = Math.min(scaleX, scaleY);
      
      drawWidth = drawWidth * scale;
      drawHeight = drawHeight * scale;
    }
    
    // Position logo left-aligned at the bottom of the mosaic
    // Use the same X position as the legend (left edge of legend)
    const logoX = legendX;
    // Align bottom of logo with bottom of mosaic
    const logoY = mosaicBottomY - drawHeight; // Position so bottom of logo aligns with bottom of mosaic
    
    // Draw logo
    ctx.drawImage(logoImg, logoX, logoY, drawWidth, drawHeight);
  }

  drawColorLegendItem(ctx, color, count, idx, legendX, itemY) {
    const swatchSize = 35; // Increased from 28 to 35
    
    const hexColor = this.rgbToHex(color.rgb[0], color.rgb[1], color.rgb[2]);
    ctx.fillStyle = hexColor;
    ctx.fillRect(legendX, itemY, swatchSize, swatchSize);
    
    if (hexColor === '#FFFFFF') {
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.strokeRect(legendX, itemY, swatchSize, swatchSize);
    }
    
    ctx.fillStyle = this.getTextColor(hexColor);
    ctx.font = 'bold 20px "Roboto Condensed", Arial, sans-serif'; // Increased font size
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText((idx + 1).toString(), legendX + swatchSize/2, itemY + swatchSize/2);
    
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#000000';
    ctx.font = '22px "Roboto Condensed", Arial, sans-serif';
    
    ctx.fillText('×', legendX + swatchSize + 10, itemY + 4); // Adjusted spacing
    
    const numberText = count.toString();
    const numberWidth = ctx.measureText(numberText).width;
    const maxNumberWidth = 60;
    const minSpaceAfterX = 8;
    const numberX = legendX + swatchSize + 10 + minSpaceAfterX + maxNumberWidth - numberWidth;
    ctx.fillText(numberText, numberX, itemY + 4);
    
    ctx.fillText(color.name, legendX + swatchSize + 10 + 8 + 60 + 15, itemY + 4);
  }

  drawGridCoordinates(ctx, studsX, studsY, studW, studH, coordinateSpace, topMargin, isFramed, frameWidth) {
    ctx.fillStyle = '#858585';
    ctx.font = '16px "Roboto Condensed", Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Calculate mosaic area (excluding frame)
    const mosaicStartX = frameWidth;
    const mosaicEndX = studsX - frameWidth;
    const mosaicStartY = frameWidth;
    const mosaicEndY = studsY - frameWidth;
    
    // Draw column numbers (only for mosaic area, starting from 1)
    for (let col = mosaicStartX; col < mosaicEndX; col++) {
      const x = col * studW + coordinateSpace + studW / 2;
      // Position column numbers closer to grid (reduce spacing from 20 to 10)
      const y = coordinateSpace + topMargin - 10;
      const mosaicColNumber = col - frameWidth + 1; // Start from 1, not 0
      ctx.fillText(mosaicColNumber.toString(), x, y);
    }
    
    // Draw row numbers (only for mosaic area, starting from 1)
    for (let row = mosaicStartY; row < mosaicEndY; row++) {
      const x = coordinateSpace - 20;
      const y = row * studH + coordinateSpace + topMargin + studH / 2;
      const mosaicRowNumber = row - frameWidth + 1; // Start from 1, not 0
      ctx.fillText(mosaicRowNumber.toString(), x, y);
    }
  }

  getTextColor(backgroundColor) {
    // Convert hex to RGB
    const hex = backgroundColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black for light backgrounds, white for dark backgrounds
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
  }

  // Cleanup method for timer cleanup
  destroy() {
    // Clean up canvases
    this.cleanupDynamicCanvas();
    
    // Clean up timers
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
    if (this.copyFeedbackTimer) {
      clearTimeout(this.copyFeedbackTimer);
      this.copyFeedbackTimer = null;
    }
    if (this.downloadCleanupTimer) {
      clearTimeout(this.downloadCleanupTimer);
      this.downloadCleanupTimer = null;
    }
  }

  // Clean up dynamically created canvas
  cleanupDynamicCanvas() {
    if (this.dynamicCtx) {
      // Clear the context to free associated resources
      this.dynamicCtx.clearRect(0, 0, this.dynamicCanvas?.width || 0, this.dynamicCanvas?.height || 0);
      this.dynamicCtx = null;
    }
    if (this.dynamicCanvas) {
      // Reset canvas dimensions to free memory
      this.dynamicCanvas.width = 1;
      this.dynamicCanvas.height = 1;
      this.dynamicCanvas = null;
    }
  }
}

// ============================================================================
// MOSAIC IMAGE CAPTURE MANAGER
// ============================================================================

class MosaicImageCaptureManager {
  constructor(stateManager, mosaicRenderer) {
    this.stateManager = stateManager;
    this.mosaicRenderer = mosaicRenderer;
    this.buyButton = null;
    this.prepareMosaicButton = null;
    this.productForm = null;
    this.mosaicCanvas = null;
    this.mosaicImageInput = null;
    this.assemblyInstructionsInput = null;
    
    // Cloudinary configuration (from CONFIG)
    this.cloudinaryConfig = CONFIG.CLOUDINARY;
    // Read upload provider from section data attribute (set by Liquid)
    // Defaults to 'shopify' if not found
    const section = document.querySelector('section[data-section]');
    const cloudinaryEnabled = section?.getAttribute('data-cloudinary-upload') === 'true';
    this.uploadProvider = cloudinaryEnabled ? 'cloudinary' : 'shopify';
    
    // Cache for prepared images
    this.preparedMosaicImageBlob = null;
    this.preparedInstructionsBlob = null;
    
    // PNG generation state
    this.isGeneratingPngs = false;
    
    // Upload completion flags (Cloudinary mode only)
    this.hasUploadedMosaic = false;
    this.hasUploadedInstructions = false;
    this.hasUploadedCustomImage = false;
    
    // Error message element reference
    this.instructionTextElement = null;
    
    // Instruction elements for opacity management
    this.atcInstructionElement = null;
    this.pmInstructionElement = null;
    
    // Intersection Observer for Add to Cart button visibility
    this.intersectionObserver = null;
    
    this.init();
  }

  init() {
    debugLog('INFO', '🟢 MosaicImageCaptureManager: Initializing...');
    this.findElements();
    this.setupEventListeners();
    this.setupMosaicRenderingListener();
    this.setupDebouncedUpdates();
    this.setupIntersectionObserver();
    debugLog('INFO', '🟢 MosaicImageCaptureManager: Initialization complete');
    
    // Initialize button text preservation
    this.initializeButtonText();
  }

  findElements() {
    // Find the Shopify buy button by its ID pattern
    this.buyButton = document.querySelector('[id^="ProductSubmitButton-"]');
    
    // Find the button wrapper for tooltip handling (disabled buttons don't fire events)
    this.buyButtonWrapper = this.buyButton ? this.buyButton.closest('.product-form__quantity-and-btn') : null;
    
    // Find the Prepare Mosaic button
    const sectionElement = document.querySelector('section[data-section]');
    const sectionId = sectionElement ? sectionElement.getAttribute('data-section') : '';
    this.prepareMosaicButton = document.getElementById(`PrepareMosaicButton-${sectionId}`);
    
    // Find instruction elements for opacity management
    this.atcInstructionElement = document.getElementById(`instructions-ProductSubmitButton-${sectionId}`);
    this.pmInstructionElement = document.getElementById(`instructions-PrepareMosaicButton-${sectionId}`);
    
    // Find instruction text element (for error messages)
    if (this.prepareMosaicButton) {
      const buttonWrapper = this.prepareMosaicButton.closest('.buy-buttons-wrapper');
      if (buttonWrapper) {
        this.instructionTextElement = buttonWrapper.querySelector('.button-instruction-text');
      }
    }
    
    // Find the product form
    this.productForm = document.querySelector('form[data-type="add-to-cart-form"]');
    
    // Find the mosaic canvas (current version uses image-edit-canvas)
    this.mosaicCanvas = document.getElementById('image-edit-canvas');
    
    // Find inputs based on upload provider mode
    if (this.uploadProvider === 'cloudinary') {
      // Cloudinary mode: find URL inputs by ID
      this.mosaicImageInput = null;
      this.mosaicImageUrlInput = document.getElementById(`mosaic-image-url-${sectionId}`);
      this.assemblyInstructionsInput = null;
      this.assemblyInstructionsUrlInput = document.getElementById(`assembly-instructions-url-${sectionId}`);
      // Cloudinary mode: file input for selection (#custom-image), URL input for submission (#custom-image-url)
      this.customImageFileInput = document.getElementById('custom-image');
      this.customImageInput = document.getElementById('custom-image-url');
    } else {
      // Shopify CDN mode: find file inputs by ID
      this.mosaicImageInput = document.getElementById(`mosaic-image-data-${sectionId}`);
      this.assemblyInstructionsInput = document.getElementById(`assembly-instructions-${sectionId}`);
      this.customImageInput = document.getElementById('custom-image'); // File input for Shopify CDN
      this.mosaicImageUrlInput = null;
      this.assemblyInstructionsUrlInput = null;
      this.customImageFileInput = null; // Not used in Shopify CDN mode
    }
    
    debugLog('INFO', 'Mosaic image capture elements found:', {
      buyButton: !!this.buyButton,
      prepareMosaicButton: !!this.prepareMosaicButton,
      productForm: !!this.productForm,
      mosaicCanvas: !!this.mosaicCanvas,
      mosaicImageInput: !!this.mosaicImageInput,
      mosaicImageUrlInput: !!this.mosaicImageUrlInput,
      assemblyInstructionsInput: !!this.assemblyInstructionsInput,
      assemblyInstructionsUrlInput: !!this.assemblyInstructionsUrlInput,
      customImageInput: !!this.customImageInput,
      uploadProvider: this.uploadProvider
    });
  }

  updateInputNames() {
    const state = this.stateManager.getState();
    const gridSize = state.gridSizeString;
    const baseplate = state.baseplate ? 'Framed' : 'Regular';
    
    if (gridSize && this.mosaicImageInput) {
      const dynamicName = `properties[Your custom ${gridSize} mosaic]`;
      this.mosaicImageInput.name = dynamicName;
    }
    
    if (gridSize && this.mosaicImageUrlInput) {
      const dynamicName = `properties[Your custom ${gridSize} mosaic]`;
      this.mosaicImageUrlInput.name = dynamicName;
    }
    
    if (baseplate && this.assemblyInstructionsInput && gridSize) {
      // Use underscore prefix to hide in cart/checkout if flag is false
      const prefix = CONFIG.SHOW_ASSEMBLY_INSTRUCTIONS_IN_CART ? '' : '_';
      const dynamicName = `properties[${prefix}Assembly instructions for your custom ${gridSize} mosaic]`;
      this.assemblyInstructionsInput.name = dynamicName;
    }
    
    if (baseplate && this.assemblyInstructionsUrlInput && gridSize) {
      // Use underscore prefix to hide in cart/checkout if flag is false
      const prefix = CONFIG.SHOW_ASSEMBLY_INSTRUCTIONS_IN_CART ? '' : '_';
      const dynamicName = `properties[${prefix}Assembly instructions for your custom ${gridSize} mosaic]`;
      this.assemblyInstructionsUrlInput.name = dynamicName;
    }
    
    // Custom image input name is always "properties[Uploaded image]" and doesn't need dynamic naming
  }

  setupEventListeners() {
    // Setup ATC button click handler (works in both modes when button is enabled)
    if (this.buyButton) {
      this.buyButton.addEventListener('click', this.handleBuyButtonClick.bind(this));
    }
    
    // Setup hover tooltip for disabled ATC button (Cloudinary mode)
    // Use both mouse events (desktop) and touch events (mobile)
    if (this.buyButtonWrapper && this.uploadProvider === 'cloudinary') {
      // Desktop: hover events
      this.buyButtonWrapper.addEventListener('mouseenter', this.handleBuyButtonHover.bind(this));
      this.buyButtonWrapper.addEventListener('mouseleave', this.handleBuyButtonHoverLeave.bind(this));
      
      // Mobile: touch events
      this.buyButtonWrapper.addEventListener('touchstart', this.handleBuyButtonTouch.bind(this), { passive: false });
    }
    
    // Setup Prepare Mosaic button listener (Cloudinary mode only)
    if (this.uploadProvider === 'cloudinary' && this.prepareMosaicButton) {
      this.prepareMosaicButton.addEventListener('click', this.handlePrepareMosaicClick.bind(this));
    }
    
    // Listen for form submission - this is the primary capture method
    if (this.productForm) {
      this.productForm.addEventListener('submit', this.handleFormSubmit.bind(this));
    }
    
    // Listen for state changes to detect mosaic modifications (Cloudinary mode only)
    if (this.uploadProvider === 'cloudinary') {
      this.stateManager.subscribe('stateChanged', (data) => {
        this.handleStateChange(data);
      });
    }
  }


  setupMosaicRenderingListener() {
    // Listen for mosaic rendering completion (final renders only)
    this.mosaicRenderer.stateManager.subscribe('mosaicRendered', () => {
      if (this.uploadProvider === 'cloudinary') {
        // Cloudinary mode: Just enable PM button, no PNG generation
        debugLog('INFO', '🟢 MosaicImageCaptureManager: mosaicRendered - enabling PM button (Cloudinary mode)');
        this.disableBuyButton();
        this.enablePrepareMosaicButton();
      } else {
        // Shopify CDN mode: Use intersection observer flow
        debugLog('INFO', '🟢 MosaicImageCaptureManager: mosaicRendered event received - checking visibility and handling accordingly');
        this.onMosaicChanged();
      }
    });
  }

  setupDebouncedUpdates() {
    // Single 5-second debounce for PNG generation
    this.debouncedOnMosaicChanged = this.debounce(() => {
      debugLog('INFO', '🟢 MosaicImageCaptureManager: 5-second timer expired - generating PNG files');
      this.generatePngFiles();
    }, 5000);
  }

  setupIntersectionObserver() {
    // In Cloudinary mode, PNGs are generated and uploaded only when PM button is clicked
    // No need for intersection observer to pre-generate PNGs
    if (this.uploadProvider === 'cloudinary') {
      debugLog('INFO', '🟢 MosaicImageCaptureManager: Cloudinary mode - skipping Intersection Observer setup');
      return;
    }

    if (!this.buyButton) {
      debugLog('WARN', '🟢 MosaicImageCaptureManager: Buy button not found, skipping Intersection Observer setup');
      return;
    }

    // Disconnect existing observer if any
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    // Create new observer with 200px margin (Shopify CDN mode only)
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          debugLog('INFO', '🟢 MosaicImageCaptureManager: Add to Cart button became visible - stopping timer and generating PNGs immediately');
          
          // Stop timer if running
          if (this.debouncedOnMosaicChanged && this.debouncedOnMosaicChanged.cancel) {
            this.debouncedOnMosaicChanged.cancel();
          }
          
          // Generate PNGs immediately
          this.generatePngFiles();
        }
      });
    }, {
      rootMargin: '200px'
    });

    // Start observing the buy button
    this.intersectionObserver.observe(this.buyButton);
    debugLog('INFO', '🟢 MosaicImageCaptureManager: Intersection Observer set up for Add to Cart button');
  }

  isAddToCartButtonVisible() {
    if (!this.buyButton) return false;
    
    const rect = this.buyButton.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    
    // Check if button is within 200px of viewport (including margins)
    return (
      rect.top < windowHeight + 200 &&
      rect.bottom > -200 &&
      rect.left < windowWidth + 200 &&
      rect.right > -200
    );
  }

  debounce(func, wait) {
    let timeout;
    const debouncedFunction = function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
    
    // Add cancel method
    debouncedFunction.cancel = () => {
      clearTimeout(timeout);
    };
    
    return debouncedFunction;
  }

  onMosaicChanged() {
    // This method is only used in Shopify CDN mode
    // In Cloudinary mode, mosaic changes are handled in setupMosaicRenderingListener()
    if (this.uploadProvider === 'cloudinary') {
      return;
    }

    // Shopify CDN mode: Use intersection observer to generate PNGs when button becomes visible
    // Cancel any existing timer first
    if (this.debouncedOnMosaicChanged && this.debouncedOnMosaicChanged.cancel) {
      this.debouncedOnMosaicChanged.cancel();
    }
    
    // Destroy existing PNG files
    this.preparedMosaicImageBlob = null;
    this.preparedInstructionsBlob = null;
    
    // Check if Add to Cart button is currently visible
    if (this.isAddToCartButtonVisible()) {
      debugLog('INFO', '🟢 MosaicImageCaptureManager: Add to Cart button is visible - generating PNGs immediately');
      this.generatePngFiles();
    } else {
      debugLog('INFO', '🟢 MosaicImageCaptureManager: Add to Cart button not visible - starting 5-second timer');
      this.debouncedOnMosaicChanged();
    }
  }

  async generatePngFiles() {
    if (this.isGeneratingPngs) {
      debugLog('INFO', '🟢 MosaicImageCaptureManager: PNG generation already in progress, skipping');
      return;
    }
    
    // Check if there's actually an image loaded before generating PNGs
    if (!this.hasImageLoaded()) {
      debugLog('INFO', '🟢 MosaicImageCaptureManager: No image loaded, skipping PNG generation');
      return;
    }
    
    this.isGeneratingPngs = true;
    
    // Note: In Cloudinary mode, button state is managed by PM button click handler
    // No button state changes here - button already shows "Preparing Mosaic..." from click handler
    
    debugLog('INFO', '🟢 MosaicImageCaptureManager: Generating PNG files from current mosaic...');
    
    try {
      // Generate mosaic image and await completion
      await this.generateMosaicImageBlobOnly();
      
      // Generate assembly instructions and await completion
      await this.generateInstructionsBlobOnly();
    } finally {
      this.isGeneratingPngs = false;
      
      // In Shopify CDN mode, re-enable PM button when PNG generation completes
      // In Cloudinary mode, button state is managed by PM button click handler
      if (this.uploadProvider !== 'cloudinary') {
        // Shopify CDN mode: button state management if needed
      }
      
      debugLog('INFO', '🟢 MosaicImageCaptureManager: PNG generation completed');
    }
  }

  // Methods to manage buy button state
  disableBuyButton() {
    if (this.buyButton) {
      this.buyButton.disabled = true;
      
      // Only store original text if not already stored
      if (!this.buyButton.dataset.originalText) {
        this.buyButton.dataset.originalText = this.buyButton.textContent;
      }
      
      // Keep normal text when disabled
      this.buyButton.classList.remove('uploading');
      const originalText = this.buyButton.dataset.originalText;
      if (originalText) {
        this.buyButton.textContent = originalText;
      }
    }
    
    // Add opacity-50 to ATC instruction element when button is inactive
    if (this.atcInstructionElement) {
      this.atcInstructionElement.classList.add('opacity-50');
    }
  }

  enableBuyButton() {
    if (this.buyButton) {
      this.buyButton.disabled = false;
      this.buyButton.classList.remove('uploading');
      
      const originalText = this.buyButton.dataset.originalText;
      if (originalText) {
        this.buyButton.textContent = originalText;
      } else {
        // Fallback if original text wasn't captured
        this.buyButton.textContent = 'Add to cart';
      }
    }
    
    // Remove opacity-50 from ATC instruction element when button is active
    if (this.atcInstructionElement) {
      this.atcInstructionElement.classList.remove('opacity-50');
    }
  }

  // Methods to manage Prepare Mosaic button state (Cloudinary mode only)
  disablePrepareMosaicButton(text = 'Generating Files...') {
    if (this.prepareMosaicButton) {
      this.prepareMosaicButton.disabled = true;
      this.prepareMosaicButton.classList.add('uploading');
      
      // Only store original text if not already stored
      if (!this.prepareMosaicButton.dataset.originalText) {
        const labelElement = this.prepareMosaicButton.querySelector('.button__label');
        this.prepareMosaicButton.dataset.originalText = labelElement ? labelElement.textContent : 'PREPARE MOSAIC';
      }
      
      const labelElement = this.prepareMosaicButton.querySelector('.button__label');
      if (labelElement) {
        labelElement.textContent = text;
      }
    }
    
    // Add opacity-50 to PM instruction element when button is inactive
    if (this.pmInstructionElement) {
      this.pmInstructionElement.classList.add('opacity-50');
    }
  }

  enablePrepareMosaicButton() {
    if (this.prepareMosaicButton) {
      this.prepareMosaicButton.disabled = false;
      this.prepareMosaicButton.classList.remove('uploading');
      
      const originalText = this.prepareMosaicButton.dataset.originalText || 'PREPARE MOSAIC';
      const labelElement = this.prepareMosaicButton.querySelector('.button__label');
      if (labelElement) {
        labelElement.textContent = originalText;
      }
    }
    
    // Remove opacity-50 from PM instruction element when button is active
    if (this.pmInstructionElement) {
      this.pmInstructionElement.classList.remove('opacity-50');
    }
  }

  // Set PM button to "Mosaic Ready" state (remove spinner, show "Mosaic Ready" text, keep disabled)
  setPrepareMosaicButtonReady() {
    if (this.prepareMosaicButton) {
      this.prepareMosaicButton.disabled = true;
      this.prepareMosaicButton.classList.remove('uploading'); // Remove spinner
      
      const labelElement = this.prepareMosaicButton.querySelector('.button__label');
      if (labelElement) {
        labelElement.textContent = 'Mosaic Ready';
      }
    }
    
    // Add opacity-50 to PM instruction element when button is inactive (Mosaic Ready state)
    if (this.pmInstructionElement) {
      this.pmInstructionElement.classList.add('opacity-50');
    }
  }


  // Initialize button text preservation on startup
  // Ensures original button text is captured before any uploads begin
  initializeButtonText() {
    if (this.buyButton && !this.buyButton.dataset.originalText) {
      this.buyButton.dataset.originalText = this.buyButton.textContent;
    }
    
    // Initialize PM button text
    if (this.prepareMosaicButton && !this.prepareMosaicButton.dataset.originalText) {
      const labelElement = this.prepareMosaicButton.querySelector('.button__label');
      this.prepareMosaicButton.dataset.originalText = labelElement ? labelElement.textContent : 'PREPARE MOSAIC';
    }
    
    // Initialize button states
    if (this.uploadProvider === 'cloudinary') {
      // Cloudinary mode: Disable both buttons initially
      this.disableBuyButton();
      if (this.prepareMosaicButton) {
        this.prepareMosaicButton.disabled = true;
        // Add opacity-50 to PM instruction element when button is initially disabled
        if (this.pmInstructionElement) {
          this.pmInstructionElement.classList.add('opacity-50');
        }
      }
    } else {
      // Shopify CDN mode: Enable ATC button (no PM button in this mode)
      this.enableBuyButton();
    }
  }

  // Check if an image has been loaded by the user
  hasImageLoaded() {
    return window.mosaicApp?.imageManager?.currentImage !== null;
  }

  // Tooltip message for disabled ATC button
  get disabledAtcTooltipMessage() {
    return '👆 Please click "Prepare Mosaic" first';
  }

  // Remove tooltip helper
  removeTooltip() {
    const tooltip = document.getElementById('atc-tooltip-message');
    if (tooltip) {
      tooltip.remove();
    }
  }

  // Handle hover on disabled ATC button wrapper (desktop)
  handleBuyButtonHover(e) {
    if (this.buyButton && this.buyButton.disabled) {
      debugLog('INFO', 'Showing tooltip on hover for disabled ATC button');
      this.showTooltipMessage(this.disabledAtcTooltipMessage, this.buyButton);
    }
  }

  // Handle mouse leave to remove tooltip (desktop)
  handleBuyButtonHoverLeave(e) {
    this.removeTooltip();
  }

  // Handle touch on disabled ATC button wrapper (mobile)
  handleBuyButtonTouch(e) {
    if (this.buyButton && this.buyButton.disabled) {
      e.preventDefault();
      debugLog('INFO', 'Showing tooltip on touch for disabled ATC button');
      this.showTooltipMessage(this.disabledAtcTooltipMessage, this.buyButton);
      
      // Remove tooltip after a delay on mobile (since there's no "touchleave")
      setTimeout(() => this.removeTooltip(), 3000);
    }
  }

  handleBuyButtonClick(e) {
    // This handler only fires for enabled buttons (Shopify CDN mode)
    // Capture files to form inputs (PNGs should already be ready due to Intersection Observer)
    this.captureMosaicImage();
    this.captureAssemblyInstructions();
  }

  // Handle Prepare Mosaic button click (Cloudinary mode only)
  async handlePrepareMosaicClick(e) {
    if (this.uploadProvider !== 'cloudinary') {
      return;
    }

    e.preventDefault();
    
    // Disable button immediately
    this.disablePrepareMosaicButton('Preparing Mosaic...');
    
    // Clear any previous error messages
    this.clearErrorMessage();
    
    try {
      // Generate PNGs and upload them (only happens when PM button is clicked in Cloudinary mode)
      debugLog('INFO', 'PM button clicked - generating PNGs and uploading to Cloudinary...');
      await this.generatePngFiles();
      
      // Upload all files simultaneously
      await this.uploadAllFiles();
    } catch (error) {
      debugLog('ERROR', 'Error in Prepare Mosaic click handler:', error);
      this.showErrorMessage('Mosaic uploads failed. Please try again by clicking Prepare Mosaic.');
      this.disableBuyButton();
      this.enablePrepareMosaicButton();
    }
  }

  // Handle state changes to detect mosaic modifications (Cloudinary mode only)
  handleStateChange(data) {
    if (this.uploadProvider !== 'cloudinary') {
      return;
    }

    const { oldState, newState } = data;
    
    // Check if mosaic-affecting properties changed
    const mosaicChanged = 
      JSON.stringify(oldState.adjustments) !== JSON.stringify(newState.adjustments) ||
      JSON.stringify(oldState.toolEdits) !== JSON.stringify(newState.toolEdits) ||
      oldState.gridWidth !== newState.gridWidth ||
      oldState.gridHeight !== newState.gridHeight ||
      oldState.baseplate !== newState.baseplate ||
      oldState.uploadedImage !== newState.uploadedImage;
    
    if (mosaicChanged && (this.hasUploadedMosaic || this.hasUploadedInstructions)) {
      debugLog('INFO', 'Mosaic changed, invalidating uploads');
      // Reset upload flags (except custom image)
      this.hasUploadedMosaic = false;
      this.hasUploadedInstructions = false;
      
      // Disable ATC, enable PM
      this.disableBuyButton();
      this.enablePrepareMosaicButton();
      
      // Clear error messages
      this.clearErrorMessage();
    }
  }

  async handleFormSubmit(e) {
    // In Cloudinary mode, check if uploads are complete before allowing submission
    if (this.uploadProvider === 'cloudinary') {
      if (!this.hasUploadedMosaic || !this.hasUploadedInstructions) {
        e.preventDefault();
        debugLog('WARN', 'Form submission prevented - uploads not complete');
        this.showErrorMessage('Please click Prepare Mosaic before adding to cart.');
        return;
      }
      // Uploads are complete, allow form submission
      return;
    }
    
    // Shopify CDN mode: This is the primary capture method - handles all form submissions
    await this.captureMosaicImage();
    await this.captureAssemblyInstructions();
  }

  async captureMosaicImage() {
    const requiredInput = this.uploadProvider === 'cloudinary' ? this.mosaicImageUrlInput : this.mosaicImageInput;
    const urlInput = this.mosaicImageUrlInput;
    
    if (!this.validateCapturePrerequisites(requiredInput, urlInput, 'mosaic image')) {
      return;
    }

    try {
      // Check if we have prepared mosaic image ready
      if (this.preparedMosaicImageBlob) {
        debugLog('INFO', 'Using prepared mosaic image');
        await this.usePreparedMosaicImage();
        return;
      }

      // Fallback to generating mosaic image on-demand
      debugLog('INFO', 'No prepared mosaic image, generating on-demand...');
      await this.generateMosaicImageOnDemand();
    } catch (error) {
      debugLog('ERROR', 'Error capturing mosaic image:', error);
    }
  }

  async usePreparedMosaicImage() {
    await this.usePreparedBlob(
      this.preparedMosaicImageBlob,
      'mosaic',
      this.mosaicImageUrlInput,
      this.mosaicImageInput,
      () => this.generateMosaicImageOnDemand()
    );
  }

  async generateMosaicImageBlobOnly() {
    try {
      // Wrap toBlob in a Promise to make it truly awaitable
      const blob = await new Promise((resolve) => {
        this.mosaicCanvas.toBlob((blob) => resolve(blob), 'image/png');
      });
      
      if (blob) {
        this.preparedMosaicImageBlob = blob;
        debugLog('INFO', 'Mosaic image blob generated and stored');
      }
    } catch (error) {
      debugLog('ERROR', 'Error generating mosaic image blob:', error);
    }
  }

  async generateMosaicImageOnDemand() {
    await this.generateOnDemand(
      () => this.generateMosaicImageBlobOnly(),
      this.preparedMosaicImageBlob,
      'mosaic',
      this.mosaicImageUrlInput,
      this.mosaicImageInput,
      'mosaic image'
    );
  }

  // Generic upload method for any blob/file
  // Handles both Shopify CDN and Cloudinary uploads based on Section Settings
  // For Shopify CDN: sets blob to file input and submits to form
  // For Cloudinary: uploads to Cloudinary and sets URL to url input
  async uploadBlobToCloudinary(blob, fileName, urlInput, uploadType = null, fileInput = null) {
    // If using Shopify CDN, just set blob to file input - no button state tracking needed
    // Shopify will handle the upload automatically when form is submitted
    // Check if the input is a file input (for Shopify CDN) or URL input (for Cloudinary)
    const isFileInput = fileInput && fileInput.type === 'file';
    const isUrlInput = urlInput && urlInput.type === 'url';
    
    if (this.uploadProvider === 'shopify' && isFileInput) {
      try {
        debugLog('INFO', 'Setting file to Shopify CDN input:', fileName);
        
        // Create a File object from the blob and set it to the file input
        const file = new File([blob], fileName, { type: 'image/png' });
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        fileInput.files = dataTransfer.files;
        
        debugLog('INFO', 'File set to Shopify CDN input successfully');
        
        // No URL to return for Shopify CDN
        return null;
      } catch (error) {
        debugLog('ERROR', 'Shopify CDN file setting error:', error);
        return null;
      }
    }
    
    // Cloudinary upload logic
    const file = new File([blob], fileName, { type: 'image/png' });
    
    // Note: Upload state tracking removed - not needed with PM button flow
    // In Cloudinary mode, all uploads go through PM button (uploadAllFiles)
    // In Shopify CDN mode, button state doesn't need tracking
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', this.cloudinaryConfig.uploadPreset);
    formData.append('cloud_name', this.cloudinaryConfig.cloudName);
    
    try {
      debugLog('INFO', 'Starting Cloudinary upload for:', fileName);
      const response = await fetch(`https://api.cloudinary.com/v1_1/${this.cloudinaryConfig.cloudName}/image/upload`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        debugLog('ERROR', 'Upload failed:', response.status, errorText);
        throw new Error(`Upload failed: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      const cloudinaryUrl = data.secure_url;
      
      // Set the Cloudinary URL to the input
      if (urlInput) {
        urlInput.value = cloudinaryUrl;
      }
      
      debugLog('INFO', 'Upload successful:', cloudinaryUrl);
      return cloudinaryUrl;
    } catch (error) {
      debugLog('ERROR', 'Cloudinary upload error:', error);
      return null;
    } finally {
      // Note: Upload state tracking and button state management removed
      // In Cloudinary mode, button state is managed explicitly in uploadAllFiles() and handleStateChange()
      // In Shopify CDN mode, button doesn't need state tracking
    }
  }



  async captureAssemblyInstructions() {
    const requiredInput = this.uploadProvider === 'cloudinary' ? this.assemblyInstructionsUrlInput : this.assemblyInstructionsInput;
    const urlInput = this.assemblyInstructionsUrlInput;
    
    if (!this.validateCapturePrerequisites(requiredInput, urlInput, 'assembly instructions')) {
      return;
    }

    try {
      // Check if we have prepared assembly instructions ready
      if (this.preparedInstructionsBlob) {
        debugLog('INFO', 'Using prepared assembly instructions');
        await this.usePreparedInstructions();
        return;
      }

      // Fallback to generating assembly instructions on-demand
      debugLog('INFO', 'No prepared assembly instructions, generating on-demand...');
      await this.generateInstructionsOnDemand();
    } catch (error) {
      debugLog('ERROR', 'Error capturing assembly instructions:', error);
    }
  }

  // Common validation logic for capture methods
  validateCapturePrerequisites(requiredInput, urlInput, typeName) {
    if (!this.mosaicCanvas || !requiredInput) {
      debugLog('WARN', `Required elements not found for ${typeName} capture`);
      return false;
    }

    if (!this.hasImageLoaded()) {
      debugLog('INFO', `No image loaded, skipping ${typeName} capture`);
      return false;
    }

    // Check if URL is already set (uploaded) - only applies to Cloudinary mode
    if (this.uploadProvider === 'cloudinary' && urlInput && urlInput.value) {
      debugLog('INFO', `${typeName} already uploaded, skipping capture`);
      return false;
    }

    // Check if mosaic has been generated
    if (!window.mosaicApp?.mosaicRenderer?.currentMosaic || 
        window.mosaicApp.mosaicRenderer.currentMosaic.length === 0) {
      debugLog('INFO', `Mosaic not yet generated, skipping ${typeName} capture`);
      return false;
    }

    return true;
  }

  async usePreparedInstructions() {
    await this.usePreparedBlob(
      this.preparedInstructionsBlob,
      'assembly-instructions',
      this.assemblyInstructionsUrlInput,
      this.assemblyInstructionsInput,
      () => this.generateInstructionsOnDemand()
    );
  }

  // Generic method to upload a prepared blob
  async usePreparedBlob(blob, fileNamePrefix, urlInput, fileInput, fallbackGenerator) {
    try {
      const fileName = `${fileNamePrefix}-${Date.now()}.png`;
      await this.uploadBlobToCloudinary(blob, fileName, urlInput, fileNamePrefix, fileInput);
    } catch (error) {
      debugLog('ERROR', `Error using prepared ${fileNamePrefix}:`, error);
      await fallbackGenerator();
    }
  }

  async generateInstructionsBlobOnly() {
    try {
      const state = this.stateManager.getState();
      const gridSize = state.gridSizeString;
      const baseplate = state.baseplate ? 'Framed' : 'Regular';
      
      if (!gridSize) {
        debugLog('WARN', 'Grid size not selected for assembly instructions');
        return;
      }
      
      // Use ProjectSummaryManager to get color counts and generate instructions
      if (window.mosaicApp?.projectSummary) {
        const colorCounts = window.mosaicApp.projectSummary.getColorCountsFromMosaic();
        
        if (!colorCounts || colorCounts.length === 0) {
          debugLog('WARN', 'No color counts available for assembly instructions');
          return;
        }
        
        // Generate instructions and store blob only
        const blob = await window.mosaicApp.projectSummary.generateAssemblyInstructions(colorCounts, gridSize, baseplate);
        
        if (blob) {
          this.preparedInstructionsBlob = blob;
          debugLog('INFO', 'Assembly instructions blob generated and stored');
        } else {
          debugLog('ERROR', 'No blob generated for assembly instructions');
        }
      } else {
        debugLog('ERROR', 'ProjectSummaryManager not available for assembly instructions');
      }
    } catch (error) {
      debugLog('ERROR', 'Error generating assembly instructions blob:', error);
    }
  }

  async generateInstructionsOnDemand() {
    await this.generateOnDemand(
      () => this.generateInstructionsBlobOnly(),
      this.preparedInstructionsBlob,
      'assembly-instructions',
      this.assemblyInstructionsUrlInput,
      this.assemblyInstructionsInput,
      'assembly instructions'
    );
  }

  // Generic method to generate blob and conditionally upload
  async generateOnDemand(blobGenerator, preparedBlob, fileNamePrefix, urlInput, fileInput, typeName) {
    try {
      // Generate blob first
      await blobGenerator();
      
      // Determine which instance variable was updated based on fileNamePrefix
      const updatedBlob = fileNamePrefix === 'mosaic' 
        ? this.preparedMosaicImageBlob 
        : this.preparedInstructionsBlob;
      
      // Upload only if not in Cloudinary mode (Cloudinary uploads happen via PM button)
      if (this.uploadProvider !== 'cloudinary' && updatedBlob) {
        const fileName = `${fileNamePrefix}-${Date.now()}.png`;
        await this.uploadBlobToCloudinary(updatedBlob, fileName, urlInput, fileNamePrefix, fileInput);
      }
    } catch (error) {
      debugLog('ERROR', `Error generating ${typeName} on-demand:`, error);
    }
  }

  // Upload all files simultaneously when PM button is clicked (Cloudinary mode only)
  async uploadAllFiles() {
    if (this.uploadProvider !== 'cloudinary') {
      return;
    }

    const uploadPromises = [];
    
    // Upload custom image if it exists
    if (this.customImageFileInput && this.customImageFileInput.files && this.customImageFileInput.files.length > 0) {
      const customImageFile = this.customImageFileInput.files[0];
      const fileName = `custom-image-${Date.now()}.${customImageFile.name.split('.').pop()}`;
      uploadPromises.push(
        this.uploadBlobToCloudinary(customImageFile, fileName, this.customImageInput, 'custom', this.customImageFileInput)
          .then(url => {
            if (url) {
              this.hasUploadedCustomImage = true;
            }
            return url;
          })
      );
    }
    
    // Upload mosaic image
    if (this.preparedMosaicImageBlob) {
      const fileName = `mosaic-${Date.now()}.png`;
      uploadPromises.push(
        this.uploadBlobToCloudinary(this.preparedMosaicImageBlob, fileName, this.mosaicImageUrlInput, 'mosaic', this.mosaicImageInput)
          .then(url => {
            if (url) {
              this.hasUploadedMosaic = true;
            }
            return url;
          })
      );
    } else {
      debugLog('WARN', 'No prepared mosaic image blob available');
    }
    
    // Upload assembly instructions
    if (this.preparedInstructionsBlob) {
      const fileName = `assembly-instructions-${Date.now()}.png`;
      uploadPromises.push(
        this.uploadBlobToCloudinary(this.preparedInstructionsBlob, fileName, this.assemblyInstructionsUrlInput, 'instructions', this.assemblyInstructionsInput)
          .then(url => {
            if (url) {
              this.hasUploadedInstructions = true;
            }
            return url;
          })
      );
    } else {
      debugLog('WARN', 'No prepared instructions blob available');
    }
    
    try {
      const results = await Promise.all(uploadPromises);
      
      // Check if all required uploads succeeded
      const mosaicUploaded = this.hasUploadedMosaic;
      const instructionsUploaded = this.hasUploadedInstructions;
      
      if (mosaicUploaded && instructionsUploaded) {
        // All uploads successful - enable ATC, set PM to "Mosaic Ready" (remove spinner)
        this.enableBuyButton();
        this.setPrepareMosaicButtonReady();
        debugLog('INFO', 'All files uploaded successfully');
      } else {
        throw new Error('Some uploads failed');
      }
    } catch (error) {
      debugLog('ERROR', 'Error uploading files:', error);
      // Reset flags on error
      this.hasUploadedMosaic = false;
      this.hasUploadedInstructions = false;
      this.hasUploadedCustomImage = false;
      throw error;
    }
  }

  async uploadCustomImage(file) {
    const fileInput = this.uploadProvider === 'cloudinary' ? this.customImageFileInput : this.customImageInput;
    const urlInput = this.uploadProvider === 'cloudinary' ? this.customImageInput : null;
    
    if (!file || !fileInput) {
      debugLog('WARN', 'Custom image file input not found');
      return null;
    }
    
    try {
      const fileName = `custom-image-${Date.now()}.${file.name.split('.').pop()}`;
      const cloudinaryUrl = await this.uploadBlobToCloudinary(file, fileName, urlInput, 'custom', fileInput);
      
      if (cloudinaryUrl) {
        debugLog('INFO', 'Custom image uploaded:', cloudinaryUrl);
        return cloudinaryUrl;
      } else {
        debugLog('INFO', 'Custom image set to Shopify CDN input');
        return 'shopify-cdn'; // Return a placeholder to indicate success for Shopify CDN
      }
    } catch (error) {
      debugLog('ERROR', 'Error uploading custom image:', error);
      return null;
    }
  }

  // Show temporary tooltip message near a button
  showTooltipMessage(message, targetButton) {
    // Remove existing tooltip if any
    const existingTooltip = document.getElementById('atc-tooltip-message');
    if (existingTooltip) {
      existingTooltip.remove();
    }
    
    if (!targetButton) {
      return;
    }
    
    // Add fade-in animation styles if not already added
    if (!document.getElementById('tooltip-animations')) {
      const style = document.createElement('style');
      style.id = 'tooltip-animations';
      style.textContent = `
        @keyframes fadeInTooltip {
          from {
            opacity: 0;
            transform: translate(-50%, -5px);
          }
          to {
            opacity: 1;
            transform: translate(-50%, 0);
          }
        }
      `;
      document.head.appendChild(style);
    }
    
    // Create tooltip element
    const tooltip = document.createElement('div');
    tooltip.id = 'atc-tooltip-message';
    tooltip.textContent = message;
    tooltip.style.cssText = `
      position: fixed;
      background: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 10px 14px;
      border-radius: 6px;
      font-size: 13px;
      z-index: 10001;
      pointer-events: none;
      white-space: nowrap;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
      animation: fadeInTooltip 0.2s ease-out;
    `;
    
    // Add to DOM first so we can measure it
    document.body.appendChild(tooltip);
    
    // Position tooltip above the button (now that it's in DOM, we can get its height)
    const buttonRect = targetButton.getBoundingClientRect();
    const tooltipHeight = tooltip.offsetHeight;
    const tooltipWidth = tooltip.offsetWidth;
    
    tooltip.style.top = `${buttonRect.top - tooltipHeight - 8}px`;
    tooltip.style.left = `${buttonRect.left + (buttonRect.width / 2)}px`;
    tooltip.style.transform = 'translateX(-50%)';
    
    // Note: Tooltip is removed on mouseleave, not with a timer
  }

  // Error message display methods
  showErrorMessage(message) {
    if (!this.prepareMosaicButton) {
      return;
    }
    
    const buttonWrapper = this.prepareMosaicButton.closest('.buy-buttons-wrapper');
    if (!buttonWrapper) {
      return;
    }
    
    // Find the first instruction text element (before PM button)
    let instructionElement = buttonWrapper.querySelector('.button-instruction-text');
    if (!instructionElement) {
      return;
    }
    
    // Replace instruction element with error message
    const errorElement = document.createElement('p');
    errorElement.className = 'button-instruction-text';
    errorElement.style.color = 'red';
    errorElement.textContent = message;
    
    instructionElement.replaceWith(errorElement);
    this.instructionTextElement = errorElement;
  }

  clearErrorMessage() {
    if (!this.prepareMosaicButton) {
      return;
    }
    
    const buttonWrapper = this.prepareMosaicButton.closest('.buy-buttons-wrapper');
    if (!buttonWrapper) {
      return;
    }
    
    // Check if current element is an error message
    const currentElement = buttonWrapper.querySelector('.button-instruction-text');
    if (currentElement && currentElement.style.color === 'red') {
      // Restore original instruction element
      const olElement = document.createElement('ol');
      olElement.className = 'button-instruction-text';
      olElement.setAttribute('start', '1');
      const liElement = document.createElement('li');
      liElement.innerHTML = 'Carefully review your design, then click <strong>Prepare Mosaic</strong> to generate the final version for the cart.';
      olElement.appendChild(liElement);
      
      currentElement.replaceWith(olElement);
      this.instructionTextElement = olElement;
    }
  }

  invalidateCache() {
    if (this.preparedInstructionsBlob) {
      debugLog('INFO', 'Invalidating prepared assembly instructions cache');
      this.preparedInstructionsBlob = null;
    }
    
    if (this.preparedMosaicImageBlob) {
      debugLog('INFO', 'Invalidating prepared mosaic image cache');
      this.preparedMosaicImageBlob = null;
    }
  }

  uploadDummyPng() {
    // Create a simple test PNG using canvas
    const canvas = document.createElement('canvas');
    canvas.width = 100;
    canvas.height = 100;
    const ctx = canvas.getContext('2d');
    
    // Draw a simple test pattern
    ctx.fillStyle = '#ff0000';
    ctx.fillRect(0, 0, 50, 50);
    ctx.fillStyle = '#00ff00';
    ctx.fillRect(50, 0, 50, 50);
    ctx.fillStyle = '#0000ff';
    ctx.fillRect(0, 50, 50, 50);
    ctx.fillStyle = '#ffff00';
    ctx.fillRect(50, 50, 50, 50);
    
    // Add text
    ctx.fillStyle = '#000000';
    ctx.font = '12px Arial';
    ctx.fillText('TEST PNG', 10, 60);
    
    // Convert to data URL
    const dataURL = canvas.toDataURL('image/png');
    
    // Create hidden text inputs with data URLs
    this.createHiddenInput('properties[Test Mosaic Image]', dataURL);
    this.createHiddenInput('properties[Test Assembly Instructions]', dataURL);
    
    debugLog('INFO', 'Dummy PNG data URLs created and attached to form');
  }

  createHiddenInput(name, value) {
    // Remove existing input with same name
    const existingInput = document.querySelector(`input[name="${name}"]`);
    if (existingInput) {
      existingInput.remove();
    }
    
    // Create new hidden input
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    
    // Add to the product form
    if (this.productForm) {
      this.productForm.appendChild(input);
      debugLog('INFO', `Created hidden input: ${name} = ${value.substring(0, 50)}...`);
    }
  }

  destroy() {
    this.invalidateCache();
  }
}

// ============================================================================
// DEVELOPMENT HELPER
// ============================================================================

// Simple function to load default image for development
async function loadDefaultImage(imageManager) {
  if (!CONFIG.DEV.LOAD_DEFAULT_IMAGE) return;
  
  try {

    const response = await fetch(CONFIG.DEV.DEFAULT_IMAGE_PATH);
    const blob = await response.blob();
    const file = new File([blob], 'dev-image.jpg', { type: 'image/jpeg' });
    
    // Create mock event and trigger image upload
    const mockEvent = { target: { files: [file] } };
    imageManager.handleImageUpload(mockEvent);

  } catch (error) {
    console.error('Failed to load development image:', error);
  }
}

// ============================================================================
// MOBILE NAVIGATION MANAGER
// ============================================================================

class MobileNavigationManager {
  constructor(stateManager) {
    this.stateManager = stateManager;
    this.currentTab = 'upload';
    this.init();
  }

  init() {
    this.setupElements();
    this.setupEventListeners();
    this.updateActiveTab();
    this.setupResponsiveBehavior();
  }

  setupElements() {
    this.navButtons = document.querySelectorAll('.mobile-nav button');
    this.controlGroups = document.querySelectorAll('.control-group[data-tab]');
  }

  setupEventListeners() {
    this.navButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const tab = e.currentTarget.dataset.tab;
        this.setActiveTab(tab);
        
        // Trigger file input for upload tab
        if (tab === 'upload') {
          // Both modes use #custom-image for the file input
          const fileInput = document.getElementById('custom-image');
          if (fileInput) {
            fileInput.click();
          }
        }
      });
    });
  }

  setupResponsiveBehavior() {
    // Handle window resize to show/hide mobile nav appropriately
    window.addEventListener('resize', () => {
      this.updateActiveTab();
    });
  }

  setActiveTab(tab) {
    this.currentTab = tab;
    this.updateActiveTab();
    this.stateManager.updateState({ activeMobileTab: tab });
  }

  updateActiveTab() {
    // Update nav buttons
    this.navButtons.forEach(button => {
      const isActive = button.dataset.tab === this.currentTab;
      button.classList.toggle('active', isActive);
    });

    // Activate the matching control group; CSS handles visibility on mobile
    if (this.controlGroups && this.controlGroups.length) {
      this.controlGroups.forEach(group => {
        const isActive = group.dataset.tab === this.currentTab;
        group.classList.toggle('active', isActive);
      });
    }
  }
}

// ============================================================================
// MAIN APPLICATION CLASS
// ============================================================================

class MosaicCreatorApp {
  constructor() {
    this.stateManager = new StateManager();
    this.imageManager = new ImageManager(this.stateManager);
    this.variantObserver = new ShopifyVariantObserver(this.stateManager);
    
    // Initialize undo/redo manager first (needed by other managers)
    this.undoRedoManager = new UndoRedoManager(this.stateManager);
    
    // Initialize empty classes for future phases
    this.cropFrame = new CropFrame(this.stateManager, this.imageManager);
    this.mosaicRenderer = new MosaicRenderer(this.stateManager);
    this.adjustmentManager = new AdjustmentManager(this.stateManager, this.mosaicRenderer, this.undoRedoManager);
    this.paintingManager = new PaintingManager(this.stateManager, this.mosaicRenderer, this.undoRedoManager);
    this.mobileNavigationManager = new MobileNavigationManager(this.stateManager);
    this.canvasInteractionManager = new CanvasInteractionManager();
    this.exportManager = new ExportManager();
    this.projectSummary = new ProjectSummaryManager(this.stateManager, this.mosaicRenderer);
    this.mosaicImageCapture = new MosaicImageCaptureManager(this.stateManager, this.mosaicRenderer);
    
    this.init();
  }

  init() {
    this.setupShapeChangeListener();
    this.setupSizeGuide();
    this.setupColorBreakdownAccordion();
    this.setupZoomControls();
    
    // Set up the variant observer to watch for grid size changes
    this.variantObserver.setupObservers();
    
    // Set initial shape-based visibility
    this.setInitialShapeVisibility();
    
    // Load development image if enabled
    loadDefaultImage(this.imageManager);
    
    // Setup project summary state listeners
    this.setupProjectSummaryListeners();
    
    // Update input names with initial values
    this.updateInputNames();
    
    debugLog('INFO', 'Mosaic Creator App initialized');
  }

  // Public method to toggle image cropper wrapper visibility
  toggleImageCropper() {
    if (this.imageManager) {
      this.imageManager.toggleCanvas();
    }
  }


  setupShapeChangeListener() {
    const shapeRadios = document.querySelectorAll(CONFIG.SELECTORS.SHAPE_RADIOS);
    shapeRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        const selectedShape = e.target.value;
        this.stateManager.updateState({ currentShape: selectedShape });
        
        // Update visible grid sizes based on selected shape
        this.variantObserver.updateVisibleByShape(selectedShape);
      });
    });
  }

  setupProjectSummaryListeners() {
    // Listen for state changes to update project summary with debounce
    this.stateManager.subscribe('stateChanged', (data) => {
      const { newState } = data;
      
      // Update project summary when relevant state changes (debounced)
      if (this.projectSummary) {
        this.projectSummary.debouncedUpdate();
      }
      
      // Update input names when grid size or baseplate changes
      if (newState.gridSizeString || newState.baseplate !== undefined) {
        this.updateInputNames();
      }
    });
    
    // Listen for mosaic rendering completion (immediate update for final render)
    this.mosaicRenderer.stateManager.subscribe('mosaicRendered', () => {
      if (this.projectSummary) {
        this.projectSummary.updateProjectSummary();
      }
    });
  }

  setupSizeGuide() {
    const link = document.getElementById('size-guide-link');
    const accordion = document.getElementById('size-guide-accordion');
    const toggleIcon = link ? link.querySelector('.size-guide-toggle') : null;
    const closeBtn = document.querySelector('.size-guide-close');
    
    if (!link || !accordion || !toggleIcon) return;
    
    const closedIcon = toggleIcon.querySelector('.size-guide-icon-closed');
    const openIcon = toggleIcon.querySelector('.size-guide-icon-open');
    
    const updateAccordionState = () => {
      const isHidden = accordion.classList.contains('hidden');
      
      // Toggle icon visibility
      if (closedIcon && openIcon) {
        closedIcon.classList.toggle('hidden', !isHidden);
        openIcon.classList.toggle('hidden', isHidden);
      }
      
      // Toggle font-weight
      link.style.fontWeight = isHidden ? 'normal' : '600';
    };
    
    // Set initial state
    updateAccordionState();
    
    // Add click listener
    link.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      accordion.classList.toggle('hidden');
      updateAccordionState();
    });
    
    // Setup close button
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        accordion.classList.add('hidden');
        updateAccordionState();
      });
    }
  }

  setupColorBreakdownAccordion() {
    const link = document.getElementById('color-breakdown-link');
    const accordion = document.getElementById('color-breakdown-accordion');
    const toggleIcon = link ? link.querySelector('.color-breakdown-toggle') : null;
    
    if (!link || !accordion || !toggleIcon) return;
    
    const closedIcon = toggleIcon.querySelector('.color-breakdown-icon-closed');
    const openIcon = toggleIcon.querySelector('.color-breakdown-icon-open');
    
    const updateAccordionState = () => {
      const isHidden = accordion.classList.contains('hidden');
      
      // Toggle icon visibility
      if (closedIcon && openIcon) {
        closedIcon.classList.toggle('hidden', !isHidden);
        openIcon.classList.toggle('hidden', isHidden);
      }
      
      // Toggle padding-bottom
      link.style.paddingBottom = isHidden ? '0' : '1.5rem';
    };
    
    // Set initial state based on screen size
    const isDesktop = window.innerWidth >= 750;
    if (isDesktop) {
      accordion.classList.remove('hidden');
    } else {
      accordion.classList.add('hidden');
    }
    
    updateAccordionState();
    
    // Add click listener
    link.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      accordion.classList.toggle('hidden');
      updateAccordionState();
    });
  }

  setupZoomControls() {
    this.zoomLevel = 1;
    this.minZoom = 1.0;  // Changed from 0.5 to 1.0 - no zoom out beyond original size
    this.maxZoom = 3;    // Max scale factor is 3x (300%)
    this.zoomStep = 0.25;
    
    const canvas = document.getElementById('image-edit-canvas');
    const zoomInBtn = document.getElementById('zoom-in-btn');
    const zoomOutBtn = document.getElementById('zoom-out-btn');
    const zoomResetBtn = document.getElementById('zoom-reset-btn');
    const panToggleBtn = document.getElementById('pan-toggle');
    
    if (!canvas || !zoomInBtn || !zoomOutBtn || !zoomResetBtn || !panToggleBtn) {
      console.warn('Zoom controls: Required elements not found');
      return;
    }
    
    // Update button states based on current zoom level
    const updateButtonStates = () => {
      zoomInBtn.disabled = this.zoomLevel >= this.maxZoom;
      zoomOutBtn.disabled = this.zoomLevel <= this.minZoom;
      zoomResetBtn.disabled = this.zoomLevel === 1;
      panToggleBtn.disabled = this.zoomLevel === 1;
    };
    
    // Apply zoom to canvas
    const applyZoom = () => {
      canvas.style.transform = `scale(${this.zoomLevel})`;
      
      // Show/hide scrollbars based on zoom level
      const container = canvas.parentElement; // .canvas-zoom-container
      if (container) {
        if (this.zoomLevel > 1) {
          container.style.overflow = 'auto'; // Show scrollbars when zoomed in
        } else {
          container.style.overflow = 'hidden'; // Hide scrollbars at default zoom
        }
      }
      
      updateButtonStates();
      
      // If zoomed out to default level and pan tool is active, switch to paintbrush
      if (this.zoomLevel === 1 && window.mosaicApp && window.mosaicApp.paintingManager) {
        const paintingManager = window.mosaicApp.paintingManager;
        if (paintingManager.currentTool === 'pan') {
          paintingManager.selectTool('paintbrush-toggle');
        }
      }
      
      debugLog('INFO', `Canvas zoomed to ${(this.zoomLevel * 100).toFixed(0)}%`);
    };
    
    // Zoom in
    zoomInBtn.addEventListener('click', () => {
      if (this.zoomLevel < this.maxZoom) {
        this.zoomLevel = Math.min(this.zoomLevel + this.zoomStep, this.maxZoom);
        applyZoom();
      }
    });
    
    // Zoom out
    zoomOutBtn.addEventListener('click', () => {
      if (this.zoomLevel > this.minZoom) {
        this.zoomLevel = Math.max(this.zoomLevel - this.zoomStep, this.minZoom);
        applyZoom();
      }
    });
    
    // Reset zoom
    zoomResetBtn.addEventListener('click', () => {
      this.zoomLevel = 1;
      applyZoom();
    });
    
    // Initialize button states
    updateButtonStates();
    
    debugLog('INFO', 'Zoom controls initialized');
  }



  setInitialShapeVisibility() {
    // Find the currently selected shape radio
    const selectedShapeRadio = document.querySelector(`${CONFIG.SELECTORS.SHAPE_RADIOS}:checked`);
    if (selectedShapeRadio) {
      const selectedShape = selectedShapeRadio.value;

      
      // Update visible grid sizes based on initial shape
      this.variantObserver.updateVisibleByShape(selectedShape);
    } else {

    }
  }

  reset() {
    // Check if there's any history to lose
    const hasHistory = this.undoRedoManager.undoStack.length > 0 || this.undoRedoManager.redoStack.length > 0;
    
    if (hasHistory) {
      const confirmed = confirm('This will reset your mosaic. All edits will be lost, and the undo/redo history will be cleared. Are you sure you want to continue?');
      if (!confirmed) return;
    }
    
    // Universal reset - clear all adjustments and tool edits, preserve selections and image
    // Note: uploadedImage, gridSize, baseplate, and currentShape are NOT reset
    
    // Reset to state zero (which includes adjustments and tool edits)
    this.undoRedoManager.resetToStateZero();
  }

  // Method to completely clear everything (start over)
  clearAll() {
    this.stateManager.reset();
    this.imageManager.clear();
    this.cropFrame.hideCropFrame();
    
    // Clear tool edits
    if (this.mosaicRenderer) {
      this.mosaicRenderer.toolEdits = [];
      // Re-composite layers to show cleared toolEdits
      this.mosaicRenderer.compositeLayers();
    }
  }


  // Cleanup method to remove all event listeners and reset state
  destroy() {
    this.cropFrame.destroy();
    this.paintingManager.destroy();
    this.variantObserver.disconnect();

    if (this.mosaicImageCapture) {
      this.mosaicImageCapture.destroy();
      this.mosaicImageCapture = null;
    }
  }

  // Method to capture mosaic for cart (called when adding to cart)
  captureMosaicForCart() {
    if (this.mosaicImageCapture) {
      // Trigger form submission to capture mosaic data
      if (this.mosaicImageCapture.productForm) {
        this.mosaicImageCapture.productForm.dispatchEvent(new Event('submit', { bubbles: true }));
      } else {
        // Fallback if form not available
        this.mosaicImageCapture.updateInputNames();
        this.mosaicImageCapture.captureMosaicImage();
        this.mosaicImageCapture.captureAssemblyInstructions();
      }
    }
  }

  // Method to update input names when variants change
  updateInputNames() {
    if (this.mosaicImageCapture) {
      this.mosaicImageCapture.updateInputNames();
    }
  }

  // Method to invalidate cache when mosaic changes
  invalidateCache() {
    if (this.mosaicImageCapture) {
      this.mosaicImageCapture.invalidateCache();
    }
  }

  // Cleanup method to destroy all managers and clear timers
  destroy() {
    if (this.imageManager) {
      this.imageManager.destroy();
    }
    if (this.cropFrame) {
      this.cropFrame.destroy();
    }
    if (this.variantObserver) {
      this.variantObserver.disconnect();
    }
    if (this.mosaicRenderer) {
      this.mosaicRenderer.destroy();
    }
    if (this.undoRedoManager) {
      this.undoRedoManager.destroy();
    }
    if (this.adjustmentManager) {
      this.adjustmentManager.destroy();
    }
    if (this.paintingManager) {
      this.paintingManager.destroy();
    }
    if (this.projectSummary) {
      this.projectSummary.destroy();
    }
    if (this.mosaicImageCapture) {
      this.mosaicImageCapture.destroy();
    }
  }
}

// ============================================================================
// INITIALIZATION
// ============================================================================

// Wait for DOM to be ready
document.addEventListener('DOMContentLoaded', () => {
  // Check if we're on a product page with mosaic creator (robust to markup changes)
  const mosaicRoot = document.getElementById('image-edit-canvas')
    || document.getElementById('uploaded-image-canvas')
    || document.querySelector('variant-selects');

  if (mosaicRoot) {
    window.mosaicApp = new MosaicCreatorApp();
  }
});

// Export for debugging
if (typeof window !== 'undefined') {
  window.MosaicCreatorApp = MosaicCreatorApp;
  window.StateManager = StateManager;
  window.ImageManager = ImageManager;
  window.ShopifyVariantObserver = ShopifyVariantObserver;
  window.CONFIG = CONFIG;
  window.MosaicRenderer = MosaicRenderer;
  window.AdjustmentManager = AdjustmentManager;
  window.UndoRedoManager = UndoRedoManager;
  window.PaintingManager = PaintingManager;
}
